import React, { useState, useEffect, useMemo, lazy, Suspense } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { 
  Users, FolderOpen, Building2, Link2, FileText, Settings, BarChart3,
  ArrowLeft, Plus, Trash2, Edit2, Eye, Download, Search, Phone, Mail,
  CheckCircle, CheckCircle2, Clock, AlertTriangle, XCircle, Euro, Calendar,
  TrendingUp, LogOut, RefreshCw, UserCheck, ChevronDown, ChevronRight,
  Receipt, Calculator, Check, X, Upload, Image, File, Shield, UserPlus, Lock,
  MessageCircle, Network, MapPin, Wrench, ExternalLink, Send, Headphones, CreditCard, Ban, Bell, Map, List, Loader2, User, Award, Gift,
  ArrowUpDown, Filter, RotateCcw, DollarSign, Menu, Copy, KeyRound, Globe
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import axios from 'axios';
import AccountingDashboard from '../components/AccountingDashboard';
import MessagingDashboard from '../components/MessagingDashboard';
import AppointmentsDashboard from '../components/AppointmentsDashboard';
import AdminSidebar from '../components/admin/AdminSidebar';
import DomainRequestsTab from '../components/admin/DomainRequestsTab';
import Pagination, { usePagination } from '../components/admin/Pagination';
import EstimationCoefsTab from '../components/admin/EstimationCoefsTab';
import ProductCatalogTab from '../components/admin/ProductCatalogTab';
import GuideCompletTab from '../components/admin/GuideCompletTab';
import PaymentErrorsTab from '../components/admin/PaymentErrorsTab';
import LoanRatesTab from '../components/admin/LoanRatesTab';
import InfoTooltip from '../components/admin/InfoTooltip';

// Lazy load map component to avoid SSR issues
const ArtisanMap = lazy(() => import('../components/ArtisanMap'));

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_CONFIG = {
  pending: { label: 'En attente', icon: Clock, color: 'bg-gray-100 text-gray-700' },
  in_review: { label: 'En cours', icon: AlertTriangle, color: 'bg-amber-100 text-amber-700' },
  analyst_review: { label: 'Chez analyste', icon: UserCheck, color: 'bg-blue-100 text-blue-700' },
  pending_validation: { label: 'À valider', icon: Clock, color: 'bg-purple-100 text-purple-700' },
  completed: { label: 'Terminé', icon: CheckCircle, color: 'bg-emerald-100 text-emerald-700' },
  rejected: { label: 'Refusé', icon: XCircle, color: 'bg-red-100 text-red-700' }
};

const EXPENSE_CATEGORIES = [
  { value: 'fournisseur', label: 'Fournisseur' },
  { value: 'charges', label: 'Charges' },
  { value: 'materiel', label: 'Matériel' },
  { value: 'autre', label: 'Autre' }
];

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { logout } = useAuth();
  
  // Récupérer l'onglet actif depuis l'URL ou utiliser 'overview' par défaut
  const tabFromUrl = searchParams.get('tab') || 'overview';
  const [activeTab, setActiveTab] = useState(tabFromUrl);
  const [loading, setLoading] = useState(true);
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  
  // Mettre à jour l'URL quand on change d'onglet
  const handleTabChange = (newTab) => {
    setActiveTab(newTab);
    setSearchParams({ tab: newTab });
  };
  
  // Data states
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [projects, setProjects] = useState([]);
  const [projectsByDate, setProjectsByDate] = useState(null);
  const [partners, setPartners] = useState([]);
  const [affiliates, setAffiliates] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [analysts, setAnalysts] = useState([]);
  const [chiffreurs, setChiffreurs] = useState([]);
  const [prospects, setProspects] = useState([]);
  const [chantierDemands, setChantierDemands] = useState([]);
  const [settings, setSettings] = useState({});
  const [sendingToAccountant, setSendingToAccountant] = useState(false);
  const [lastAccountantExport, setLastAccountantExport] = useState(null);
  const [accountantPeriod, setAccountantPeriod] = useState(() => {
    const d = new Date(); d.setDate(1); d.setMonth(d.getMonth() - 1);
    return { year: d.getFullYear().toString(), month: (d.getMonth() + 1).toString() };
  });

  const loadAccountantHistory = async () => {
    try {
      const res = await axios.get(`${API}/admin/accounting/export-history`);
      setLastAccountantExport(res.data?.[0] || null);
    } catch {}
  };

  const sendToAccountantNow = async () => {
    try {
      setSendingToAccountant(true);
      await axios.put(`${API}/admin/settings`, settings);
      const res = await axios.post(`${API}/admin/accounting/send-to-accountant`, null, { params: { year: accountantPeriod.year, month: accountantPeriod.month } });
      toast.success(`Export ${res.data.period} envoyé à ${res.data.to_email} (${res.data.invoice_count} facture(s))`);
      setLastAccountantExport(res.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Échec de l'envoi au comptable");
    } finally {
      setSendingToAccountant(false);
    }
  };
  const [accountingSummary, setAccountingSummary] = useState(null);
  const [networkMembers, setNetworkMembers] = useState([]);
  const [selectedNetworkMembers, setSelectedNetworkMembers] = useState([]);
  const [networkFilter, setNetworkFilter] = useState('all'); // 'all', 'pending', 'validated', 'rejected'
  const [artisans, setArtisans] = useState([]);
  const [reseauLeads, setReseauLeads] = useState([]);
  const [manualPaymentPartner, setManualPaymentPartner] = useState(null);
  const [manualPaymentForm, setManualPaymentForm] = useState({
    amount: '',
    payment_method: 'virement',
    payment_date: new Date().toISOString().split('T')[0],
    notes: ''
  });
  const [selectedAgencies, setSelectedAgencies] = useState([]);
  const [selectedArtisans, setSelectedArtisans] = useState([]);
  const [selectedLeads, setSelectedLeads] = useState([]);
  
  // Selection states for bulk delete
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [selectedProjects, setSelectedProjects] = useState([]);
  const [selectedInvoices, setSelectedInvoices] = useState([]);
  const [selectedExpenses, setSelectedExpenses] = useState([]);
  const [selectedAffiliates, setSelectedAffiliates] = useState([]);
  const [editingAffiliateId, setEditingAffiliateId] = useState(null);
  const [editingCommissionValue, setEditingCommissionValue] = useState('');
  const [selectedAnalysts, setSelectedAnalysts] = useState([]);
  const [selectedChiffreurs, setSelectedChiffreurs] = useState([]);
  const [selectedPartners, setSelectedPartners] = useState([]);
  const [selectedProspects, setSelectedProspects] = useState([]);
  
  // Date view states for invoices
  const [expandedInvoiceDates, setExpandedInvoiceDates] = useState({ today: true, thisMonth: true });
  
  // Search states
  const [userSearch, setUserSearch] = useState('');
  const [projectSearch, setProjectSearch] = useState('');
  
  // Modal states
  const [editingProject, setEditingProject] = useState(null);
  const [editingUser, setEditingUser] = useState(null);
  const [newPartner, setNewPartner] = useState({ name: '', subdomain: '', contact_email: '', logo_url: '' });
  const [showPartnerModal, setShowPartnerModal] = useState(false);
  const [newAnalyst, setNewAnalyst] = useState({ name: '', email: '', phone: '', password: '' });
  const [showAnalystModal, setShowAnalystModal] = useState(false);
  const [viewingAnalyst, setViewingAnalyst] = useState(null);
  const [passwordModal, setPasswordModal] = useState({ show: false, analyst: null, newPassword: '' });
  const [createdAnalystPassword, setCreatedAnalystPassword] = useState(null);
  const [newChiffreur, setNewChiffreur] = useState({ name: '', email: '', phone: '', password: '' });
  const [showChiffreurModal, setShowChiffreurModal] = useState(false);
  const [viewingChiffreur, setViewingChiffreur] = useState(null);
  const [chiffreurPasswordModal, setChiffreurPasswordModal] = useState({ show: false, chiffreur: null, newPassword: '' });
  const [createdChiffreurPassword, setCreatedChiffreurPassword] = useState(null);
  const [assignChiffreurModal, setAssignChiffreurModal] = useState({ show: false, projectId: null });
  const [newExpense, setNewExpense] = useState({ description: '', category: 'fournisseur', amount_ht: '', tva_rate: 0.20, supplier_name: '', invoice_number: '', invoice_date: new Date().toISOString().split('T')[0], file_url: '', file_name: '' });
  const [showExpenseModal, setShowExpenseModal] = useState(false);
  const [expenseFileUploading, setExpenseFileUploading] = useState(false);
  const [assignModal, setAssignModal] = useState({ show: false, projectId: null });
  const [viewingUser, setViewingUser] = useState(null);
  const [viewingAffiliate, setViewingAffiliate] = useState(null);
  const [transferModal, setTransferModal] = useState({ show: false, project: null });
  const [nearbyArtisans, setNearbyArtisans] = useState([]);
  const [transferNotes, setTransferNotes] = useState('');
  const [transferLoading, setTransferLoading] = useState(false);
  
  // Hotline states
  const [hotlineConversations, setHotlineConversations] = useState([]);
  const [activeConversation, setActiveConversation] = useState(null);
  const [hotlineMessages, setHotlineMessages] = useState([]);
  const [hotlineHasMore, setHotlineHasMore] = useState(false);
  const [hotlineTotal, setHotlineTotal] = useState(0);
  const [hotlineSkip, setHotlineSkip] = useState(0);
  const [loadingMoreMessages, setLoadingMoreMessages] = useState(false);
  const [newHotlineMessage, setNewHotlineMessage] = useState('');
  const [sendingHotlineMessage, setSendingHotlineMessage] = useState(false);
  
  // Partner subscriptions states
  const [partnerSubscriptions, setPartnerSubscriptions] = useState([]);
  const [expandedPartnerId, setExpandedPartnerId] = useState(null);
  // Algérie paiements manuels
  const [algeriaPayments, setAlgeriaPayments] = useState([]);
  
  // Purge states
  const [purgeCollections, setPurgeCollections] = useState([]);
  const [purgeLoading, setPurgeLoading] = useState(false);
  
  // Network view state (list or map)
  const [networkView, setNetworkView] = useState('list'); // 'list', 'map', 'global-map'
  const [mapSubView, setMapSubView] = useState('validation'); // 'validation' or 'global'
  
  // Prospection Proprio states
  const [prospectionDemandes, setProspectionDemandes] = useState([]);
  const [prospectionStats, setProspectionStats] = useState(null);
  const [prospectionFilter, setProspectionFilter] = useState('all');
  const [selectedProspectionDemande, setSelectedProspectionDemande] = useState(null);
  const [showProspectionModal, setShowProspectionModal] = useState(false);
  const [proprietaireForm, setProprietaireForm] = useState({ nom: '', prenom: '', adresse: '', ville: '', code_postal: '' });
  const [savingProprietaire, setSavingProprietaire] = useState(false);
  const [newProspectionMessage, setNewProspectionMessage] = useState('');
  const [sendingProspectionMessage, setSendingProspectionMessage] = useState(false);
  const [mairieInfo, setMairieInfo] = useState(null);
  const [newSpfDept, setNewSpfDept] = useState('');
  const [newSpfEmail, setNewSpfEmail] = useState('');
  const [spfCostInput, setSpfCostInput] = useState('15');
  const [savingSpf, setSavingSpf] = useState(false);
  const [copiedSpf, setCopiedSpf] = useState(false);
  
  // Conseil Stratégique states
  const [conseilRequests, setConseilRequests] = useState([]);
  const [selectedConseil, setSelectedConseil] = useState(null);
  const [showConseilModal, setShowConseilModal] = useState(false);
  const [newConseilMessage, setNewConseilMessage] = useState('');
  const [sendingConseilMessage, setSendingConseilMessage] = useState(false);
  
  // Gratuités states
  const [demoPartners, setDemoPartners] = useState([]);
  const [freeStudies, setFreeStudies] = useState([]);
  const [gratuitesStats, setGratuitesStats] = useState(null);
  const [showCreateDemoModal, setShowCreateDemoModal] = useState(false);
  const [demoCreatedResult, setDemoCreatedResult] = useState(null);
  const [showCreateStudyModal, setShowCreateStudyModal] = useState(false);
  const [generatedFreeLink, setGeneratedFreeLink] = useState(null);
  const [generatingFreeLink, setGeneratingFreeLink] = useState(false);
  const [demoForm, setDemoForm] = useState({ company_name: '', email: '', phone: '', duration_days: 14, note: '' });
  const [studyForm, setStudyForm] = useState({ study_type: 'audit_flash', client_email: '', client_phone: '', client_name: '', description: '' });
  const [creatingDemo, setCreatingDemo] = useState(false);
  const [creatingStudy, setCreatingStudy] = useState(false);
  
  // Date view states
  const [expandedDates, setExpandedDates] = useState({ today: true, yesterday: true });

  // Pagination hooks (10 par page par défaut, plus récents en premier)
  const usersPagination = usePagination(10);
  const prospectsPagination = usePagination(10);
  const partnersPagination = usePagination(10);
  const affiliatesPagination = usePagination(10);
  const prospectionPagination = usePagination(10);
  const conseilPagination = usePagination(10);
  const chantiersPagination = usePagination(10);
  const networkPagination = usePagination(10);

  // Search states for tabs that don't have one yet
  const [prospectSearch, setProspectSearch] = useState('');
  const [partnerSearch, setPartnerSearch] = useState('');
  const [prospectionSearch, setProspectionSearch] = useState('');
  const [conseilSearch, setConseilSearch] = useState('');
  const [networkSearch, setNetworkSearch] = useState('');
  const [chantierSearch, setChantierSearch] = useState('');

  // Filter states
  const [prospectBloc, setProspectBloc] = useState('direct');
  const [prospectSourceFilter, setProspectSourceFilter] = useState('all');
  const [prospectPartnerFilter, setProspectPartnerFilter] = useState('all');
  const [prospectStatusFilter, setProspectStatusFilter] = useState('all');
  const [conseilStatusFilter, setConseilStatusFilter] = useState('all');
  const [chantierStatusFilter, setChantierStatusFilter] = useState('all');

  // Sort states
  const [userSort, setUserSort] = useState({ field: 'created_at', dir: 'desc' });
  const [prospectSort, setProspectSort] = useState({ field: 'created_at', dir: 'desc' });

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (activeTab === 'purge') fetchPurgeCollections();
  }, [activeTab]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [statsRes, usersRes, projectsRes, projectsByDateRes, partnersRes, affiliatesRes, invoicesRes, expensesRes, analystsRes, chiffreursRes, prospectsRes, chantierRes, settingsRes, accountingRes, networkRes, artisansRes, leadsRes] = await Promise.all([
        axios.get(`${API}/admin/stats`),
        axios.get(`${API}/admin/users`),
        axios.get(`${API}/expert/projects`),
        axios.get(`${API}/admin/projects/by-date`),
        axios.get(`${API}/admin/partners`).catch(() => ({ data: [] })),
        axios.get(`${API}/admin/affiliates`).catch(() => ({ data: [] })),
        axios.get(`${API}/admin/invoices`).catch(() => ({ data: [] })),
        axios.get(`${API}/admin/expenses`).catch(() => ({ data: [] })),
        axios.get(`${API}/admin/analysts`).catch(() => ({ data: [] })),
        axios.get(`${API}/admin/chiffreurs`).catch(() => ({ data: [] })),
        axios.get(`${API}/admin/prospects`).catch(() => ({ data: [] })),
        axios.get(`${API}/admin/chantier-demands`).catch(() => ({ data: [] })),
        axios.get(`${API}/admin/settings`).catch(() => ({ data: {} })),
        axios.get(`${API}/admin/accounting/full-summary`).catch(() => ({ data: null })),
        axios.get(`${API}/admin/reseau/members`).catch(() => ({ data: [] })),
        axios.get(`${API}/admin/artisans`).catch(() => ({ data: [] })),
        axios.get(`${API}/admin/reseau/leads`).catch(() => ({ data: [] })),
      ]);
      
      setStats(statsRes.data);
      setUsers(usersRes.data);
      setProjects(projectsRes.data);
      setProjectsByDate(projectsByDateRes.data);
      setPartners(partnersRes.data);
      setAffiliates(affiliatesRes.data);
      setInvoices(invoicesRes.data);
      setExpenses(expensesRes.data);
      setAnalysts(analystsRes.data);
      setChiffreurs(chiffreursRes.data);
      setProspects(prospectsRes.data);
      setChantierDemands(chantierRes.data);
      setSettings(settingsRes.data);
      loadAccountantHistory();
      setAccountingSummary(accountingRes.data);
      setNetworkMembers(networkRes.data);
      setArtisans(artisansRes.data);
      setReseauLeads(leadsRes.data);
      
      // Fetch hotline conversations
      try {
        const hotlineRes = await axios.get(`${API}/admin/hotline/conversations`);
        setHotlineConversations(hotlineRes.data || []);
      } catch (e) {
        console.log('No hotline conversations');
      }
      
      // Fetch partner subscriptions
      try {
        const subsRes = await axios.get(`${API}/admin/partenaires/subscriptions`);
        setPartnerSubscriptions(subsRes.data || []);
      } catch (e) {
        console.log('No partner subscriptions');
      }
      
      // Fetch Algeria manual payments
      try {
        const algRes = await axios.get(`${API}/admin/algerie/payment-requests`);
        setAlgeriaPayments(algRes.data || []);
      } catch (e) {
        console.log('No Algeria payments');
      }
      
      // Fetch prospection demandes
      try {
        const prospectionRes = await axios.get(`${API}/admin/prospection/demandes`);
        setProspectionDemandes(prospectionRes.data || []);
        const statsRes = await axios.get(`${API}/admin/prospection/stats`);
        setProspectionStats(statsRes.data || null);
      } catch (e) {
        console.log('No prospection data');
      }
      
      // Fetch conseil stratégique requests
      try {
        const conseilRes = await axios.get(`${API}/admin/conseil-requests`);
        setConseilRequests(conseilRes.data || []);
      } catch (e) {
        console.log('No conseil data');
      }
      
      // Fetch gratuités data
      try {
        const [demoRes, studiesRes, statsRes] = await Promise.all([
          axios.get(`${API}/admin/demo-partners`),
          axios.get(`${API}/admin/free-studies`),
          axios.get(`${API}/admin/gratuites/stats`)
        ]);
        setDemoPartners(demoRes.data || []);
        setFreeStudies(studiesRes.data || []);
        setGratuitesStats(statsRes.data || null);
      } catch (e) {
        console.log('No gratuites data');
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const formatPrice = (price) => {
    if (price === null || price === undefined) return '0 €';
    return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(price);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return '-';
    const d = new Date(dateStr);
    const today = new Date();
    const isToday = d.toDateString() === today.toDateString();
    if (isToday) return d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
    return d.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' }) + ' ' + d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
  };

  const formatDateFull = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
  };

  // Group invoices by date (today, this month by day, by month, by year)
  const invoicesByDate = useMemo(() => {
    if (!invoices.length) return null;
    
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    
    const result = {
      today: [],
      yesterday: [],
      thisMonth: {}, // { day: invoices[] }
      byMonth: {}, // { 'YYYY-MM': invoices[] }
      byYear: {} // { 'YYYY': invoices[] }
    };
    
    invoices.forEach(invoice => {
      const invoiceDate = new Date(invoice.created_at);
      invoiceDate.setHours(0, 0, 0, 0);
      
      if (invoiceDate.getTime() === today.getTime()) {
        result.today.push(invoice);
      } else if (invoiceDate.getTime() === yesterday.getTime()) {
        result.yesterday.push(invoice);
      } else if (invoiceDate.getMonth() === today.getMonth() && invoiceDate.getFullYear() === today.getFullYear()) {
        const day = invoiceDate.getDate();
        if (!result.thisMonth[day]) result.thisMonth[day] = [];
        result.thisMonth[day].push(invoice);
      } else if (invoiceDate.getFullYear() === today.getFullYear()) {
        const monthKey = `${invoiceDate.getFullYear()}-${String(invoiceDate.getMonth() + 1).padStart(2, '0')}`;
        if (!result.byMonth[monthKey]) result.byMonth[monthKey] = [];
        result.byMonth[monthKey].push(invoice);
      } else {
        const yearKey = String(invoiceDate.getFullYear());
        if (!result.byYear[yearKey]) result.byYear[yearKey] = [];
        result.byYear[yearKey].push(invoice);
      }
    });
    
    return result;
  }, [invoices]);

  // Bulk delete functions
  const bulkDeleteUsers = async () => {
    if (!selectedUsers.length || !window.confirm(`Supprimer ${selectedUsers.length} utilisateur(s) ? Leurs dossiers et factures seront conservés.`)) return;
    try {
      await axios.post(`${API}/admin/users/bulk-delete`, { ids: selectedUsers });
      toast.success(`${selectedUsers.length} utilisateur(s) supprimé(s)`);
      setSelectedUsers([]);
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const bulkDeleteProjects = async () => {
    if (!selectedProjects.length || !window.confirm(`Supprimer ${selectedProjects.length} dossier(s) ?`)) return;
    try {
      await axios.post(`${API}/admin/projects/bulk-delete`, { ids: selectedProjects });
      toast.success(`${selectedProjects.length} dossier(s) supprimé(s)`);
      setSelectedProjects([]);
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const bulkDeleteInvoices = async () => {
    if (!selectedInvoices.length || !window.confirm(`Supprimer ${selectedInvoices.length} facture(s) ?`)) return;
    try {
      await axios.post(`${API}/admin/invoices/bulk-delete`, { ids: selectedInvoices });
      toast.success(`${selectedInvoices.length} facture(s) supprimée(s)`);
      setSelectedInvoices([]);
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const bulkDeleteExpenses = async () => {
    if (!selectedExpenses.length || !window.confirm(`Supprimer ${selectedExpenses.length} dépense(s) ?`)) return;
    try {
      await axios.post(`${API}/admin/expenses/bulk-delete`, { ids: selectedExpenses });
      toast.success(`${selectedExpenses.length} dépense(s) supprimée(s)`);
      setSelectedExpenses([]);
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const bulkDeleteAffiliates = async () => {
    if (!selectedAffiliates.length || !window.confirm(`Supprimer ${selectedAffiliates.length} affilié(s) ?`)) return;
    try {
      await axios.post(`${API}/admin/affiliates/bulk-delete`, { ids: selectedAffiliates });
      toast.success(`${selectedAffiliates.length} affilié(s) supprimé(s)`);
      setSelectedAffiliates([]);
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const bulkDeleteAnalysts = async () => {
    if (!selectedAnalysts.length || !window.confirm(`Supprimer ${selectedAnalysts.length} analyste(s) ? Leurs dossiers seront désassignés.`)) return;
    try {
      await axios.post(`${API}/admin/analysts/bulk-delete`, { ids: selectedAnalysts });
      toast.success(`${selectedAnalysts.length} analyste(s) supprimé(s)`);
      setSelectedAnalysts([]);
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const bulkDeletePartners = async () => {
    if (!selectedPartners.length || !window.confirm(`Supprimer ${selectedPartners.length} partenaire(s) ?`)) return;
    try {
      await axios.post(`${API}/admin/partners/bulk-delete`, { ids: selectedPartners });
      toast.success(`${selectedPartners.length} partenaire(s) supprimé(s)`);
      setSelectedPartners([]);
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  // Single delete functions
  const deleteAffiliate = async (affiliateId) => {
    if (!window.confirm('Supprimer cet affilié ?')) return;
    try {
      await axios.delete(`${API}/admin/affiliates/${affiliateId}`);
      toast.success('Affilié supprimé');
      fetchData();
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const deleteUser = async (userId) => {
    if (!window.confirm('Supprimer cet utilisateur ? Ses dossiers et factures seront conservés.')) return;
    try {
      await axios.delete(`${API}/admin/users/${userId}`);
      toast.success('Utilisateur supprimé');
      fetchData();
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const deleteProject = async (projectId) => {
    if (!window.confirm('Supprimer ce dossier ?')) return;
    try {
      await axios.delete(`${API}/admin/projects/${projectId}`);
      toast.success('Dossier supprimé');
      fetchData();
    } catch (error) {
      toast.error('Erreur');
    }
  };

  // Update functions
  const updateUser = async (userId, data) => {
    try {
      await axios.put(`${API}/admin/users/${userId}`, data);
      toast.success('Utilisateur mis à jour');
      setEditingUser(null);
      fetchData();
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const updateProject = async (projectId, data) => {
    try {
      await axios.put(`${API}/admin/projects/${projectId}`, data);
      toast.success('Dossier mis à jour');
      setEditingProject(null);
      fetchData();
    } catch (error) {
      toast.error('Erreur');
    }
  };

  // Partner functions
  const createPartner = async () => {
    if (!newPartner.name || !newPartner.subdomain || !newPartner.contact_email) {
      toast.error('Remplissez tous les champs obligatoires');
      return;
    }
    try {
      await axios.post(`${API}/admin/partners`, newPartner);
      toast.success('Partenaire créé');
      setShowPartnerModal(false);
      setNewPartner({ name: '', subdomain: '', contact_email: '', logo_url: '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    }
  };

  const deletePartner = async (partnerId) => {
    if (!window.confirm('Supprimer ce partenaire ?')) return;
    try {
      await axios.delete(`${API}/admin/partners/${partnerId}`);
      toast.success('Partenaire supprimé');
      fetchData();
    } catch (error) {
      toast.error('Erreur');
    }
  };

  // Analyst functions
  const createAnalyst = async () => {
    if (!newAnalyst.name || !newAnalyst.email || !newAnalyst.password) {
      toast.error('Remplissez tous les champs obligatoires');
      return;
    }
    try {
      const savedPassword = newAnalyst.password;
      await axios.post(`${API}/admin/analysts`, newAnalyst);
      setShowAnalystModal(false);
      setNewAnalyst({ name: '', email: '', phone: '', password: '' });
      setCreatedAnalystPassword({ email: newAnalyst.email, password: savedPassword, name: newAnalyst.name });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    }
  };

  const updateAnalystPassword = async () => {
    if (!passwordModal.newPassword || passwordModal.newPassword.length < 4) {
      toast.error('Le mot de passe doit contenir au moins 4 caractères');
      return;
    }
    try {
      await axios.put(`${API}/admin/analysts/${passwordModal.analyst.id}/password`, {
        new_password: passwordModal.newPassword
      });
      toast.success('Mot de passe mis à jour !');
      setPasswordModal({ show: false, analyst: null, newPassword: '' });
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const viewAnalystDetails = async (analyst) => {
    try {
      const response = await axios.get(`${API}/admin/analysts/${analyst.id}`);
      setViewingAnalyst(response.data);
    } catch (error) {
      toast.error('Erreur lors du chargement');
    }
  };

  const deleteAnalyst = async (analystId) => {
    if (!window.confirm('Supprimer cet analyste ? Ses dossiers seront désassignés.')) return;
    try {
      await axios.delete(`${API}/admin/analysts/${analystId}`);
      toast.success('Analyste supprimé');
      fetchData();
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const assignProjectToAnalyst = async (projectId, analystId) => {
    try {
      await axios.post(`${API}/admin/analysts/${analystId}/assign?project_id=${projectId}`);
      toast.success('Dossier assigné');
      setAssignModal({ show: false, projectId: null });
      fetchData();
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const validateProject = async (projectId, approved) => {
    try {
      await axios.post(`${API}/admin/projects/${projectId}/validate?approved=${approved}`);
      toast.success(approved ? 'Dossier validé' : 'Dossier renvoyé à l\'analyste');
      fetchData();
    } catch (error) {
      toast.error('Erreur');
    }
  };

  // ============== CHIFFREUR FUNCTIONS ==============
  const createChiffreur = async () => {
    if (!newChiffreur.name || !newChiffreur.email || !newChiffreur.password) {
      toast.error('Remplissez tous les champs obligatoires');
      return;
    }
    try {
      const savedPassword = newChiffreur.password;
      await axios.post(`${API}/admin/chiffreurs`, newChiffreur);
      setShowChiffreurModal(false);
      setNewChiffreur({ name: '', email: '', phone: '', password: '' });
      setCreatedChiffreurPassword({ email: newChiffreur.email, password: savedPassword, name: newChiffreur.name });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    }
  };

  const updateChiffreurPassword = async () => {
    if (!chiffreurPasswordModal.newPassword || chiffreurPasswordModal.newPassword.length < 4) {
      toast.error('Le mot de passe doit contenir au moins 4 caractères');
      return;
    }
    try {
      await axios.put(`${API}/admin/chiffreurs/${chiffreurPasswordModal.chiffreur.id}/password`, {
        new_password: chiffreurPasswordModal.newPassword
      });
      toast.success('Mot de passe mis à jour !');
      setChiffreurPasswordModal({ show: false, chiffreur: null, newPassword: '' });
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const viewChiffreurDetails = async (chiffreur) => {
    try {
      const response = await axios.get(`${API}/admin/chiffreurs/${chiffreur.id}`);
      setViewingChiffreur(response.data);
    } catch (error) {
      toast.error('Erreur lors du chargement');
    }
  };

  const deleteChiffreur = async (chiffreurId) => {
    if (!window.confirm('Supprimer ce chiffreur ? Ses dossiers seront désassignés.')) return;
    try {
      await axios.delete(`${API}/admin/chiffreurs/${chiffreurId}`);
      toast.success('Chiffreur supprimé');
      fetchData();
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const assignProjectToChiffreur = async (chantierDemandId, chiffreurId) => {
    try {
      await axios.post(`${API}/admin/chiffreurs/${chiffreurId}/assign-chantier?chantier_id=${chantierDemandId}`);
      toast.success('Chantier assigné au chiffreur');
      setAssignChiffreurModal({ show: false, projectId: null });
      fetchData();
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const validateChiffrage = async (projectId, approved) => {
    try {
      await axios.post(`${API}/admin/projects/${projectId}/validate-chiffrage?approved=${approved}`);
      toast.success(approved ? 'Chiffrage validé' : 'Chiffrage renvoyé au chiffreur');
      fetchData();
    } catch (error) {
      toast.error('Erreur');
    }
  };

  // Expense functions
  const uploadExpenseFile = async (file) => {
    setExpenseFileUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await axios.post(`${API}/admin/expenses/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      setNewExpense({
        ...newExpense,
        file_url: response.data.file_url,
        file_name: response.data.file_name
      });
      toast.success('Fichier uploadé');
    } catch (error) {
      toast.error('Erreur lors de l\'upload');
    } finally {
      setExpenseFileUploading(false);
    }
  };

  const createExpense = async () => {
    if (!newExpense.description || !newExpense.amount_ht || !newExpense.invoice_date) {
      toast.error('Remplissez tous les champs obligatoires');
      return;
    }
    try {
      await axios.post(`${API}/admin/expenses`, {
        ...newExpense,
        amount_ht: parseFloat(newExpense.amount_ht),
        tva_rate: parseFloat(newExpense.tva_rate)
      });
      toast.success('Dépense ajoutée');
      setShowExpenseModal(false);
      setNewExpense({ description: '', category: 'fournisseur', amount_ht: '', tva_rate: 0.20, supplier_name: '', invoice_number: '', invoice_date: new Date().toISOString().split('T')[0], file_url: '', file_name: '' });
      fetchData();
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const viewAffiliateDetails = async (affiliate) => {
    try {
      const response = await axios.get(`${API}/admin/affiliates/${affiliate.id}`);
      setViewingAffiliate(response.data);
    } catch (error) {
      toast.error('Erreur lors du chargement');
    }
  };

  const updateAffiliateCommission = async (affiliateId, ratePercent) => {
    const rate = parseFloat(ratePercent);
    if (isNaN(rate) || rate < 0 || rate > 100) { toast.error('Valeur invalide (0-100%)'); return; }
    try {
      await axios.put(`${API}/admin/affiliates/${affiliateId}?commission_rate=${rate / 100}`);
      toast.success('Commission mise à jour');
      setEditingAffiliateId(null);
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const deleteExpense = async (expenseId) => {
    if (!window.confirm('Supprimer cette dépense ?')) return;
    try {
      await axios.delete(`${API}/admin/expenses/${expenseId}`);
      toast.success('Dépense supprimée');
      fetchData();
    } catch (error) {
      toast.error('Erreur');
    }
  };

  // Affiliate functions
  const createAffiliate = async (userId) => {
    try {
      await axios.post(`${API}/admin/affiliates?user_id=${userId}`);
      toast.success('Affilié créé');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    }
  };

  // Settings
  const updateSettings = async () => {
    try {
      await axios.put(`${API}/admin/settings`, settings);
      toast.success('Paramètres enregistrés');
    } catch (error) {
      toast.error('Erreur');
    }
  };

  // Filtered data
  const clientUsers = users.filter(u => !u.is_deleted && u.role !== 'admin' && u.role !== 'expert');
  const startOfMonth = new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString();
  const clientUsersThisMonth = clientUsers.filter(u => (u.created_at || '') >= startOfMonth).length;

  const filteredUsers = clientUsers
    .filter(u =>
      u.email?.toLowerCase().includes(userSearch.toLowerCase()) ||
      u.name?.toLowerCase().includes(userSearch.toLowerCase()) ||
      u.phone?.includes(userSearch)
    )
    .sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0));

  const filteredProjects = projects.filter(p =>
    p.address?.toLowerCase().includes(projectSearch.toLowerCase())
  );

  // Toggle date section
  const toggleDateSection = (key) => {
    setExpandedDates(prev => ({ ...prev, [key]: !prev[key] }));
  };

  // Render project row
  const renderProjectRow = (project, showUser = true) => {
    const status = STATUS_CONFIG[project.status] || STATUS_CONFIG.pending;
    const user = showUser ? users.find(u => u.id === project.user_id) : null;
    const analyst = analysts.find(a => a.id === project.assigned_to);
    
    return (
      <tr key={project.id} className="border-b hover:bg-gray-50">
        <td className="p-3">
          <Checkbox 
            checked={selectedProjects.includes(project.id)}
            onCheckedChange={(checked) => {
              if (checked) {
                setSelectedProjects([...selectedProjects, project.id]);
              } else {
                setSelectedProjects(selectedProjects.filter(id => id !== project.id));
              }
            }}
          />
        </td>
        <td className="p-3">
          <p className="font-medium truncate max-w-[200px]">{project.address}</p>
          <div className="flex items-center gap-1 mt-0.5">
            <p className="text-xs text-muted-foreground">{project.surface} m²</p>
            {project.payment_status !== 'paid' && (
              <Badge className="bg-red-100 text-red-700 text-[10px] py-0 px-1.5 h-4">⚠ NON PAYÉ</Badge>
            )}
          </div>
        </td>
        <td className="p-3 text-sm text-muted-foreground whitespace-nowrap">
          {formatDateTime(project.created_at)}
        </td>
        {showUser && (
          <td className="p-3">
            {user ? (
              <div>
                <p className="text-sm">{user.name || user.email}</p>
                {user.phone && (
                  <a href={`tel:${user.phone}`} className="text-xs text-blue-600 flex items-center gap-1 hover:underline">
                    <Phone className="w-3 h-3" />{user.phone}
                  </a>
                )}
              </div>
            ) : (
              <span className="text-muted-foreground text-sm">Utilisateur supprimé</span>
            )}
          </td>
        )}
        <td className="p-3 font-medium">{formatPrice(project.purchase_price)}</td>
        <td className="p-3">{formatPrice(project.total_works || 0)}</td>
        <td className="p-3">
          <Badge className={status.color}>{status.label}</Badge>
        </td>
        <td className="p-3">
          {analyst ? (
            <span className="text-sm text-blue-600">{analyst.name}</span>
          ) : project.status === 'pending_validation' ? (
            <div className="flex gap-1">
              <Button size="sm" variant="ghost" className="text-emerald-600" onClick={() => validateProject(project.id, true)}>
                <Check className="w-4 h-4" />
              </Button>
              <Button size="sm" variant="ghost" className="text-red-600" onClick={() => validateProject(project.id, false)}>
                <X className="w-4 h-4" />
              </Button>
            </div>
          ) : (
            <Button size="sm" variant="outline" onClick={() => setAssignModal({ show: true, projectId: project.id })}>
              <UserCheck className="w-3 h-3 mr-1" />
              Assigner Analyste
            </Button>
          )}
        </td>
        <td className="p-3 text-right">
          <div className="flex items-center justify-end gap-1">
            {/* Transfer button - only for completed projects */}
            {project.status === 'completed' && !project.transferred && (
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => openTransferModal(project)}
                className="text-emerald-600 hover:bg-emerald-50"
                title="Transférer à un artisan"
              >
                <Send className="w-4 h-4" />
              </Button>
            )}
            {project.transferred && (
              <Badge variant="outline" className="text-xs bg-emerald-50 text-emerald-700">
                Transféré
              </Badge>
            )}
            <Button variant="ghost" size="sm" onClick={() => setEditingProject(project)}>
              <Edit2 className="w-4 h-4" />
            </Button>
            <Button variant="ghost" size="sm" onClick={() => deleteProject(project.id)} className="text-red-500">
              <Trash2 className="w-4 h-4" />
            </Button>
          </div>
        </td>
      </tr>
    );
  };

  // ===================== NETWORK MEMBER FUNCTIONS =====================
  const MEMBER_TYPES = {
    partenaire: { label: 'Partenaire', color: 'bg-purple-100 text-purple-700', icon: Building2 },
    affilie: { label: 'Affilié', color: 'bg-blue-100 text-blue-700', icon: Link2 },
    chiffreur: { label: 'Chiffreur', color: 'bg-amber-100 text-amber-700', icon: Calculator },
    analyste: { label: 'Analyste', color: 'bg-cyan-100 text-cyan-700', icon: FileText },
    artisan: { label: 'Artisan / Société', color: 'bg-emerald-100 text-emerald-700', icon: Wrench },
    artisan: { label: 'Artisan / Société', color: 'bg-teal-100 text-teal-700', icon: Wrench }
  };

  const NETWORK_STATUS = {
    pending: { label: 'En attente', color: 'bg-amber-100 text-amber-700', icon: Clock },
    validated: { label: 'Validé', color: 'bg-emerald-100 text-emerald-700', icon: CheckCircle },
    approved: { label: 'Validé', color: 'bg-emerald-100 text-emerald-700', icon: CheckCircle },
    rejected: { label: 'Refusé', color: 'bg-red-100 text-red-700', icon: XCircle }
  };

  const filteredNetworkMembers = networkMembers.filter(member => {
    if (networkFilter === 'all') return true;
    if (networkFilter === 'validated') return member.status === 'validated' || member.status === 'approved';
    return member.status === networkFilter;
  });

  const validateNetworkMember = async (memberId) => {
    try {
      await axios.put(`${API}/admin/reseau/members/${memberId}/validate`);
      toast.success('Membre validé ! Un email lui a été envoyé. (rappeler de vérifier les spams)');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la validation');
    }
  };

  const rejectNetworkMember = async (memberId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir refuser ce membre ?')) return;
    try {
      await axios.put(`${API}/admin/reseau/members/${memberId}/reject`);
      toast.success('Membre refusé');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors du refus');
    }
  };

  const deleteNetworkMember = async (memberId) => {
    if (!window.confirm('Supprimer définitivement ce membre ?')) return;
    try {
      await axios.delete(`${API}/admin/reseau/members/${memberId}`);
      toast.success('Membre supprimé');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    }
  };

  const pendingNetworkCount = networkMembers.filter(m => m.status === 'pending').length;

  // ===================== TRANSFER FUNCTIONS =====================
  const openTransferModal = async (project) => {
    setTransferModal({ show: true, project });
    setTransferNotes('');
    setNearbyArtisans([]);
    
    // Fetch nearby artisans if project has coordinates
    try {
      // First, try to get project coordinates (might need geocoding)
      let lat = project.latitude;
      let lon = project.longitude;
      
      // If no coordinates, use a general query for validated artisans
      const endpoint = lat && lon 
        ? `${API}/admin/reseau/artisans/nearby?lat=${lat}&lon=${lon}`
        : `${API}/admin/reseau/members?status=validated&member_type=artisan`;
      
      const response = await axios.get(endpoint);
      setNearbyArtisans(response.data || []);
    } catch (error) {
      // Fallback to all validated artisans
      const validatedArtisans = networkMembers.filter(m => 
        (m.member_type === 'artisan') &&
        (m.status === 'validated' || m.status === 'approved')
      );
      setNearbyArtisans(validatedArtisans);
    }
  };

  const executeTransfer = async (artisanId) => {
    if (!transferModal.project) return;
    
    setTransferLoading(true);
    try {
      await axios.post(`${API}/admin/projects/transfer`, {
        project_id: transferModal.project.id,
        artisan_id: artisanId,
        notes: transferNotes
      });
      
      toast.success('Dossier transféré ! L\'artisan a été notifié par email.');
      setTransferModal({ show: false, project: null });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors du transfert');
    } finally {
      setTransferLoading(false);
    }
  };

  // Polling automatique toutes les 10s quand une conversation est ouverte
  useEffect(() => {
    if (!activeConversation) return;
    const interval = setInterval(async () => {
      try {
        const convType = activeConversation.conversation_type || 'partner';
        const response = await axios.get(`${API}/admin/hotline/messages/${activeConversation._id}?skip=0&limit=50&type=${convType}`);
        setHotlineMessages(response.data.messages || []);
        setHotlineHasMore(response.data.has_more || false);
        setHotlineTotal(response.data.total || 0);
      } catch (e) { /* silent */ }
    }, 10000);
    return () => clearInterval(interval);
  }, [activeConversation]);

  // Polling badge non lu dans la liste des conversations (toutes les 15s)
  useEffect(() => {
    const refreshConversations = async () => {
      try {
        const res = await axios.get(`${API}/admin/hotline/conversations`);
        setHotlineConversations(prev => {
          const updated = res.data || [];
          // Garder unread_count=0 pour les conversations déjà ouvertes
          return updated.map(c =>
            activeConversation && c._id === activeConversation._id ? { ...c, unread_count: 0 } : c
          );
        });
      } catch (e) { /* silent */ }
    };
    const interval = setInterval(refreshConversations, 15000);
    return () => clearInterval(interval);
  }, [activeConversation]);
  const openHotlineConversation = async (conversation) => {
    setActiveConversation(conversation);
    setHotlineSkip(0);
    try {
      const convType = conversation.conversation_type || 'partner';
      const response = await axios.get(`${API}/admin/hotline/messages/${conversation._id}?skip=0&limit=50&type=${convType}`);
      setHotlineMessages(response.data.messages || []);
      setHotlineHasMore(response.data.has_more || false);
      setHotlineTotal(response.data.total || 0);
      // Vider le badge rouge immédiatement dans la liste des conversations
      setHotlineConversations(prev => prev.map(c =>
        c._id === conversation._id ? { ...c, unread_count: 0 } : c
      ));
    } catch (error) {
      toast.error('Erreur lors du chargement des messages');
    }
  };

  const loadMoreHotlineMessages = async () => {
    if (!activeConversation || loadingMoreMessages) return;
    setLoadingMoreMessages(true);
    const newSkip = hotlineSkip + 50;
    try {
      const convType = activeConversation.conversation_type || 'partner';
      const response = await axios.get(`${API}/admin/hotline/messages/${activeConversation._id}?skip=${newSkip}&limit=50&type=${convType}`);
      setHotlineMessages(prev => [...(response.data.messages || []), ...prev]);
      setHotlineHasMore(response.data.has_more || false);
      setHotlineSkip(newSkip);
    } catch (error) {
      toast.error('Erreur lors du chargement');
    } finally {
      setLoadingMoreMessages(false);
    }
  };

  const sendHotlineMessage = async () => {
    if (!newHotlineMessage.trim() || !activeConversation) return;
    
    setSendingHotlineMessage(true);
    try {
      const convType = activeConversation.conversation_type || 'partner';
      await axios.post(`${API}/admin/hotline/send/${activeConversation._id}?type=${convType}`, {
        message: newHotlineMessage
      });
      
      toast.success('Message envoyé');
      setNewHotlineMessage('');
      
      // Refresh messages
      const response = await axios.get(`${API}/admin/hotline/messages/${activeConversation._id}?skip=0&limit=50&type=${convType}`);
      setHotlineMessages(response.data.messages || []);
      setHotlineHasMore(response.data.has_more || false);
      setHotlineTotal(response.data.total || 0);
      setHotlineSkip(0);
    } catch (error) {
      toast.error('Erreur lors de l\'envoi');
    } finally {
      setSendingHotlineMessage(false);
    }
  };

  const totalUnreadHotline = hotlineConversations.reduce((sum, c) => sum + (c.unread_count || 0), 0);

  // ===================== PARTNER SUBSCRIPTION FUNCTIONS =====================
  const blockPartner = async (partnerId) => {
    if (!window.confirm('Bloquer l\'accès de ce partenaire ?')) return;
    try {
      await axios.post(`${API}/admin/partenaires/${partnerId}/block`);
      toast.success('Partenaire bloqué');
      fetchData();
    } catch (error) {
      toast.error('Erreur lors du blocage');
    }
  };

  const unblockPartner = async (partnerId) => {
    try {
      await axios.post(`${API}/admin/partenaires/${partnerId}/unblock`);
      toast.success('Partenaire débloqué');
      fetchData();
    } catch (error) {
      toast.error('Erreur lors du déblocage');
    }
  };

  const togglePartnerDomain = async (partnerId, currentVal) => {
    try {
      const res = await axios.post(`${API}/admin/partenaires/${partnerId}/domain-toggle`);
      toast.success(res.data.domain_enabled ? 'Option domaine activée pour ce partenaire' : 'Option domaine désactivée');
      fetchData();
    } catch {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const setPartnerCountry = async (partnerId, country) => {
    try {
      await axios.patch(`${API}/admin/partenaires/${partnerId}/country`, { country });
      toast.success(country === 'algerie' ? '🇩🇿 Partenaire basculé en Algérie (DZD)' : '🇫🇷 Partenaire basculé en France (EUR)');
      fetchData();
    } catch {
      toast.error('Erreur lors du changement de pays');
    }
  };

  const validateAlgeriaPayment = async (paymentId, status, note = '') => {
    try {
      await axios.patch(`${API}/admin/algerie/payment-requests/${paymentId}`, { status, note });
      toast.success(status === 'approved' ? 'Paiement validé — abonnement activé' : 'Paiement rejeté');
      fetchData();
    } catch {
      toast.error('Erreur lors de la validation');
    }
  };

  const cancelPartnerSubscription = async (partnerId) => {
    if (!window.confirm('Annuler l\'abonnement et bloquer l\'accès de ce partenaire ?')) return;
    try {
      await axios.post(`${API}/admin/partenaires/${partnerId}/cancel-subscription`);
      toast.success('Abonnement annulé');
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de l\'annulation');
    }
  };

  const sendPartnerReminder = async (partnerId) => {
    try {
      await axios.post(`${API}/admin/partenaires/${partnerId}/send-reminder`);
      toast.success('Rappel de paiement envoyé ! (vérifier spams si pas reçu)');
    } catch (error) {
      toast.error('Erreur lors de l\'envoi');
    }
  };

  const submitManualPayment = async () => {
    if (!manualPaymentPartner) return;
    const amount = parseFloat(manualPaymentForm.amount);
    if (!amount || amount <= 0) {
      toast.error('Montant invalide');
      return;
    }
    try {
      const res = await axios.post(
        `${API}/admin/partenaires/${manualPaymentPartner.id}/manual-payment`,
        {
          amount: amount,
          payment_method: manualPaymentForm.payment_method,
          payment_date: manualPaymentForm.payment_date,
          notes: manualPaymentForm.notes || null,
        }
      );
      toast.success(`Paiement enregistré ! Facture ${res.data.invoice_number} créée.`);
      setManualPaymentPartner(null);
      setManualPaymentForm({ amount: '', payment_method: 'virement', payment_date: new Date().toISOString().split('T')[0], notes: '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'enregistrement');
    }
  };

  const updatePartnerPricing = async (partnerId, customPrice) => {
    try {
      await axios.put(`${API}/admin/partenaires/${partnerId}/pricing`, { custom_subscription_price: customPrice });
      toast.success('Prix personnalisé mis à jour');
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de la mise à jour du prix');
    }
  };

  const updatePartnerServices = async (partnerId, services) => {
    try {
      await axios.put(`${API}/admin/partenaires/${partnerId}/services`, { enabled_services: services });
      toast.success('Services mis à jour');
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de la mise à jour des services');
    }
  };

  const renewPartner = async (partnerId) => {
    if (!window.confirm('Renouveler l\'abonnement de ce partenaire pour 1 mois ?')) return;
    try {
      await axios.post(`${API}/admin/partenaires/${partnerId}/renew`);
      toast.success('Abonnement renouvelé pour 1 mois');
      fetchData();
    } catch (error) {
      toast.error('Erreur lors du renouvellement');
    }
  };

  const sendRenewalLink = async (partnerId) => {
    if (!window.confirm('Envoyer un lien de paiement Stripe par email au partenaire pour le renouvellement ?')) return;
    try {
      const res = await axios.post(`${API}/admin/partenaires/${partnerId}/send-renewal-link`);
      toast.success(res.data.message || 'Lien de renouvellement envoyé');
      if (res.data.checkout_url) {
        toast.info(`Lien Stripe : le partenaire a reçu l'email (${res.data.amount}€)`);
      }
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de l\'envoi du lien');
    }
  };

  const resendPartnerCredentials = async (partnerId) => {
    if (!window.confirm('Générer un nouveau mot de passe et renvoyer les identifiants par email au partenaire ?')) return;
    try {
      const res = await axios.post(`${API}/admin/partenaires/${partnerId}/resend-credentials`);
      toast.success(res.data.message || 'Identifiants renvoyés');
    } catch (error) {
      toast.error('Erreur lors du renvoi des identifiants');
    }
  };

  // Purge functions
  const fetchPurgeCollections = async () => {
    try {
      const res = await axios.get(`${API}/admin/purge/collections`);
      setPurgeCollections(res.data);
    } catch (error) {
      toast.error('Erreur chargement des collections');
    }
  };

  const purgeSingleCollection = async (key, label) => {
    if (!window.confirm(`Supprimer TOUTES les données de "${label}" ? Cette action est irréversible.`)) return;
    setPurgeLoading(true);
    try {
      const res = await axios.post(`${API}/admin/purge/collection/${key}`);
      toast.success(res.data.message);
      fetchPurgeCollections();
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de la purge');
    }
    setPurgeLoading(false);
  };

  const purgeAll = async () => {
    const confirmation = window.prompt('Pour supprimer TOUTES les données, tapez "SUPPRIMER TOUT" :');
    if (confirmation !== 'SUPPRIMER TOUT') {
      toast.info('Purge annulée');
      return;
    }
    setPurgeLoading(true);
    try {
      const res = await axios.post(`${API}/admin/purge/all`);
      toast.success(res.data.message);
      // Reset immédiat des états pour éviter l'affichage de données périmées
      setPartnerSubscriptions([]);
      setNetworkMembers([]);
      setProspects([]);
      setUsers([]);
      setProjects([]);
      fetchPurgeCollections();
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de la purge');
    }
    setPurgeLoading(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-navy flex items-center justify-center">
        <div className="text-center">
          <RefreshCw className="w-8 h-8 text-gold animate-spin mx-auto mb-4" />
          <p className="text-white">Chargement...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <header className="bg-navy text-white shadow-lg">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 sm:py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 sm:gap-4">
              <Button variant="ghost" size="sm" onClick={() => setMobileSidebarOpen(true)} className="text-white/70 hover:text-white hover:bg-white/10 lg:hidden" data-testid="mobile-menu-btn">
                <Menu className="w-5 h-5" />
              </Button>
              <Button variant="ghost" size="sm" onClick={() => navigate('/')} className="text-white/70 hover:text-white hover:bg-white/10 hidden sm:flex">
                <ArrowLeft className="w-4 h-4 mr-2" />Retour
              </Button>
              <div className="h-6 w-px bg-white/20 hidden sm:block" />
              <div>
                <h1 className="font-heading text-lg sm:text-xl font-bold">ADMINISTRATION</h1>
                <p className="text-xs text-white/60 hidden sm:block">L'Œil du Marchand</p>
              </div>
            </div>
            <div className="flex items-center gap-2 sm:gap-4">
              <Button variant="ghost" size="sm" onClick={() => navigate('/expert')} className="text-gold hover:bg-gold/10 text-xs sm:text-sm">
                <span className="hidden sm:inline">Dashboard Expert</span>
                <span className="sm:hidden">Expert</span>
              </Button>
              <Button variant="ghost" size="sm" onClick={logout} className="text-white/70 hover:text-white hover:bg-white/10">
                <LogOut className="w-4 h-4" /><span className="ml-2 hidden sm:inline">Déconnexion</span>
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-[1400px] mx-auto px-3 sm:px-4 py-4 sm:py-6">
        <div className="flex gap-4 sm:gap-6">
          <AdminSidebar
            activeTab={activeTab}
            onTabChange={handleTabChange}
            mobileOpen={false}
            onMobileClose={() => setMobileSidebarOpen(false)}
            badges={{
              network: pendingNetworkCount,
              hotline: totalUnreadHotline,
              conseil: conseilRequests.filter(c => c.status === 'paid' || c.status === 'pending').length,
            }}
          />
          <div className="flex-1 min-w-0">
        <Tabs value={activeTab} onValueChange={handleTabChange}>
          <TabsList className="hidden">
            <TabsTrigger value="overview">Vue d'ensemble</TabsTrigger>
          </TabsList>

          {/* OVERVIEW TAB */}
          <TabsContent value="overview">
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 sm:gap-4 mb-6">
              <Card
                className="cursor-pointer hover:shadow-md transition-shadow"
                onClick={() => handleTabChange('users')}
                data-testid="overview-card-clients"
              >
                <CardContent className="p-3 sm:p-4">
                  <div className="flex flex-col gap-2">
                    <div className="flex items-center justify-between">
                      <div className="p-2 rounded-lg bg-blue-100">
                        <Users className="w-4 h-4 sm:w-5 sm:h-5 text-blue-600" />
                      </div>
                      <p className="text-xl sm:text-2xl font-bold">{clientUsers.length}</p>
                    </div>
                    <div>
                      <p className="text-xs sm:text-sm font-medium text-gray-700">Clients</p>
                      <p className="text-[10px] sm:text-xs text-emerald-600">+{clientUsersThisMonth} ce mois</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-3 sm:p-4">
                  <div className="flex flex-col gap-2">
                    <div className="flex items-center justify-between">
                      <div className="p-2 rounded-lg bg-amber-100">
                        <FolderOpen className="w-4 h-4 sm:w-5 sm:h-5 text-amber-600" />
                      </div>
                      <p className="text-xl sm:text-2xl font-bold">{stats?.projects?.total || 0}</p>
                    </div>
                    <div>
                      <p className="text-xs sm:text-sm font-medium text-gray-700">Dossiers</p>
                      <p className="text-[10px] sm:text-xs text-amber-600">{stats?.projects?.pending || 0} en attente</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-3 sm:p-4">
                  <div className="flex flex-col gap-2">
                    <div className="flex items-center justify-between">
                      <div className="p-2 rounded-lg bg-emerald-100">
                        <Euro className="w-4 h-4 sm:w-5 sm:h-5 text-emerald-600" />
                      </div>
                      <p className="text-lg sm:text-2xl font-bold truncate">{formatPrice(accountingSummary?.profit || 0)}</p>
                    </div>
                    <div>
                      <p className="text-xs sm:text-sm font-medium text-gray-700">Bénéfice net</p>
                      <p className="text-[10px] sm:text-xs text-muted-foreground truncate" data-testid="overview-tva">TVA: {formatPrice(accountingSummary?.tva_balance || 0)}</p>
                      <p className="text-[10px] sm:text-xs text-red-500 truncate" data-testid="overview-stripe-fees">Stripe: -{formatPrice(accountingSummary?.stripe_fees || 0)}</p>
                      {(accountingSummary?.spf_costs > 0) && (
                        <p className="text-[10px] sm:text-xs text-red-500 truncate" data-testid="overview-spf-costs">SPF: -{formatPrice(accountingSummary?.spf_costs || 0)}</p>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* ÉQUIPE */}
              <Card
                className="cursor-pointer hover:shadow-md transition-shadow"
                onClick={() => handleTabChange('analysts')}
                data-testid="overview-card-equipe"
              >
                <CardContent className="p-3 sm:p-4">
                  <div className="flex flex-col gap-2">
                    <div className="flex items-center justify-between">
                      <div className="p-2 rounded-lg bg-emerald-100">
                        <UserCheck className="w-4 h-4 sm:w-5 sm:h-5 text-emerald-600" />
                      </div>
                      <p className="text-xl sm:text-2xl font-bold">{(stats?.analysts || 0) + (stats?.chiffreurs || 0)}</p>
                    </div>
                    <div>
                      <p className="text-xs sm:text-sm font-medium text-gray-700">Équipe</p>
                      <p className="text-[10px] sm:text-xs text-muted-foreground">
                        {stats?.analysts || 0} anal. · {stats?.chiffreurs || 0} chiff.
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* RÉSEAU */}
              <Card
                className="cursor-pointer hover:shadow-md transition-shadow col-span-2 sm:col-span-1"
                onClick={() => handleTabChange('partners')}
                data-testid="overview-card-reseau"
              >
                <CardContent className="p-3 sm:p-4">
                  <div className="flex flex-col gap-2">
                    <div className="flex items-center justify-between">
                      <div className="p-2 rounded-lg bg-orange-100">
                        <Network className="w-4 h-4 sm:w-5 sm:h-5 text-orange-600" />
                      </div>
                      <p className="text-xl sm:text-2xl font-bold">
                        {(stats?.partners || 0) + (stats?.affiliates || 0) + (stats?.artisans || 0)}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs sm:text-sm font-medium text-gray-700">Réseau</p>
                      <p className="text-[10px] sm:text-xs text-muted-foreground">
                        {stats?.partners || 0}P · {stats?.affiliates || 0}Af · {stats?.artisans || 0}Ar
                      </p>
                      {(stats?.network_pending || 0) > 0 && (
                        <button
                          onClick={(e) => { e.stopPropagation(); handleTabChange('network'); }}
                          className="mt-1 inline-flex items-center gap-1 text-[10px] sm:text-xs px-2 py-0.5 rounded-full bg-red-100 text-red-700 hover:bg-red-200 font-medium"
                          data-testid="overview-pending-validation-badge"
                        >
                          <Clock className="w-3 h-3" />
                          {stats.network_pending} à valider
                        </button>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* PROSPECTS */}
              <Card
                className="cursor-pointer hover:shadow-md transition-shadow col-span-2 sm:col-span-1"
                onClick={() => handleTabChange('prospects')}
                data-testid="overview-card-prospects"
              >
                <CardContent className="p-3 sm:p-4">
                  <div className="flex flex-col gap-2">
                    <div className="flex items-center justify-between">
                      <div className="p-2 rounded-lg bg-emerald-100">
                        <TrendingUp className="w-4 h-4 sm:w-5 sm:h-5 text-emerald-600" />
                      </div>
                      <p className="text-xl sm:text-2xl font-bold">
                        {stats?.prospects?.total || 0}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs sm:text-sm font-medium text-gray-700">Prospects</p>
                      <p className="text-[10px] sm:text-xs text-muted-foreground">
                        +{stats?.prospects?.this_month || 0} ce mois
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* RADAR FONCIER */}
              <Card
                className="cursor-pointer hover:shadow-md transition-shadow col-span-2 sm:col-span-1"
                onClick={() => { handleTabChange('prospects'); setProspectSourceFilter('radar'); }}
                data-testid="overview-card-radar"
              >
                <CardContent className="p-3 sm:p-4">
                  <div className="flex flex-col gap-2">
                    <div className="flex items-center justify-between">
                      <div className="p-2 rounded-lg bg-purple-100">
                        <MapPin className="w-4 h-4 sm:w-5 sm:h-5 text-purple-600" />
                      </div>
                      <p className="text-xl sm:text-2xl font-bold">
                        {(prospects.filter(p => p.source === 'radar').length) + (prospectionStats?.total_demandes || 0)}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs sm:text-sm font-medium text-gray-700">Radar Foncier</p>
                      <p className="text-[10px] sm:text-xs text-muted-foreground">
                        {prospects.filter(p => p.source === 'radar').length} pl. · {prospectionStats?.total_demandes || 0} prop.
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Projects to validate */}
            {projects.filter(p => p.status === 'pending_validation').length > 0 && (
              <Card className="mb-6 border-purple-200">
                <CardHeader>
                  <CardTitle className="text-lg text-purple-700">Dossiers à valider ({projects.filter(p => p.status === 'pending_validation').length})</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {projects.filter(p => p.status === 'pending_validation').slice(0, 5).map(project => {
                      const analyst = analysts.find(a => a.id === project.assigned_to);
                      return (
                        <div key={project.id} className="flex items-center justify-between p-3 bg-purple-50 rounded-lg">
                          <div>
                            <p className="font-medium">{project.address}</p>
                            <p className="text-xs text-muted-foreground">Analysé par: {analyst?.name || 'Inconnu'}</p>
                          </div>
                          <div className="flex gap-2">
                            <Button size="sm" className="bg-emerald-500 hover:bg-emerald-600" onClick={() => validateProject(project.id, true)}>
                              <Check className="w-4 h-4 mr-1" />Valider
                            </Button>
                            <Button size="sm" variant="outline" onClick={() => validateProject(project.id, false)}>
                              <X className="w-4 h-4 mr-1" />Refuser
                            </Button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Recent activity */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Derniers utilisateurs</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {filteredUsers.slice(0, 5).map(user => (
                      <div key={user.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <div>
                          <p className="font-medium">{user.name || user.email}</p>
                          <div className="flex gap-3 mt-1">
                            {user.phone && (
                              <a href={`tel:${user.phone}`} className="text-xs text-blue-600 flex items-center gap-1 hover:underline">
                                <Phone className="w-3 h-3" />{user.phone}
                              </a>
                            )}
                            <a href={`mailto:${user.email}`} className="text-xs text-blue-600 flex items-center gap-1 hover:underline">
                              <Mail className="w-3 h-3" />{user.email}
                            </a>
                          </div>
                        </div>
                        <Badge className={user.subscription_type === 'master' ? 'bg-gold text-black' : 'bg-gray-200'}>
                          {user.subscription_type || 'free'}
                        </Badge>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Dossiers du jour</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {(projectsByDate?.today || []).slice(0, 5).map(project => {
                      const status = STATUS_CONFIG[project.status] || STATUS_CONFIG.pending;
                      return (
                        <div key={project.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                          <div className="flex-1 min-w-0">
                            <p className="font-medium truncate">{project.address}</p>
                            <p className="text-xs text-muted-foreground">{formatPrice(project.purchase_price)}</p>
                          </div>
                          <Badge className={status.color}>{status.label}</Badge>
                        </div>
                      );
                    })}
                    {(!projectsByDate?.today || projectsByDate.today.length === 0) && (
                      <p className="text-center text-muted-foreground py-4">Aucun dossier aujourd'hui</p>
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* PROJECTS TAB - Organized by date */}
          <TabsContent value="projects">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle>Dossiers par date</CardTitle>
                  <div className="flex items-center gap-4">
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={() => {
                        if (selectedProjects.length === projects.length) {
                          setSelectedProjects([]);
                        } else {
                          setSelectedProjects(projects.map(p => p.id));
                        }
                      }}
                    >
                      {selectedProjects.length === projects.length ? 'Tout désélectionner' : 'Tout sélectionner'}
                    </Button>
                    {selectedProjects.length > 0 && (
                      <Button variant="destructive" size="sm" onClick={bulkDeleteProjects}>
                        <Trash2 className="w-4 h-4 mr-2" />Supprimer ({selectedProjects.length})
                      </Button>
                    )}
                    <div className="flex items-center gap-2">
                      <Search className="w-4 h-4 text-muted-foreground" />
                      <Input placeholder="Rechercher..." value={projectSearch} onChange={(e) => setProjectSearch(e.target.value)} className="w-64" />
                    </div>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {/* Today */}
                <div className="mb-4">
                  <button className="flex items-center gap-2 text-lg font-semibold mb-2 hover:text-navy" onClick={() => toggleDateSection('today')}>
                    {expandedDates.today ? <ChevronDown className="w-5 h-5" /> : <ChevronRight className="w-5 h-5" />}
                    <Calendar className="w-5 h-5 text-gold" />
                    AUJOURD'HUI ({projectsByDate?.today?.length || 0})
                  </button>
                  {expandedDates.today && projectsByDate?.today?.length > 0 && (
                    <div className="overflow-x-auto ml-7">
                      <table className="w-full">
                        <thead>
                          <tr className="border-b text-sm">
                            <th className="p-2 w-10"></th>
                            <th className="text-left p-2">Adresse</th>
                            <th className="text-left p-2">Reçu à</th>
                            <th className="text-left p-2">Client</th>
                            <th className="text-left p-2">Prix</th>
                            <th className="text-left p-2">Travaux</th>
                            <th className="text-left p-2">Statut</th>
                            <th className="text-left p-2">Analyste</th>
                            <th className="text-right p-2">Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {projectsByDate.today.filter(p => p.address?.toLowerCase().includes(projectSearch.toLowerCase())).map(project => renderProjectRow(project))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>

                {/* Yesterday */}
                <div className="mb-4">
                  <button className="flex items-center gap-2 text-lg font-semibold mb-2 hover:text-navy" onClick={() => toggleDateSection('yesterday')}>
                    {expandedDates.yesterday ? <ChevronDown className="w-5 h-5" /> : <ChevronRight className="w-5 h-5" />}
                    <Calendar className="w-5 h-5 text-blue-500" />
                    HIER ({projectsByDate?.yesterday?.length || 0})
                  </button>
                  {expandedDates.yesterday && projectsByDate?.yesterday?.length > 0 && (
                    <div className="overflow-x-auto ml-7">
                      <table className="w-full">
                        <thead>
                          <tr className="border-b text-sm">
                            <th className="p-2 w-10"></th>
                            <th className="text-left p-2">Adresse</th>
                            <th className="text-left p-2">Reçu à</th>
                            <th className="text-left p-2">Client</th>
                            <th className="text-left p-2">Prix</th>
                            <th className="text-left p-2">Travaux</th>
                            <th className="text-left p-2">Statut</th>
                            <th className="text-left p-2">Analyste</th>
                            <th className="text-right p-2">Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {projectsByDate.yesterday.filter(p => p.address?.toLowerCase().includes(projectSearch.toLowerCase())).map(project => renderProjectRow(project))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>

                {/* This month by day */}
                {projectsByDate?.this_month && Object.keys(projectsByDate.this_month).length > 0 && (
                  <div className="mb-4">
                    <h3 className="text-lg font-semibold mb-2 flex items-center gap-2">
                      <Calendar className="w-5 h-5 text-emerald-500" />
                      CE MOIS
                    </h3>
                    {Object.entries(projectsByDate.this_month).sort((a, b) => b[0].localeCompare(a[0])).map(([date, dayProjects]) => (
                      <div key={date} className="ml-4 mb-3">
                        <button className="flex items-center gap-2 font-medium mb-1 hover:text-navy" onClick={() => toggleDateSection(date)}>
                          {expandedDates[date] ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                          {formatDateFull(date)} ({dayProjects.length})
                        </button>
                        {expandedDates[date] && (
                          <div className="overflow-x-auto ml-6">
                            <table className="w-full">
                              <tbody>
                                {dayProjects.filter(p => p.address?.toLowerCase().includes(projectSearch.toLowerCase())).map(project => renderProjectRow(project))}
                              </tbody>
                            </table>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}

                {/* By month */}
                {projectsByDate?.by_month && Object.keys(projectsByDate.by_month).length > 0 && (
                  <div className="mb-4">
                    {Object.entries(projectsByDate.by_month).sort((a, b) => b[0].localeCompare(a[0])).map(([month, monthData]) => (
                      <div key={month} className="mb-3">
                        <button className="flex items-center gap-2 text-lg font-semibold mb-2 hover:text-navy" onClick={() => toggleDateSection(month)}>
                          {expandedDates[month] ? <ChevronDown className="w-5 h-5" /> : <ChevronRight className="w-5 h-5" />}
                          <Calendar className="w-5 h-5 text-purple-500" />
                          {new Date(month + '-01').toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' }).toUpperCase()} ({Object.values(monthData).flat().length})
                        </button>
                        {expandedDates[month] && Object.entries(monthData).sort((a, b) => b[0].localeCompare(a[0])).map(([date, dayProjects]) => (
                          <div key={date} className="ml-4 mb-2">
                            <p className="font-medium text-sm text-muted-foreground mb-1">{formatDateFull(date)} ({dayProjects.length})</p>
                            <div className="overflow-x-auto ml-4">
                              <table className="w-full">
                                <tbody>
                                  {dayProjects.filter(p => p.address?.toLowerCase().includes(projectSearch.toLowerCase())).map(project => renderProjectRow(project))}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        ))}
                      </div>
                    ))}
                  </div>
                )}

                {/* By year */}
                {projectsByDate?.by_year && Object.keys(projectsByDate.by_year).length > 0 && (
                  <div>
                    {Object.entries(projectsByDate.by_year).sort((a, b) => b[0].localeCompare(a[0])).map(([year, yearData]) => (
                      <div key={year} className="mb-3">
                        <button className="flex items-center gap-2 text-lg font-semibold mb-2 hover:text-navy" onClick={() => toggleDateSection(year)}>
                          {expandedDates[year] ? <ChevronDown className="w-5 h-5" /> : <ChevronRight className="w-5 h-5" />}
                          <Calendar className="w-5 h-5 text-gray-500" />
                          {year} ({Object.values(yearData).flat().length})
                        </button>
                        {expandedDates[year] && Object.entries(yearData).sort((a, b) => b[0].localeCompare(a[0])).map(([month, monthProjects]) => (
                          <div key={month} className="ml-4 mb-2">
                            <p className="font-medium mb-1">{new Date(month + '-01').toLocaleDateString('fr-FR', { month: 'long' })} ({monthProjects.length})</p>
                            <div className="overflow-x-auto ml-4">
                              <table className="w-full">
                                <tbody>
                                  {monthProjects.filter(p => p.address?.toLowerCase().includes(projectSearch.toLowerCase())).map(project => renderProjectRow(project))}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        ))}
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* USERS TAB */}
          <TabsContent value="users">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle>Clients</CardTitle>
                  <div className="flex items-center gap-4">
                    {selectedUsers.length > 0 && (
                      <Button variant="destructive" size="sm" onClick={bulkDeleteUsers}>
                        <Trash2 className="w-4 h-4 mr-2" />Supprimer ({selectedUsers.length})
                      </Button>
                    )}
                    <div className="flex items-center gap-2">
                      <Search className="w-4 h-4 text-muted-foreground" />
                      <Input placeholder="Rechercher..." value={userSearch} onChange={(e) => setUserSearch(e.target.value)} className="w-64" />
                    </div>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b">
                        <th className="p-3 w-10">
                          <Checkbox 
                            checked={selectedUsers.length === filteredUsers.length && filteredUsers.length > 0}
                            onCheckedChange={(checked) => {
                              if (checked) {
                                setSelectedUsers(filteredUsers.map(u => u.id));
                              } else {
                                setSelectedUsers([]);
                              }
                            }}
                          />
                        </th>
                        <th className="text-left p-3 font-medium">Client</th>
                        <th className="text-left p-3 font-medium">Contact</th>
                        <th className="text-left p-3 font-medium">Abonnement</th>
                        <th className="text-left p-3 font-medium">Dossiers</th>
                        <th className="text-left p-3 font-medium">Inscrit le</th>
                        <th className="text-right p-3 font-medium">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {usersPagination.paginate(filteredUsers).map(user => (
                        <tr key={user.id} className="border-b hover:bg-gray-50">
                          <td className="p-3">
                            <Checkbox 
                              checked={selectedUsers.includes(user.id)}
                              onCheckedChange={(checked) => {
                                if (checked) {
                                  setSelectedUsers([...selectedUsers, user.id]);
                                } else {
                                  setSelectedUsers(selectedUsers.filter(id => id !== user.id));
                                }
                              }}
                            />
                          </td>
                          <td className="p-3">
                            <p className="font-medium">{user.name || '-'}</p>
                            <a href={`mailto:${user.email}`} className="text-sm text-blue-600 hover:underline flex items-center gap-1">
                              <Mail className="w-3 h-3" />{user.email}
                            </a>
                          </td>
                          <td className="p-3">
                            {user.phone ? (
                              <a href={`tel:${user.phone}`} className="text-blue-600 hover:underline flex items-center gap-1">
                                <Phone className="w-4 h-4" />{user.phone}
                              </a>
                            ) : (
                              <span className="text-muted-foreground">-</span>
                            )}
                          </td>
                          <td className="p-3">
                            <Badge className={
                              user.subscription_type === 'master' ? 'bg-gold text-black' :
                              user.subscription_type === 'pro' ? 'bg-amber-500 text-white' :
                              'bg-gray-200'
                            }>
                              {user.subscription_type || 'free'}
                            </Badge>
                          </td>
                          <td className="p-3">
                            <Button variant="link" size="sm" onClick={() => setViewingUser(user)}>
                              {user.project_count || 0} dossier(s)
                            </Button>
                          </td>
                          <td className="p-3 text-sm">{formatDate(user.created_at)}</td>
                          <td className="p-3 text-right">
                            <div className="flex items-center justify-end gap-1">
                              <Button variant="ghost" size="sm" onClick={() => createAffiliate(user.id)} title="Créer affilié">
                                <Link2 className="w-4 h-4" />
                              </Button>
                              <Button variant="ghost" size="sm" onClick={() => setEditingUser(user)}>
                                <Edit2 className="w-4 h-4" />
                              </Button>
                              <Button variant="ghost" size="sm" onClick={() => deleteUser(user.id)} className="text-red-500">
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <Pagination currentPage={usersPagination.page} totalItems={filteredUsers.length} itemsPerPage={usersPagination.perPage} onPageChange={usersPagination.setPage} onItemsPerPageChange={usersPagination.setPerPage} />
              </CardContent>
            </Card>
          </TabsContent>
          <TabsContent value="analysts">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle>Analystes</CardTitle>
                  <div className="flex items-center gap-2">
                    {selectedAnalysts.length > 0 && (
                      <Button variant="destructive" size="sm" onClick={bulkDeleteAnalysts}>
                        <Trash2 className="w-4 h-4 mr-2" />Supprimer ({selectedAnalysts.length})
                      </Button>
                    )}
                    {analysts.length > 0 && (
                      <Button 
                        variant="outline" 
                        size="sm" 
                        onClick={() => setSelectedAnalysts(selectedAnalysts.length === analysts.length ? [] : analysts.map(a => a.id))}
                      >
                        {selectedAnalysts.length === analysts.length ? 'Tout désélectionner' : 'Tout sélectionner'}
                      </Button>
                    )}
                    <Button onClick={() => setShowAnalystModal(true)} className="bg-gold hover:bg-gold/90 text-black">
                      <Plus className="w-4 h-4 mr-2" />Nouvel Analyste
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {analysts.length === 0 ? (
                  <div className="text-center py-12 text-muted-foreground">
                    <UserCheck className="w-12 h-12 mx-auto mb-4 opacity-20" />
                    <p>Aucun analyste</p>
                    <p className="text-sm">Créez des analystes pour vous aider à traiter les dossiers</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {analysts.map(analyst => (
                      <Card key={analyst.id} className={`border-2 transition-colors ${selectedAnalysts.includes(analyst.id) ? 'border-red-500 bg-red-50' : 'hover:border-gold'}`}>
                        <CardContent className="p-4">
                          <div className="flex items-start justify-between mb-4">
                            <div className="flex items-start gap-3">
                              <Checkbox
                                checked={selectedAnalysts.includes(analyst.id)}
                                onCheckedChange={(checked) => {
                                  if (checked) {
                                    setSelectedAnalysts([...selectedAnalysts, analyst.id]);
                                  } else {
                                    setSelectedAnalysts(selectedAnalysts.filter(id => id !== analyst.id));
                                  }
                                }}
                              />
                              <div>
                                <h3 className="font-bold text-lg">{analyst.name}</h3>
                                <a href={`mailto:${analyst.email}`} className="text-sm text-blue-600 hover:underline flex items-center gap-1">
                                  <Mail className="w-3 h-3" />{analyst.email}
                                </a>
                                {analyst.phone && (
                                <a href={`tel:${analyst.phone}`} className="text-sm text-blue-600 hover:underline flex items-center gap-1 mt-1">
                                  <Phone className="w-3 h-3" />{analyst.phone}
                                </a>
                              )}
                              </div>
                            </div>
                            <Badge className={analyst.is_active ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100'}>
                              {analyst.is_active ? 'Actif' : 'Inactif'}
                            </Badge>
                          </div>
                          <div className="space-y-2 text-sm">
                            <p><FolderOpen className="w-4 h-4 inline mr-2" />{analyst.assigned_count || 0} dossiers en cours</p>
                            <p><CheckCircle className="w-4 h-4 inline mr-2" />{analyst.completed_projects || 0} dossiers terminés</p>
                          </div>
                          <div className="flex justify-end gap-2 mt-4">
                            <Button variant="outline" size="sm" onClick={() => setPasswordModal({ show: true, analyst, newPassword: '' })} title="Modifier le mot de passe">
                              <Edit2 className="w-4 h-4" />
                            </Button>
                            <Button variant="outline" size="sm" onClick={() => viewAnalystDetails(analyst)} title="Voir les dossiers">
                              <Eye className="w-4 h-4" />
                            </Button>
                            <Button variant="outline" size="sm" onClick={() => deleteAnalyst(analyst.id)} className="text-red-500 hover:text-red-700" title="Supprimer">
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* CHIFFREURS TAB */}
          <TabsContent value="chiffreurs">
            <Card>
              <CardHeader>
                <div className="flex justify-between items-center">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      <Calculator className="w-5 h-5 text-emerald-600" />
                      Chiffreurs
                    </CardTitle>
                    <p className="text-sm text-muted-foreground mt-1">
                      Équipe de chiffrage des travaux • Données clients masquées
                    </p>
                  </div>
                  <Button onClick={() => setShowChiffreurModal(true)} className="bg-emerald-600 hover:bg-emerald-700">
                    <UserPlus className="w-4 h-4 mr-2" />
                    Nouveau chiffreur
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {chiffreurs.length === 0 ? (
                  <div className="text-center py-12 text-muted-foreground">
                    <Calculator className="w-12 h-12 mx-auto mb-4 opacity-20" />
                    <p>Aucun chiffreur créé</p>
                    <p className="text-sm">Les chiffreurs font l'estimation des travaux sans voir les données clients</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {chiffreurs.map(chiffreur => (
                      <div key={chiffreur.id} className="flex items-center justify-between p-4 bg-emerald-50 rounded-lg border border-emerald-100">
                        <div className="flex items-center gap-4">
                          <div className="w-10 h-10 rounded-full bg-emerald-500 flex items-center justify-center text-white font-bold">
                            {chiffreur.name?.charAt(0)?.toUpperCase() || 'C'}
                          </div>
                          <div>
                            <p className="font-semibold">{chiffreur.name}</p>
                            <p className="text-sm text-muted-foreground">{chiffreur.email}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-4">
                          <Badge variant="outline" className="border-emerald-300 text-emerald-700">
                            {chiffreur.assigned_count || 0} dossiers
                          </Badge>
                          <Badge variant="outline" className="border-blue-300 text-blue-700">
                            {chiffreur.completed_projects || 0} terminés
                          </Badge>
                          <div className="flex gap-1">
                            <Button size="sm" variant="outline" onClick={() => viewChiffreurDetails(chiffreur)}>
                              <Eye className="w-4 h-4" />
                            </Button>
                            <Button 
                              size="sm" 
                              variant="outline"
                              onClick={() => setChiffreurPasswordModal({ show: true, chiffreur, newPassword: '' })}
                            >
                              <Lock className="w-4 h-4" />
                            </Button>
                            <Button size="sm" variant="destructive" onClick={() => deleteChiffreur(chiffreur.id)}>
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Info Box */}
                <div className="mt-6 p-4 bg-amber-50 border border-amber-200 rounded-lg">
                  <p className="text-amber-800 text-sm">
                    <strong>🔒 Confidentialité :</strong> Les chiffreurs accèdent uniquement aux vidéos, photos et informations 
                    techniques du bien. L'adresse exacte, le nom et les coordonnées du client sont masqués.
                  </p>
                </div>
              </CardContent>
            </Card>
          </TabsContent>          {/* ACCOUNTING TAB */}
          <TabsContent value="accounting">
            <AccountingDashboard />
          </TabsContent>

          {/* PARTNERS TAB */}
          <TabsContent value="partners">
            {/* Section En attente (Démo) */}
            {demoPartners.length > 0 && (
              <Card className="mb-4 border-amber-200">
                <CardHeader className="pb-3">
                  <CardTitle className="flex items-center gap-2 text-base text-amber-700">
                    <span className="w-2 h-2 rounded-full bg-amber-400 inline-block"></span>
                    En attente de souscription
                    <span className="ml-1 text-sm font-normal text-amber-500">({demoPartners.length})</span>
                  </CardTitle>
                  <p className="text-xs text-muted-foreground">Ces partenaires ont un accès démo. Dès qu'ils souscrivent un abonnement, ils apparaîtront dans la liste ci-dessous.</p>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {demoPartners.map(partner => (
                      <Card key={partner.id} className="border border-amber-200 bg-amber-50/40">
                        <CardContent className="p-4">
                          <div className="flex items-start justify-between mb-3">
                            <div>
                              <h3 className="font-bold text-base">{partner.company_name || partner.full_name}</h3>
                              <p className="text-xs text-muted-foreground">/p/{partner.subdomain}</p>
                            </div>
                            <span className="text-xs px-2 py-0.5 rounded-full font-medium bg-amber-100 text-amber-700 border border-amber-300">
                              Démo
                            </span>
                          </div>
                          <div className="space-y-1 text-sm text-muted-foreground mb-3">
                            <p className="flex items-center gap-2"><Mail className="w-3.5 h-3.5" />{partner.email}</p>
                            <p className="flex items-center gap-2 text-xs">
                              <Clock className="w-3.5 h-3.5" />
                              {partner.is_expired
                                ? <span className="text-red-500 font-medium">Expiré</span>
                                : <span>{partner.days_remaining} jour{partner.days_remaining > 1 ? 's' : ''} restant{partner.days_remaining > 1 ? 's' : ''}</span>
                              }
                            </p>
                          </div>
                          <div className="flex justify-end gap-2">
                            {partner.subdomain && (
                              <Button variant="outline" size="sm" onClick={() => navigate(`/expert?partner=${partner.subdomain}`)} className="text-sky-600 border-sky-200 hover:bg-sky-50">
                                <FolderOpen className="w-4 h-4 mr-1" />Dossiers
                              </Button>
                            )}
                            <Button variant="outline" size="sm" onClick={() => handleTabChange('gratuites')} className="text-amber-600 border-amber-200 hover:bg-amber-50">
                              <Settings className="w-4 h-4" />
                            </Button>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Section Partenaires actifs */}
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="flex items-center gap-2">
                    Partenaires White-Label
                    <span className="ml-2 text-sm font-normal text-muted-foreground">({partnerSubscriptions.filter(p => p.subscription_status !== 'demo').length})</span>
                  </CardTitle>

                </div>
              </CardHeader>
              <CardContent>
                {partnerSubscriptions.filter(p => p.subscription_status !== 'demo').length === 0 ? (
                  <div className="text-center py-12 text-muted-foreground">
                    <Building2 className="w-12 h-12 mx-auto mb-4 opacity-20" />
                    <p>Aucun partenaire actif</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {partnerSubscriptions.filter(p => p.subscription_status !== 'demo').map(partner => (
                      <Card key={partner.id} className="border hover:border-gold transition-colors">
                        <CardContent className="p-4">
                          <div className="flex items-start justify-between mb-3">
                            <div>
                              <h3 className="font-bold text-base">{partner.company_name || partner.full_name}</h3>
                              <p className="text-xs text-muted-foreground">/p/{partner.subdomain}</p>
                            </div>
                            <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${partner.subscription_status === 'active' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}>
                              {partner.subscription_status === 'active' ? 'Actif' : 'Inactif'}
                            </span>
                          </div>
                          <div className="space-y-1 text-sm text-muted-foreground mb-4">
                            <p className="flex items-center gap-2"><Mail className="w-3.5 h-3.5" />{partner.email}</p>
                            {partner.subscription_expiry && (
                              <p className="flex items-center gap-2 text-xs">
                                <Euro className="w-3.5 h-3.5" />Abo. jusqu'au {new Date(partner.subscription_expiry).toLocaleDateString('fr-FR')}
                              </p>
                            )}
                          </div>
                          <div className="flex justify-end gap-2">
                            {partner.subdomain && (
                              <Button variant="outline" size="sm" onClick={() => navigate(`/expert?partner=${partner.subdomain}`)} className="text-sky-600 border-sky-200 hover:bg-sky-50">
                                <FolderOpen className="w-4 h-4 mr-1" />Dossiers
                              </Button>
                            )}
                            <Button variant="outline" size="sm" onClick={() => handleTabChange('subscriptions')} className="text-muted-foreground">
                              <Settings className="w-4 h-4" />
                            </Button>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* AFFILIATES TAB */}
          <TabsContent value="affiliates">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle>Programme Affiliation</CardTitle>
                  {selectedAffiliates.length > 0 && (
                    <Button variant="destructive" size="sm" onClick={bulkDeleteAffiliates}>
                      <Trash2 className="w-4 h-4 mr-2" />Supprimer ({selectedAffiliates.length})
                    </Button>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                {affiliates.length === 0 ? (
                  <div className="text-center py-12 text-muted-foreground">
                    <Link2 className="w-12 h-12 mx-auto mb-4 opacity-20" />
                    <p>Aucun affilié</p>
                    <p className="text-sm">Créez des affiliés depuis l'onglet Utilisateurs</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b">
                          <th className="p-3 w-10">
                            <Checkbox 
                              checked={selectedAffiliates.length === affiliates.length && affiliates.length > 0}
                              onCheckedChange={(checked) => {
                                if (checked) {
                                  setSelectedAffiliates(affiliates.map(a => a.id));
                                } else {
                                  setSelectedAffiliates([]);
                                }
                              }}
                            />
                          </th>
                          <th className="text-left p-3 font-medium">Affilié</th>
                          <th className="text-left p-3 font-medium">Code</th>
                          <th className="text-left p-3 font-medium">Commission</th>
                          <th className="text-left p-3 font-medium">Parrainages</th>
                          <th className="text-left p-3 font-medium">Gains</th>
                          <th className="text-left p-3 font-medium">En attente</th>
                          <th className="text-right p-3 font-medium">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {affiliates.map(affiliate => (
                          <tr key={affiliate.id} className="border-b hover:bg-gray-50">
                            <td className="p-3">
                              <Checkbox 
                                checked={selectedAffiliates.includes(affiliate.id)}
                                onCheckedChange={(checked) => {
                                  if (checked) {
                                    setSelectedAffiliates([...selectedAffiliates, affiliate.id]);
                                  } else {
                                    setSelectedAffiliates(selectedAffiliates.filter(id => id !== affiliate.id));
                                  }
                                }}
                              />
                            </td>
                            <td className="p-3">
                              <p className="font-medium">{affiliate.user?.name || affiliate.user?.email || '-'}</p>
                              {affiliate.user?.email && (
                                <a href={`mailto:${affiliate.user.email}`} className="text-xs text-blue-600 hover:underline flex items-center gap-1">
                                  <Mail className="w-3 h-3" />{affiliate.user.email}
                                </a>
                              )}
                            </td>
                            <td className="p-3">
                              <Badge variant="outline" className="font-mono">{affiliate.code}</Badge>
                            </td>
                            <td className="p-3">
                              {editingAffiliateId === affiliate.id ? (
                                <div className="flex items-center gap-1">
                                  <input
                                    type="number"
                                    min="0" max="100" step="1"
                                    value={editingCommissionValue}
                                    onChange={e => setEditingCommissionValue(e.target.value)}
                                    onKeyDown={e => { if (e.key === 'Enter') updateAffiliateCommission(affiliate.id, editingCommissionValue); if (e.key === 'Escape') setEditingAffiliateId(null); }}
                                    className="w-16 border rounded px-1 py-0.5 text-sm text-center focus:outline-none focus:ring-1 focus:ring-blue-400"
                                    autoFocus
                                  />
                                  <span className="text-sm">%</span>
                                  <button onClick={() => updateAffiliateCommission(affiliate.id, editingCommissionValue)} className="text-xs bg-emerald-500 text-white px-2 py-0.5 rounded hover:bg-emerald-600">OK</button>
                                  <button onClick={() => setEditingAffiliateId(null)} className="text-xs bg-gray-200 text-gray-600 px-2 py-0.5 rounded hover:bg-gray-300">✕</button>
                                </div>
                              ) : (
                                <div className="flex items-center gap-1">
                                  <span>{(affiliate.commission_rate * 100).toFixed(0)}%</span>
                                  <button onClick={() => { setEditingAffiliateId(affiliate.id); setEditingCommissionValue(((affiliate.commission_rate || 0.15) * 100).toFixed(0)); }} className="text-gray-400 hover:text-blue-500 ml-1" title="Modifier">
                                    <Edit2 className="w-3 h-3" />
                                  </button>
                                </div>
                              )}
                            </td>
                            <td className="p-3">{affiliate.total_referrals || 0}</td>
                            <td className="p-3 text-emerald-600 font-medium">{formatPrice(affiliate.total_earnings)}</td>
                            <td className="p-3 text-amber-600">{formatPrice(affiliate.pending_earnings)}</td>
                            <td className="p-3 text-right">
                              <div className="flex items-center justify-end gap-1">
                                <Button variant="ghost" size="sm" onClick={() => viewAffiliateDetails(affiliate)} title="Voir les parrainages">
                                  <Eye className="w-4 h-4" />
                                </Button>
                                <Button variant="ghost" size="sm" onClick={() => deleteAffiliate(affiliate.id)} className="text-red-500" title="Supprimer">
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* RÉSEAU - LEADS UTILISATEURS (étape 1 du tunnel) */}
          <TabsContent value="reseau-users">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="flex items-center gap-2">
                    <Users className="w-5 h-5 text-orange-600" />
                    Utilisateurs du Réseau (Leads)
                    <InfoTooltip text="Tous les leads capturés à l'étape 1 du tunnel d'inscription Réseau (email + nom + prénom + choix de rôle). Un lead 'non converti' est quelqu'un qui a rempli le mini-formulaire mais n'a pas terminé l'inscription complète. Tu peux les relancer." />
                  </CardTitle>
                  <div className="flex items-center gap-2">
                    {selectedLeads.length > 0 && (
                      <Button
                        variant="destructive"
                        size="sm"
                        onClick={async () => {
                          if (!window.confirm(`Supprimer ${selectedLeads.length} lead(s) ?`)) return;
                          try {
                            await axios.post(`${API}/admin/reseau/leads/bulk-delete`, { ids: selectedLeads });
                            toast.success('Leads supprimés');
                            setSelectedLeads([]);
                            fetchData();
                          } catch (e) { toast.error('Erreur'); }
                        }}
                        data-testid="leads-bulk-delete"
                      >
                        <Trash2 className="w-4 h-4 mr-2" />Supprimer ({selectedLeads.length})
                      </Button>
                    )}
                    <Badge variant="outline" className="text-sm">
                      {reseauLeads.length} total • {reseauLeads.filter(l => !l.converted_member_id).length} non convertis
                    </Badge>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {reseauLeads.length === 0 ? (
                  <div className="text-center py-12 text-muted-foreground">
                    <Users className="w-12 h-12 mx-auto mb-4 opacity-20" />
                    <p>Aucun lead capturé pour le moment</p>
                    <p className="text-sm">Les visiteurs qui remplissent le mini-formulaire sur <code>/reseau</code> apparaissent ici.</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full" data-testid="leads-table">
                      <thead>
                        <tr className="border-b">
                          <th className="p-3 w-10">
                            <Checkbox
                              checked={selectedLeads.length === reseauLeads.length && reseauLeads.length > 0}
                              onCheckedChange={(checked) => {
                                if (checked) setSelectedLeads(reseauLeads.map(l => l.id));
                                else setSelectedLeads([]);
                              }}
                            />
                          </th>
                          <th className="text-left p-3 font-medium">Email</th>
                          <th className="text-left p-3 font-medium">Nom</th>
                          <th className="text-left p-3 font-medium">Prénom</th>
                          <th className="text-left p-3 font-medium">Rôle choisi</th>
                          <th className="text-left p-3 font-medium">Date</th>
                          <th className="text-left p-3 font-medium">Statut</th>
                          <th className="text-right p-3 font-medium">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {reseauLeads.map(lead => {
                          const isConverted = !!lead.converted_member_id;
                          const memberStatus = lead.member_status;
                          let statusBadge;
                          if (memberStatus === 'validated') {
                            statusBadge = <Badge className="bg-emerald-100 text-emerald-700"><CheckCircle className="w-3 h-3 mr-1" />Validé</Badge>;
                          } else if (memberStatus === 'pending') {
                            statusBadge = <Badge className="bg-amber-100 text-amber-700"><Clock className="w-3 h-3 mr-1" />Inscrit (à valider)</Badge>;
                          } else if (memberStatus === 'rejected') {
                            statusBadge = <Badge className="bg-red-100 text-red-700"><XCircle className="w-3 h-3 mr-1" />Refusé</Badge>;
                          } else if (isConverted) {
                            statusBadge = <Badge className="bg-blue-100 text-blue-700">Inscrit</Badge>;
                          } else {
                            statusBadge = <Badge className="bg-gray-100 text-gray-700"><AlertTriangle className="w-3 h-3 mr-1" />Lead seul</Badge>;
                          }
                          return (
                            <tr key={lead.id} className="border-b hover:bg-gray-50">
                              <td className="p-3">
                                <Checkbox
                                  checked={selectedLeads.includes(lead.id)}
                                  onCheckedChange={(checked) => {
                                    if (checked) setSelectedLeads([...selectedLeads, lead.id]);
                                    else setSelectedLeads(selectedLeads.filter(id => id !== lead.id));
                                  }}
                                />
                              </td>
                              <td className="p-3">
                                <a href={`mailto:${lead.email}`} className="text-blue-600 hover:underline flex items-center gap-1 text-sm">
                                  <Mail className="w-3 h-3" />{lead.email}
                                </a>
                              </td>
                              <td className="p-3 text-sm">{lead.nom || '-'}</td>
                              <td className="p-3 text-sm">{lead.prenom || '-'}</td>
                              <td className="p-3">
                                <Badge variant="outline" className="text-xs capitalize">{lead.member_type}</Badge>
                              </td>
                              <td className="p-3 text-sm text-muted-foreground">{formatDate(lead.created_at)}</td>
                              <td className="p-3">{statusBadge}</td>
                              <td className="p-3 text-right">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={async () => {
                                    if (!window.confirm('Supprimer ce lead ?')) return;
                                    try {
                                      await axios.delete(`${API}/admin/reseau/leads/${lead.id}`);
                                      toast.success('Lead supprimé');
                                      fetchData();
                                    } catch (e) { toast.error('Erreur'); }
                                  }}
                                  className="text-red-500"
                                  data-testid={`delete-lead-${lead.id}`}
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>


          {/* ARTISANS TAB */}
          <TabsContent value="artisans">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="flex items-center gap-2">
                    <Wrench className="w-5 h-5 text-emerald-600" />
                    Artisans / Sociétés Travaux
                    <InfoTooltip text="Artisans validés depuis 'Mon Réseau'. Reçoivent les chantiers qualifiés selon leur zone d'intervention." />
                  </CardTitle>
                </div>
              </CardHeader>
              <CardContent>
                {artisans.length === 0 ? (
                  <div className="text-center py-12 text-muted-foreground">
                    <Wrench className="w-12 h-12 mx-auto mb-4 opacity-20" />
                    <p>Aucun artisan validé</p>
                    <p className="text-sm">Les artisans inscrits apparaissent ici après validation dans "Mon Réseau".</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="artisans-grid">
                    {artisans.map(artisan => (
                      <Card key={artisan.id} className="border-2 hover:border-emerald-500 transition-colors">
                        <CardContent className="p-4">
                          <div className="flex items-start justify-between mb-3">
                            <div>
                              <h3 className="font-bold text-lg">{artisan.company_name}</h3>
                              <p className="text-xs text-muted-foreground">{artisan.full_name}</p>
                              {artisan.siret && <p className="text-xs text-muted-foreground">SIRET: {artisan.siret}</p>}
                            </div>
                          </div>
                          <div className="space-y-2 text-sm">
                            <a href={`mailto:${artisan.email}`} className="flex items-center gap-2 text-blue-600 hover:underline">
                              <Mail className="w-4 h-4" />{artisan.email}
                            </a>
                            {artisan.phone && (
                              <p className="flex items-center gap-2 text-muted-foreground">
                                <Phone className="w-4 h-4" />{artisan.phone}
                              </p>
                            )}
                            {artisan.zone_ville && (
                              <p className="flex items-center gap-2 text-muted-foreground">
                                <MapPin className="w-4 h-4" />Zone : {artisan.zone_ville} ({artisan.zone_rayon_km || 50} km)
                              </p>
                            )}
                            {artisan.metiers && artisan.metiers.length > 0 && (
                              <div className="flex flex-wrap gap-1 mt-2">
                                {artisan.metiers.map((metier, idx) => (
                                  <Badge key={idx} variant="outline" className="text-xs">{metier}</Badge>
                                ))}
                                {artisan.metier_autre && (
                                  <Badge variant="outline" className="text-xs bg-gray-100">{artisan.metier_autre}</Badge>
                                )}
                              </div>
                            )}
                          </div>
                          <div className="flex justify-end mt-3">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={async () => {
                                if (!window.confirm('Supprimer cet artisan ?')) return;
                                try {
                                  await axios.delete(`${API}/admin/artisans/${artisan.id}`);
                                  toast.success('Artisan supprimé');
                                  fetchData();
                                } catch (e) { toast.error('Erreur'); }
                              }}
                              className="text-red-500"
                              data-testid={`delete-artisan-${artisan.id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* PROSPECTS TAB */}
          <TabsContent value="prospects">
            <div className="space-y-4">
              {/* Stats */}
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                <Card><CardContent className="p-4 flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-emerald-100"><TrendingUp className="w-5 h-5 text-emerald-600" /></div>
                  <div><p className="text-2xl font-bold">{prospects.length}</p><p className="text-xs text-muted-foreground">Total prospects</p></div>
                </CardContent></Card>
                <Card><CardContent className="p-4 flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-blue-100"><Mail className="w-5 h-5 text-blue-600" /></div>
                  <div><p className="text-2xl font-bold">{prospects.filter(p => p.phone).length}</p><p className="text-xs text-muted-foreground">Avec téléphone</p></div>
                </CardContent></Card>
                <Card><CardContent className="p-4 flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-purple-100"><Calendar className="w-5 h-5 text-purple-600" /></div>
                  <div><p className="text-2xl font-bold">{prospects.filter(p => { const d = new Date(p.created_at); const now = new Date(); return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear(); }).length}</p><p className="text-xs text-muted-foreground">Ce mois</p></div>
                </CardContent></Card>
                <Card><CardContent className="p-4 flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-amber-100"><Euro className="w-5 h-5 text-amber-600" /></div>
                  <div><p className="text-2xl font-bold">{prospects.length > 0 ? formatPrice(prospects.reduce((s, p) => s + (p.estimated_price || 0), 0) / prospects.length) : '0 €'}</p><p className="text-xs text-muted-foreground">Estimation moy.</p></div>
                </CardContent></Card>
              </div>

              <Card>
                <CardHeader className="pb-3">
                  <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
                    <CardTitle className="flex items-center gap-2 text-base">
                      <TrendingUp className="w-5 h-5 text-emerald-600" />
                      Prospects
                      <InfoTooltip text="Tous les leads capturés (Estimation, Radar, Conseil, Audit). Clique sur un prospect pour voir les détails. Change le statut quand tu as contacté/converti." />
                    </CardTitle>
                    <div className="flex flex-wrap items-center gap-2">
                      {selectedProspects.length > 0 && (
                        <Button variant="destructive" size="sm" onClick={async () => {
                          if (window.confirm(`Supprimer ${selectedProspects.length} prospect(s) ?`)) {
                            try { await axios.post(`${API}/admin/prospects/bulk-delete`, selectedProspects); toast.success(`${selectedProspects.length} prospect(s) supprimé(s)`); setSelectedProspects([]); fetchData(); } catch { toast.error('Erreur'); }
                          }
                        }}>
                          <Trash2 className="w-3.5 h-3.5 mr-1" />Supprimer ({selectedProspects.length})
                        </Button>
                      )}
                      <Button variant="outline" size="sm" onClick={() => window.open(`${API}/admin/prospects/export`, '_blank')} className="border-emerald-500 text-emerald-600 hover:bg-emerald-50">
                        <Download className="w-3.5 h-3.5 mr-1" />CSV
                      </Button>
                      <InfoTooltip text="Télécharge la liste complète des prospects au format CSV (ouvrable dans Excel)." />
                    </div>
                  </div>
                  {/* Search + Blocs Filtres */}
                  <div className="mt-3 space-y-3">
                    {/* Barre de recherche */}
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <Input data-testid="prospect-search" placeholder="Rechercher par email, téléphone, adresse..." value={prospectSearch} onChange={(e) => { setProspectSearch(e.target.value); prospectsPagination.reset(); }} className="pl-9 h-9" />
                    </div>
                    {/* 3 Blocs de navigation */}
                    <div className="grid grid-cols-3 gap-3">
                      {[
                        { key: 'direct', label: 'Mes Prospects', sublabel: 'Directs (sans partenaire)', color: 'emerald', count: prospects.filter(p => !p.partner_subdomain).length },
                        { key: 'partenaire', label: 'Partenaires', sublabel: 'Via sous-domaine partenaire', color: 'orange', count: prospects.filter(p => !!p.partner_subdomain).length },
                        { key: 'affilie', label: 'Affiliés', sublabel: 'Via code affilié', color: 'blue', count: 0 },
                      ].map(bloc => (
                        <button key={bloc.key} data-testid={`prospect-bloc-${bloc.key}`}
                          onClick={() => { setProspectBloc(bloc.key); setProspectSourceFilter('all'); setProspectPartnerFilter('all'); setProspectStatusFilter('all'); prospectsPagination.reset(); }}
                          className={`p-3 rounded-xl border-2 text-left transition-all cursor-pointer ${
                            prospectBloc === bloc.key
                              ? bloc.color === 'emerald' ? 'border-emerald-500 bg-emerald-50' : bloc.color === 'orange' ? 'border-orange-500 bg-orange-50' : 'border-blue-500 bg-blue-50'
                              : 'border-gray-200 bg-white hover:border-gray-300'
                          }`}>
                          <div className={`text-xl font-bold ${prospectBloc === bloc.key ? (bloc.color === 'emerald' ? 'text-emerald-700' : bloc.color === 'orange' ? 'text-orange-700' : 'text-blue-700') : 'text-gray-700'}`}>{bloc.count}</div>
                          <div className="text-sm font-semibold text-gray-800">{bloc.label}</div>
                          <div className="text-xs text-gray-400">{bloc.sublabel}</div>
                        </button>
                      ))}
                    </div>
                    {/* Sous-filtres selon le bloc actif */}
                    {prospectBloc === 'direct' && (
                      <div className="flex flex-wrap items-center gap-2 pt-1 border-t border-gray-100">
                        <span className="text-xs font-medium text-gray-500">Type :</span>
                        {['all', 'estimation', 'radar', 'conseil', 'chantier'].map(f => (
                          <Button key={f} variant={prospectSourceFilter === f ? 'default' : 'outline'} size="sm"
                            onClick={() => { setProspectSourceFilter(f); prospectsPagination.reset(); }}
                            className={`text-xs h-7 ${prospectSourceFilter === f ? 'bg-emerald-600 hover:bg-emerald-700 text-white' : ''}`}>
                            {f === 'all' ? 'Tous' : f.charAt(0).toUpperCase() + f.slice(1)}
                          </Button>
                        ))}
                        <span className="text-gray-200 mx-1">|</span>
                        <span className="text-xs font-medium text-gray-500">Statut :</span>
                        {['all', 'completed', 'abandoned'].map(f => (
                          <Button key={f} variant={prospectStatusFilter === f ? 'default' : 'outline'} size="sm"
                            onClick={() => { setProspectStatusFilter(f); prospectsPagination.reset(); }}
                            className={`text-xs h-7 ${prospectStatusFilter === f ? (f === 'abandoned' ? 'bg-red-500 hover:bg-red-600 text-white' : f === 'completed' ? 'bg-green-600 hover:bg-green-700 text-white' : 'bg-gray-600 hover:bg-gray-700 text-white') : ''}`}>
                            {f === 'all' ? 'Tous' : f === 'completed' ? 'Complété' : 'Abandonné'}
                          </Button>
                        ))}
                      </div>
                    )}
                    {prospectBloc === 'partenaire' && (
                      <div className="flex flex-wrap items-center gap-2 pt-1 border-t border-gray-100">
                        <span className="text-xs font-medium text-gray-500">Partenaire :</span>
                        {['all', ...Array.from(new Set(prospects.filter(p => p.partner_subdomain).map(p => p.partner_subdomain)))].map(f => (
                          <Button key={f} variant={prospectPartnerFilter === f ? 'default' : 'outline'} size="sm"
                            onClick={() => { setProspectPartnerFilter(f); prospectsPagination.reset(); }}
                            className={`text-xs h-7 ${prospectPartnerFilter === f ? 'bg-orange-500 hover:bg-orange-600 text-white' : ''}`}>
                            {f === 'all' ? 'Tous' : f}
                          </Button>
                        ))}
                        <span className="text-gray-200 mx-1">|</span>
                        <span className="text-xs font-medium text-gray-500">Type :</span>
                        {['all', 'estimation', 'radar', 'conseil'].map(f => (
                          <Button key={f} variant={prospectSourceFilter === f ? 'default' : 'outline'} size="sm"
                            onClick={() => { setProspectSourceFilter(f); prospectsPagination.reset(); }}
                            className={`text-xs h-7 ${prospectSourceFilter === f ? 'bg-orange-500 hover:bg-orange-600 text-white' : ''}`}>
                            {f === 'all' ? 'Tous' : f.charAt(0).toUpperCase() + f.slice(1)}
                          </Button>
                        ))}
                        <span className="text-gray-200 mx-1">|</span>
                        <span className="text-xs font-medium text-gray-500">Statut :</span>
                        {['all', 'completed', 'abandoned'].map(f => (
                          <Button key={f} variant={prospectStatusFilter === f ? 'default' : 'outline'} size="sm"
                            onClick={() => { setProspectStatusFilter(f); prospectsPagination.reset(); }}
                            className={`text-xs h-7 ${prospectStatusFilter === f ? (f === 'abandoned' ? 'bg-red-500 hover:bg-red-600 text-white' : f === 'completed' ? 'bg-green-600 hover:bg-green-700 text-white' : 'bg-gray-600 hover:bg-gray-700 text-white') : ''}`}>
                            {f === 'all' ? 'Tous' : f === 'completed' ? 'Complété' : 'Abandonné'}
                          </Button>
                        ))}
                      </div>
                    )}
                    {prospectBloc === 'affilie' && (
                      <div className="flex items-center justify-center gap-2 py-3 border-t border-gray-100 text-gray-400 text-sm">
                        <Link2 className="w-4 h-4" />
                        Aucun prospect affilié enregistré pour l'instant.
                      </div>
                    )}
                  </div>
                </CardHeader>
                <CardContent>
                  {(() => {
                    const filtered = prospects
                      .filter(p => {
                        const q = prospectSearch.toLowerCase();
                        const matchSearch = !q || p.email?.toLowerCase().includes(q) || p.phone?.includes(q) || p.address?.toLowerCase().includes(q);
                        const matchBloc =
                          prospectBloc === 'direct' ? !p.partner_subdomain :
                          prospectBloc === 'partenaire' ? !!p.partner_subdomain :
                          false;
                        const matchSource = prospectSourceFilter === 'all' || (p.source || 'estimation') === prospectSourceFilter;
                        const matchPartner = prospectPartnerFilter === 'all' || p.partner_subdomain === prospectPartnerFilter;
                        const isAbandoned = p.source === 'estimation' && (!p.estimated_price || p.estimated_price === 0);
                        const matchStatus = prospectStatusFilter === 'all' || (prospectStatusFilter === 'abandoned' ? isAbandoned : !isAbandoned);
                        return matchSearch && matchBloc && matchSource && matchPartner && matchStatus;
                      })
                      .sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
                    const paginated = prospectsPagination.paginate(filtered);
                    return filtered.length === 0 ? (
                      <div className="text-center py-12 text-muted-foreground">
                        <TrendingUp className="w-12 h-12 mx-auto mb-4 text-emerald-200" />
                        <p>Aucun prospect trouvé</p>
                      </div>
                    ) : (
                      <>
                        <div className="flex items-center gap-2 p-2 bg-gray-50 rounded-lg mb-3">
                          <Checkbox checked={selectedProspects.length === filtered.length && filtered.length > 0} onCheckedChange={(checked) => { if (checked) { setSelectedProspects(filtered.map(p => p.id)); } else { setSelectedProspects([]); } }} />
                          <span className="text-xs text-muted-foreground">Tout sélectionner ({filtered.length})</span>
                        </div>
                        <div className="overflow-x-auto">
                          <table className="w-full text-sm">
                            <thead>
                              <tr className="border-b bg-gray-50/50">
                                <th className="p-2 w-8"></th>
                                <th className="text-left p-2 font-medium text-gray-600">Email</th>
                                <th className="text-left p-2 font-medium text-gray-600">Téléphone</th>
                                <th className="text-left p-2 font-medium text-gray-600">Source</th>
                                <th className="text-left p-2 font-medium text-gray-600">Partenaire</th>
                                <th className="text-left p-2 font-medium text-gray-600">Statut</th>
                                <th className="text-left p-2 font-medium text-gray-600">Adresse</th>
                                <th className="text-right p-2 font-medium text-gray-600">Estimation</th>
                                <th className="text-right p-2 font-medium text-gray-600">Date</th>
                              </tr>
                            </thead>
                            <tbody>
                              {paginated.map((prospect) => (
                                <tr key={prospect.id} className="border-b hover:bg-gray-50 transition-colors">
                                  <td className="p-2">
                                    <Checkbox checked={selectedProspects.includes(prospect.id)} onCheckedChange={(checked) => {
                                      if (checked) setSelectedProspects([...selectedProspects, prospect.id]);
                                      else setSelectedProspects(selectedProspects.filter(id => id !== prospect.id));
                                    }} />
                                  </td>
                                  <td className="p-2 font-medium text-navy">{prospect.email}</td>
                                  <td className="p-2">{prospect.phone ? <a href={`tel:${prospect.phone}`} className="text-blue-600 hover:underline flex items-center gap-1"><Phone className="w-3 h-3" />{prospect.phone}</a> : <span className="text-gray-400">-</span>}</td>
                                  <td className="p-2">
                                    <Badge variant="outline" className={`text-[10px] ${
                                      (prospect.source || 'estimation') === 'radar' ? 'border-purple-300 text-purple-700 bg-purple-50' :
                                      (prospect.source || 'estimation') === 'conseil' ? 'border-indigo-300 text-indigo-700 bg-indigo-50' :
                                      'border-emerald-300 text-emerald-700 bg-emerald-50'
                                    }`}>{prospect.source || 'estimation'}</Badge>
                                  </td>
                                  <td className="p-2">
                                    {prospect.partner_subdomain ? (
                                      <Badge variant="outline" className="text-[10px] border-orange-300 text-orange-700 bg-orange-50 font-semibold">{prospect.partner_subdomain}</Badge>
                                    ) : <span className="text-gray-300 text-xs">direct</span>}
                                  </td>
                                  <td className="p-2">
                                    {prospect.source === 'radar' ? (
                                      prospect.plan_received ? (
                                        <Badge variant="outline" className="text-[10px] border-green-300 text-green-700 bg-green-50 font-semibold">Avec plan</Badge>
                                      ) : (
                                        <Badge variant="outline" className="text-[10px] border-orange-300 text-orange-700 bg-orange-50 font-semibold">Sans plan</Badge>
                                      )
                                    ) : prospect.source === 'estimation' ? (
                                      (!prospect.estimated_price || prospect.estimated_price === 0) ? (
                                        <Badge variant="outline" className="text-[10px] border-red-300 text-red-700 bg-red-50 font-semibold">Abandonné</Badge>
                                      ) : (
                                        <Badge variant="outline" className="text-[10px] border-green-300 text-green-700 bg-green-50">Complété</Badge>
                                      )
                                    ) : (
                                      <Badge variant="outline" className="text-[10px] border-green-300 text-green-700 bg-green-50">Complété</Badge>
                                    )}
                                  </td>
                                  <td className="p-2 text-muted-foreground max-w-[200px] truncate">
                                    {prospect.source === 'radar' ? (prospect.adresse_saisie || prospect.address) : prospect.address}
                                  </td>
                                  <td className="p-2 text-right font-semibold text-amber-600">{prospect.estimated_price ? formatPrice(prospect.estimated_price) : '-'}</td>
                                  <td className="p-2 text-right text-muted-foreground text-xs">{formatDate(prospect.created_at)}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                        <Pagination currentPage={prospectsPagination.page} totalItems={filtered.length} itemsPerPage={prospectsPagination.perPage} onPageChange={prospectsPagination.setPage} onItemsPerPageChange={prospectsPagination.setPerPage} />
                      </>
                    );
                  })()}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* CHANTIERS VIP TAB */}
          <TabsContent value="chantiers">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      <Shield className="w-5 h-5 text-gold" />
                      Demandes de Chantier VIP
                      <InfoTooltip text="Demandes Suivi de Travaux : PAS de paiement en ligne. Tu appelles directement le client pour négocier. Utilise les statuts Nouveau / Contacté / En cours / Terminé pour suivre." />
                    </CardTitle>
                    <p className="text-sm text-muted-foreground mt-1">
                      Demandes d'accompagnement suivi de travaux - Priorité haute
                    </p>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {chantierDemands.length === 0 ? (
                  <div className="text-center py-12 text-muted-foreground">
                    <Shield className="w-12 h-12 mx-auto mb-4 text-gold/30" />
                    <p>Aucune demande de chantier pour le moment</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {chantierDemands.map((demand) => {
                      const budgetLabels = {
                        "moins_20k": "< 20 000 €",
                        "20k_50k": "20k - 50k €",
                        "50k_100k": "50k - 100k €",
                        "plus_100k": "> 100 000 €"
                      };
                      const statusColors = {
                        "nouveau": "bg-red-100 text-red-700",
                        "contacte": "bg-blue-100 text-blue-700",
                        "en_cours": "bg-gold/20 text-gold",
                        "termine": "bg-emerald-100 text-emerald-700"
                      };
                      const statusLabels = {
                        "nouveau": "🔴 Nouveau",
                        "contacte": "📞 Contacté",
                        "en_cours": "🔧 En cours",
                        "termine": "✅ Terminé"
                      };
                      
                      return (
                        <div key={demand.id} className="p-4 border-2 border-gold/30 rounded-xl bg-gold/5">
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="flex items-center gap-3 mb-2">
                                <Badge className={statusColors[demand.status] || statusColors.nouveau}>
                                  {statusLabels[demand.status] || "Nouveau"}
                                </Badge>
                                <InfoTooltip text="Nouveau = à rappeler | Contacté = appel passé | En cours = devis en route | Terminé = dossier clôturé" size={11} />
                                <Badge variant="outline" className="border-gold text-gold">
                                  {budgetLabels[demand.budget] || demand.budget}
                                </Badge>
                              </div>
                              <h4 className="font-semibold text-navy text-lg">{demand.address}</h4>
                              {demand.description && (
                                <p className="text-sm text-muted-foreground mt-1">{demand.description}</p>
                              )}
                              <div className="flex items-center gap-4 mt-3 text-sm">
                                <span className="flex items-center gap-1">
                                  <Phone className="w-4 h-4 text-gold" />
                                  <a href={`tel:${demand.phone}`} className="text-navy hover:underline">{demand.phone}</a>
                                </span>
                                <span className="flex items-center gap-1">
                                  <Mail className="w-4 h-4 text-gold" />
                                  <a href={`mailto:${demand.email}`} className="text-navy hover:underline">{demand.email}</a>
                                </span>
                                <span className="text-muted-foreground">
                                  {formatDate(demand.created_at)}
                                </span>
                              </div>
                            </div>
                            <div className="flex flex-col gap-2">
                              <select
                                value={demand.status}
                                onChange={async (e) => {
                                  try {
                                    await axios.put(`${API}/admin/chantier-demands/${demand.id}/status?status=${e.target.value}`);
                                    toast.success('Statut mis à jour');
                                    fetchData();
                                  } catch (error) {
                                    toast.error('Erreur lors de la mise à jour');
                                  }
                                }}
                                className="p-2 border border-gold/30 rounded-lg text-sm bg-white"
                              >
                                <option value="nouveau">🔴 Nouveau</option>
                                <option value="contacte">📞 Contacté</option>
                                <option value="en_cours">🔧 En cours</option>
                                <option value="termine">✅ Terminé</option>
                              </select>
                              
                              {/* Assign to Chiffreur */}
                              {demand.assigned_chiffreur ? (
                                <div className="flex items-center gap-1 p-2 bg-emerald-50 border border-emerald-200 rounded-lg">
                                  <Calculator className="w-4 h-4 text-emerald-600" />
                                  <span className="text-sm text-emerald-700 font-medium">
                                    {chiffreurs.find(c => c.id === demand.assigned_chiffreur)?.name || 'Chiffreur'}
                                  </span>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="h-5 w-5 p-0 text-red-400 hover:text-red-600"
                                    onClick={async () => {
                                      try {
                                        await axios.post(`${API}/admin/chiffreurs/${demand.assigned_chiffreur}/unassign-chantier?chantier_id=${demand.id}`);
                                        toast.success('Chiffreur retiré');
                                        fetchData();
                                      } catch (error) {
                                        toast.error('Erreur');
                                      }
                                    }}
                                  >
                                    <X className="w-3 h-3" />
                                  </Button>
                                </div>
                              ) : (
                                <Button
                                  data-testid={`assign-chiffreur-${demand.id}`}
                                  variant="outline"
                                  size="sm"
                                  className="border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                                  onClick={() => setAssignChiffreurModal({ show: true, projectId: demand.id })}
                                >
                                  <Calculator className="w-4 h-4 mr-1" />
                                  Assigner
                                </Button>
                              )}
                              
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={async () => {
                                  if (window.confirm('Supprimer cette demande ?')) {
                                    try {
                                      await axios.delete(`${API}/admin/chantier-demands/${demand.id}`);
                                      toast.success('Demande supprimée');
                                      fetchData();
                                    } catch (error) {
                                      toast.error('Erreur lors de la suppression');
                                    }
                                  }
                                }}
                                className="text-red-500 hover:text-red-700"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* MESSAGING TAB */}
          <TabsContent value="messaging">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <MessageCircle className="w-5 h-5 text-purple-600" />
                  Messagerie Master VIP
                </CardTitle>
              </CardHeader>
              <CardContent>
                <MessagingDashboard token={localStorage.getItem('token')} />
              </CardContent>
            </Card>
          </TabsContent>

          {/* APPOINTMENTS TAB */}
          <TabsContent value="appointments">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Calendar className="w-5 h-5 text-gold" />
                  Gestion des Rendez-vous
                  <InfoTooltip text="Tous les RDV pris via le calendrier interne (clients Conseil 99€). Tu peux configurer les créneaux disponibles dans les onglets ci-dessous." />
                </CardTitle>
              </CardHeader>
              <CardContent>
                <AppointmentsDashboard token={localStorage.getItem('token')} />
              </CardContent>
            </Card>
          </TabsContent>

          {/* NETWORK TAB - MON RÉSEAU */}
          <TabsContent value="network">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="flex items-center gap-2">
                    <Network className="w-5 h-5 text-emerald-600" />
                    Gestion du Réseau
                    {pendingNetworkCount > 0 && (
                      <Badge variant="destructive" className="ml-2">{pendingNetworkCount} en attente</Badge>
                    )}
                  </CardTitle>
                  <div className="flex gap-2">
                    {/* View toggle - List or Map */}
                    <div className="flex border rounded-lg overflow-hidden mr-4">
                      <Button
                        variant={networkView === 'list' ? 'default' : 'ghost'}
                        size="sm"
                        onClick={() => setNetworkView('list')}
                        className={networkView === 'list' ? 'bg-emerald-500 text-white' : ''}
                      >
                        <List className="w-4 h-4 mr-1" />Liste
                      </Button>
                      <Button
                        variant={networkView === 'map' ? 'default' : 'ghost'}
                        size="sm"
                        onClick={() => setNetworkView('map')}
                        className={networkView === 'map' ? 'bg-amber-500 text-white' : ''}
                      >
                        <Map className="w-4 h-4 mr-1" />À valider
                      </Button>
                      <Button
                        variant={networkView === 'global-map' ? 'default' : 'ghost'}
                        size="sm"
                        onClick={() => setNetworkView('global-map')}
                        className={networkView === 'global-map' ? 'bg-blue-500 text-white' : ''}
                      >
                        <Map className="w-4 h-4 mr-1" />Carte Globale
                      </Button>
                    </div>
                    
                    {/* Filters - only show in list view */}
                    {networkView === 'list' && (
                      <>
                        <Button
                          variant={networkFilter === 'all' ? 'default' : 'outline'}
                          size="sm"
                          onClick={() => setNetworkFilter('all')}
                        >
                          Tous ({networkMembers.length})
                        </Button>
                        <Button
                          variant={networkFilter === 'pending' ? 'default' : 'outline'}
                          size="sm"
                          onClick={() => setNetworkFilter('pending')}
                          className={networkFilter === 'pending' ? 'bg-amber-500' : ''}
                        >
                          En attente ({networkMembers.filter(m => m.status === 'pending').length})
                        </Button>
                        <Button
                          variant={networkFilter === 'validated' ? 'default' : 'outline'}
                          size="sm"
                          onClick={() => setNetworkFilter('validated')}
                          className={networkFilter === 'validated' ? 'bg-emerald-500' : ''}
                        >
                          Validés ({networkMembers.filter(m => m.status === 'validated' || m.status === 'approved').length})
                        </Button>
                        <Button
                          variant={networkFilter === 'rejected' ? 'default' : 'outline'}
                          size="sm"
                          onClick={() => setNetworkFilter('rejected')}
                          className={networkFilter === 'rejected' ? 'bg-red-500' : ''}
                        >
                          Refusés ({networkMembers.filter(m => m.status === 'rejected').length})
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {/* MAP VIEW — À valider (jaune + vert) */}
                {networkView === 'map' && (
                  <div>
                    <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm">
                      <p className="text-amber-800">
                        <Map className="w-4 h-4 inline mr-2" />
                        <strong>Carte À valider.</strong> Jaune = en attente, Vert = validés.
                        Cliquez sur un marqueur pour voir les détails.
                      </p>
                    </div>
                    <Suspense fallback={
                      <div className="h-[500px] bg-gray-100 rounded-lg flex items-center justify-center">
                        <RefreshCw className="w-8 h-8 animate-spin text-emerald-500" />
                      </div>
                    }>
                      <ArtisanMap
                        members={networkMembers.filter(m =>
                          (m.status === 'pending' || m.status === 'validated' || m.status === 'approved') &&
                          (m.zone_latitude || m.artisan_info?.intervention_zone?.lat)
                        )}
                        showPending={true}
                      />
                    </Suspense>
                  </div>
                )}

                {/* GLOBAL MAP VIEW — tous les types + tous les statuts */}
                {networkView === 'global-map' && (
                  <div>
                    <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg text-sm">
                      <p className="text-blue-800">
                        <Map className="w-4 h-4 inline mr-2" />
                        <strong>Carte Réseau Globale.</strong> Tous les membres géolocalisés —
                        Artisans, Agences, Affiliés, Partenaires. Jaune = en attente, Vert = validé.
                      </p>
                    </div>
                    <Suspense fallback={
                      <div className="h-[500px] bg-gray-100 rounded-lg flex items-center justify-center">
                        <RefreshCw className="w-8 h-8 animate-spin text-blue-500" />
                      </div>
                    }>
                      <ArtisanMap
                        members={networkMembers.filter(m =>
                          m.zone_latitude || m.artisan_info?.intervention_zone?.lat
                        )}
                        showPending={true}
                      />
                    </Suspense>
                  </div>
                )}

                {/* LIST VIEW */}
                {networkView === 'list' && (
                  <>
                    {filteredNetworkMembers.length === 0 ? (
                  <div className="text-center py-12 text-gray-500">
                    <Network className="w-12 h-12 mx-auto mb-4 opacity-50" />
                    <p>Aucun membre dans cette catégorie</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {filteredNetworkMembers.map((member) => {
                      const typeConfig = MEMBER_TYPES[member.member_type] || MEMBER_TYPES.artisan;
                      const statusConfig = NETWORK_STATUS[member.status] || NETWORK_STATUS.pending;
                      const TypeIcon = typeConfig.icon;
                      const StatusIcon = statusConfig.icon;
                      
                      return (
                        <div key={member.id} className="border rounded-lg p-4 hover:shadow-md transition-shadow">
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              {/* Header avec type et status */}
                              <div className="flex items-center gap-3 mb-3">
                                <Badge className={typeConfig.color}>
                                  <TypeIcon className="w-3 h-3 mr-1" />
                                  {typeConfig.label}
                                </Badge>
                                <Badge className={statusConfig.color}>
                                  <StatusIcon className="w-3 h-3 mr-1" />
                                  {statusConfig.label}
                                </Badge>
                                <span className="text-xs text-gray-500">
                                  Inscrit le {formatDate(member.created_at)}
                                </span>
                              </div>
                              
                              {/* Infos personnelles */}
                              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                <div>
                                  <h4 className="font-semibold text-lg">{member.full_name}</h4>
                                  <div className="flex items-center gap-2 text-sm text-gray-600 mt-1">
                                    <Mail className="w-4 h-4" />
                                    {member.email}
                                  </div>
                                  <div className="flex items-center gap-2 text-sm text-gray-600">
                                    <Phone className="w-4 h-4" />
                                    {member.phone || '-'}
                                  </div>
                                </div>
                                
                                {/* Infos société */}
                                {member.company_info && (
                                  <div>
                                    <p className="text-sm font-medium text-gray-700">Société</p>
                                    <p className="text-sm">{member.company_info.name || '-'}</p>
                                    <p className="text-xs text-gray-500">SIRET: {member.company_info.siret || '-'}</p>
                                    {member.company_info.city && (
                                      <p className="text-xs text-gray-500 flex items-center gap-1">
                                        <MapPin className="w-3 h-3" />
                                        {member.company_info.city} {member.company_info.postal_code}
                                      </p>
                                    )}
                                  </div>
                                )}
                                
                                {/* Infos artisan spécifiques */}
                                {member.artisan_info && (
                                  <div>
                                    <p className="text-sm font-medium text-gray-700">Métiers</p>
                                    <div className="flex flex-wrap gap-1 mt-1">
                                      {member.artisan_info.trades?.map((trade, idx) => (
                                        <Badge key={idx} variant="outline" className="text-xs">
                                          {trade}
                                        </Badge>
                                      ))}
                                      {member.artisan_info.other_trade && (
                                        <Badge variant="outline" className="text-xs bg-gray-100">
                                          {member.artisan_info.other_trade}
                                        </Badge>
                                      )}
                                    </div>
                                    {member.artisan_info.intervention_zone && (
                                      <p className="text-xs text-gray-500 mt-2 flex items-center gap-1">
                                        <MapPin className="w-3 h-3" />
                                        Zone: {member.artisan_info.intervention_zone.address} ({member.artisan_info.intervention_zone.radius_km} km)
                                      </p>
                                    )}
                                  </div>
                                )}
                              </div>
                              
                              {/* Décennale (pour artisans) */}
                              {member.artisan_info?.decennale_url && (
                                <div className="mt-3 p-2 bg-gray-50 rounded flex items-center gap-2">
                                  <FileText className="w-4 h-4 text-gray-500" />
                                  <span className="text-sm text-gray-600">Décennale:</span>
                                  <a 
                                    href={member.artisan_info.decennale_url} 
                                    target="_blank" 
                                    rel="noopener noreferrer"
                                    className="text-sm text-blue-600 hover:underline flex items-center gap-1"
                                  >
                                    Voir le document <ExternalLink className="w-3 h-3" />
                                  </a>
                                </div>
                              )}
                            </div>
                            
                            {/* Actions */}
                            <div className="flex flex-col gap-2 ml-4">
                              {member.status === 'pending' && (
                                <>
                                  <Button 
                                    size="sm" 
                                    className="bg-emerald-500 hover:bg-emerald-600"
                                    onClick={() => validateNetworkMember(member.id)}
                                  >
                                    <Check className="w-4 h-4 mr-1" />
                                    Valider
                                  </Button>
                                  <Button 
                                    size="sm" 
                                    variant="outline"
                                    className="text-red-600 border-red-300 hover:bg-red-50"
                                    onClick={() => rejectNetworkMember(member.id)}
                                  >
                                    <X className="w-4 h-4 mr-1" />
                                    Refuser
                                  </Button>
                                </>
                              )}
                              {(member.status === 'validated' || member.status === 'approved') && (
                                <Badge className="bg-emerald-100 text-emerald-700">
                                  <CheckCircle className="w-4 h-4 mr-1" />
                                  Actif
                                </Badge>
                              )}
                              <Button 
                                size="sm" 
                                variant="ghost"
                                className="text-red-500"
                                onClick={() => deleteNetworkMember(member.id)}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
                  </>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* HOTLINE TAB */}
          <TabsContent value="hotline">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Conversations List */}
              <Card className="lg:col-span-1">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Headphones className="w-5 h-5 text-purple-600" />
                    Conversations
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {hotlineConversations.length === 0 ? (
                    <div className="text-center py-8 text-gray-500">
                      <Headphones className="w-12 h-12 mx-auto mb-4 opacity-30" />
                      <p>Aucune conversation</p>
                    <p className="text-xs mt-2">Les messages des partenaires apparaîtront ici.</p>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {hotlineConversations.map((conv) => (
                        <button
                          key={conv._id}
                          onClick={() => openHotlineConversation(conv)}
                          className={`w-full p-3 rounded-lg text-left transition-colors ${
                            activeConversation?._id === conv._id 
                              ? 'bg-purple-100 border-purple-300' 
                              : 'bg-gray-50 hover:bg-gray-100'
                          } border`}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2 min-w-0">
                              <span className={`flex-shrink-0 text-xs px-1.5 py-0.5 rounded font-medium ${
                                conv.conversation_type === 'commercial'
                                  ? 'bg-green-100 text-green-700'
                                  : 'bg-purple-100 text-purple-700'
                              }`}>
                                {conv.conversation_type === 'commercial' ? 'Commercial' : 'Partenaire'}
                              </span>
                              <p className="font-medium truncate">{conv.partner_name}</p>
                            </div>
                            {conv.unread_count > 0 && (
                              <Badge className="bg-red-500 text-white flex-shrink-0">{conv.unread_count}</Badge>
                            )}
                          </div>
                          {conv.conversation_type === 'commercial' && conv.parent_partner_name && (
                            <p className="text-xs text-gray-500 mt-0.5 pl-1">via {conv.parent_partner_name}</p>
                          )}
                          <p className="text-sm text-gray-500 truncate mt-1">{conv.last_message}</p>
                          <p className="text-xs text-gray-400 mt-1">{formatDate(conv.last_date)}</p>
                        </button>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Messages */}
              <Card className="lg:col-span-2">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <MessageCircle className="w-5 h-5 text-purple-600" />
                    {activeConversation
                      ? `Discussion avec ${activeConversation.partner_name}${activeConversation.conversation_type === 'commercial' && activeConversation.parent_partner_name ? ` (${activeConversation.parent_partner_name})` : ''}`
                      : 'Sélectionnez une conversation'}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {!activeConversation ? (
                    <div className="text-center py-12 text-gray-500">
                      <MessageCircle className="w-16 h-16 mx-auto mb-4 opacity-30" />
                      <p>Sélectionnez une conversation dans la liste</p>
                    </div>
                  ) : (
                    <>
                      {/* Messages list */}
                      <div className="bg-gray-50 rounded-lg p-4 mb-4 max-h-[400px] overflow-y-auto">
                        {/* Bouton "Voir les précédents" */}
                        {hotlineHasMore && (
                          <div className="text-center mb-4">
                            <button
                              onClick={loadMoreHotlineMessages}
                              disabled={loadingMoreMessages}
                              className="text-sm text-purple-600 hover:text-purple-800 border border-purple-200 rounded-full px-4 py-1.5 bg-white hover:bg-purple-50 transition-colors disabled:opacity-50"
                            >
                              {loadingMoreMessages ? 'Chargement...' : `Voir les messages précédents (${hotlineTotal - hotlineMessages.length} de plus)`}
                            </button>
                          </div>
                        )}
                        {hotlineMessages.length === 0 ? (
                          <div className="text-center py-8 text-gray-500">
                            <p>Aucun message</p>
                          </div>
                        ) : (
                          <div className="space-y-4">
                            {hotlineMessages.map((msg, idx) => (
                              <div 
                                key={idx} 
                                className={`flex ${msg.from === 'admin' ? 'justify-end' : 'justify-start'}`}
                              >
                                <div className={`max-w-[70%] p-3 rounded-lg ${
                                  msg.from === 'admin' 
                                    ? 'bg-purple-600 text-white' 
                                    : 'bg-white border shadow-sm'
                                }`}>
                                  <p className="text-sm">{msg.message}</p>
                                  <p className={`text-xs mt-1 ${
                                    msg.from === 'admin' ? 'text-purple-200' : 'text-gray-400'
                                  }`}>
                                    {formatDate(msg.created_at)}
                                  </p>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>

                      {/* Send message */}
                      <div className="flex gap-2">
                        <Input
                          value={newHotlineMessage}
                          onChange={(e) => setNewHotlineMessage(e.target.value)}
                          placeholder="Écrire un message..."
                          onKeyDown={(e) => e.key === 'Enter' && sendHotlineMessage()}
                        />
                        <Button 
                          onClick={sendHotlineMessage}
                          disabled={sendingHotlineMessage || !newHotlineMessage.trim()}
                          className="bg-purple-600 hover:bg-purple-700"
                        >
                          <Send className="w-4 h-4" />
                        </Button>
                      </div>
                    </>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* SUBSCRIPTIONS TAB */}
          <TabsContent value="subscriptions">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="flex items-center gap-2">
                    <CreditCard className="w-5 h-5 text-pink-600" />
                    Gestion des Abonnements Partenaires
                  </CardTitle>
                  <div className="flex gap-2">
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={() => window.open(`${API}/admin/partenaires/promo-document`, '_blank')}
                      className="text-purple-600 border-purple-200 hover:bg-purple-50"
                      data-testid="promo-document-btn"
                    >
                      <FileText className="w-4 h-4 mr-1" /> Document Promo
                    </Button>
                    <Badge className="bg-emerald-100 text-emerald-700">
                      {partnerSubscriptions.filter(p => p.subscription_status === 'active').length} Actifs
                    </Badge>
                    <Badge className="bg-amber-100 text-amber-700">
                      {partnerSubscriptions.filter(p => p.status === 'pending').length} En attente
                    </Badge>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {partnerSubscriptions.length === 0 ? (
                  <div className="text-center py-12 text-gray-500">
                    <CreditCard className="w-16 h-16 mx-auto mb-4 opacity-30" />
                    <h3 className="text-lg font-medium mb-2">Aucun abonnement</h3>
                    <p className="text-sm">Les partenaires Page Blanche apparaîtront ici après paiement.</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b bg-gray-50">
                          <th className="text-left p-3 font-medium">Partenaire</th>
                          <th className="text-left p-3 font-medium">URL</th>
                          <th className="text-left p-3 font-medium">Statut</th>
                          <th className="text-left p-3 font-medium">Période</th>
                          <th className="text-left p-3 font-medium">Expiration</th>
                          <th className="text-right p-3 font-medium">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {partnerSubscriptions.map((partner) => {
                          // Code couleur par statut
                          const expiry = partner.subscription_expiry ? new Date(partner.subscription_expiry) : null;
                          const now = new Date();
                          const daysLeft = expiry ? Math.floor((expiry - now) / (1000 * 60 * 60 * 24)) : null;
                          const isExpired = expiry && expiry < now;
                          const isExpiringSoon = !isExpired && daysLeft !== null && daysLeft <= 7;
                          const rowBg = partner.is_blocked
                            ? 'bg-red-50 border-l-4 border-l-red-400'
                            : isExpired
                              ? 'bg-orange-50 border-l-4 border-l-orange-400'
                              : isExpiringSoon
                                ? 'bg-yellow-50 border-l-4 border-l-yellow-400'
                                : partner.subscription_status === 'active'
                                  ? 'border-l-4 border-l-emerald-400'
                                  : '';
                          return (
                          <React.Fragment key={partner.id}>
                          <tr className={`border-b hover:bg-gray-50/80 cursor-pointer ${rowBg}`}
                              onClick={() => setExpandedPartnerId(expandedPartnerId === partner.id ? null : partner.id)}>
                            <td className="p-3">
                              <div className="flex items-center gap-2">
                                {expandedPartnerId === partner.id ? <ChevronDown className="w-4 h-4 text-gray-400" /> : <ChevronRight className="w-4 h-4 text-gray-400" />}
                                <div>
                                  <div className="flex items-center gap-1.5">
                                    <p className="font-medium">{partner.full_name}</p>
                                    <span className="text-base" title={partner.country === 'algerie' ? 'Algérie' : 'France'}>{partner.country === 'algerie' ? '🇩🇿' : '🇫🇷'}</span>
                                  </div>
                                  <p className="text-sm text-gray-500">{partner.email}</p>
                                  {partner.company_name && (
                                    <p className="text-xs text-gray-400">{partner.company_name}</p>
                                  )}
                                </div>
                              </div>
                            </td>
                            <td className="p-3">
                              {partner.subdomain ? (
                                <a 
                                  href={`${window.location.origin}/p/${partner.subdomain}`} 
                                  target="_blank" 
                                  rel="noopener noreferrer"
                                  className="text-purple-600 hover:underline flex items-center gap-1"
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  /p/{partner.subdomain}
                                  <ExternalLink className="w-3 h-3" />
                                </a>
                              ) : (
                                <span className="text-gray-400">-</span>
                              )}
                            </td>
                            <td className="p-3">
                              {partner.is_blocked ? (
                                <Badge className="bg-red-100 text-red-700">
                                  <Ban className="w-3 h-3 mr-1" />Bloqué
                                </Badge>
                              ) : partner.subscription_status === 'active' ? (
                                <Badge className="bg-emerald-100 text-emerald-700">
                                  <CheckCircle className="w-3 h-3 mr-1" />Actif
                                </Badge>
                              ) : partner.subscription_status === 'pending_payment' ? (
                                <Badge className="bg-amber-100 text-amber-700">
                                  <Clock className="w-3 h-3 mr-1" />Paiement en attente
                                </Badge>
                              ) : partner.subscription_status === 'cancelled' ? (
                                <Badge className="bg-gray-100 text-gray-700">
                                  <XCircle className="w-3 h-3 mr-1" />Annulé
                                </Badge>
                              ) : (
                                <Badge className="bg-gray-100 text-gray-600">
                                  <Clock className="w-3 h-3 mr-1" />En attente
                                </Badge>
                              )}
                            </td>
                            <td className="p-3 text-sm">
                              <span className="font-medium">{partner.subscription_year || 0}</span>
                              <span className="text-gray-400 text-xs ml-1">
                                {partner.custom_subscription_price !== null && partner.custom_subscription_price !== undefined 
                                  ? `(${partner.custom_subscription_price}€/mois)` 
                                  : '(100€/mois)'}
                              </span>
                            </td>
                            <td className="p-3 text-sm">
                              {partner.subscription_expiry ? (
                                <div>
                                  <span className={
                                    isExpired
                                      ? 'text-orange-600 font-medium'
                                      : isExpiringSoon
                                        ? 'text-yellow-600 font-medium'
                                        : 'text-gray-600'
                                  }>
                                    {formatDate(partner.subscription_expiry)}
                                  </span>
                                  <div className="text-xs mt-0.5">
                                    {partner.is_blocked ? (
                                      <span className="text-red-600 font-semibold">Bloqué</span>
                                    ) : isExpired ? (
                                      <span className="text-orange-500">Expiré il y a {Math.abs(daysLeft)}j — grace</span>
                                    ) : isExpiringSoon ? (
                                      <span className="text-yellow-600 font-semibold">⚡ {daysLeft}j restants</span>
                                    ) : daysLeft !== null ? (
                                      <span className="text-emerald-600">{daysLeft}j restants</span>
                                    ) : null}
                                  </div>
                                </div>
                              ) : partner.subscription_next_billing ? (
                                <span className={
                                  new Date(partner.subscription_next_billing) < new Date() 
                                    ? 'text-red-600 font-medium' 
                                    : 'text-gray-600'
                                }>
                                  {formatDate(partner.subscription_next_billing)}
                                </span>
                              ) : (
                                <span className="text-gray-400">-</span>
                              )}
                            </td>
                            <td className="p-3 text-right" onClick={(e) => e.stopPropagation()}>
                              <div className="flex items-center justify-end gap-1">
                                {partner.subdomain && (
                                  <Button variant="ghost" size="sm" onClick={() => navigate(`/expert?partner=${partner.subdomain}`)} title={`Voir les dossiers de ${partner.full_name}`} className="text-sky-600 hover:bg-sky-50" data-testid={`view-dossiers-${partner.id}`}>
                                    <FolderOpen className="w-4 h-4" />
                                  </Button>
                                )}
                                <Button variant="ghost" size="sm" onClick={() => setManualPaymentPartner(partner)} title="Enregistrer un paiement reçu (virement, chèque, cash...)" className="text-emerald-600 hover:bg-emerald-50" data-testid={`manual-payment-${partner.id}`}>
                                  <Euro className="w-4 h-4" />
                                </Button>
                                <Button variant="ghost" size="sm" onClick={() => sendRenewalLink(partner.id)} title="Envoyer lien Stripe par email" className="text-purple-600 hover:bg-purple-50" data-testid={`renewal-link-${partner.id}`}>
                                  <Mail className="w-4 h-4" />
                                </Button>
                                <Button variant="ghost" size="sm" onClick={() => renewPartner(partner.id)} title="Renouveler 1 an (sans paiement)" className="text-blue-600 hover:bg-blue-50" data-testid={`renew-${partner.id}`}>
                                  <RotateCcw className="w-4 h-4" />
                                </Button>
                                <Button variant="ghost" size="sm" onClick={() => sendPartnerReminder(partner.id)} title="Envoyer un rappel" className="text-amber-600 hover:bg-amber-50">
                                  <Bell className="w-4 h-4" />
                                </Button>
                                <Button variant="ghost" size="sm" onClick={() => resendPartnerCredentials(partner.id)} title="Renvoyer les identifiants (nouveau mot de passe)" className="text-indigo-600 hover:bg-indigo-50" data-testid={`resend-credentials-${partner.id}`}>
                                  <KeyRound className="w-4 h-4" />
                                </Button>
                                <Button
                                  variant="ghost" size="sm"
                                  onClick={() => togglePartnerDomain(partner.id, partner.domain_enabled)}
                                  title={partner.domain_enabled ? 'Domaine custom ACTIVÉ — cliquer pour désactiver' : 'Activer option domaine custom'}
                                  className={partner.domain_enabled 
                                    ? 'text-white bg-emerald-500 hover:bg-emerald-600 rounded-md px-1.5' 
                                    : 'text-gray-300 hover:bg-gray-50'}
                                  data-testid={`domain-toggle-${partner.id}`}
                                >
                                  <Globe className="w-4 h-4" />
                                </Button>
                                <Button
                                  variant="ghost" size="sm"
                                  onClick={() => setPartnerCountry(partner.id, partner.country === 'algerie' ? 'france' : 'algerie')}
                                  title={partner.country === 'algerie' ? '🇩🇿 Algérie — cliquer pour France' : '🇫🇷 France — cliquer pour Algérie'}
                                  className="text-base hover:bg-gray-50 px-1.5"
                                  data-testid={`country-toggle-${partner.id}`}
                                >
                                  {partner.country === 'algerie' ? '🇩🇿' : '🇫🇷'}
                                </Button>
                                {partner.is_blocked ? (
                                  <Button variant="ghost" size="sm" onClick={() => unblockPartner(partner.id)} title="Débloquer" className="text-emerald-600 hover:bg-emerald-50">
                                    <CheckCircle className="w-4 h-4" />
                                  </Button>
                                ) : (
                                  <Button variant="ghost" size="sm" onClick={() => blockPartner(partner.id)} title="Bloquer" className="text-red-600 hover:bg-red-50">
                                    <Ban className="w-4 h-4" />
                                  </Button>
                                )}
                                {partner.subscription_status === 'active' && (
                                  <Button variant="ghost" size="sm" onClick={() => cancelPartnerSubscription(partner.id)} title="Annuler l'abonnement" className="text-red-600 hover:bg-red-50">
                                    <XCircle className="w-4 h-4" />
                                  </Button>
                                )}
                              </div>
                            </td>
                          </tr>
                          {/* Expanded row: pricing + services config */}
                          {expandedPartnerId === partner.id && (
                            <tr className="bg-slate-50">
                              <td colSpan={6} className="p-4">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                  {/* Pricing config */}
                                  <div className="bg-white p-4 rounded-lg border">
                                    <h4 className="font-semibold text-sm mb-3 flex items-center gap-2"><DollarSign className="w-4 h-4 text-purple-600" /> Prix pour <span className="text-purple-700">{partner.full_name || partner.email}</span></h4>
                                    <p className="text-xs text-gray-500 mb-3">Laissez vide = prix par défaut. Mettez 0 = gratuit. Seul vous voyez ce prix.</p>
                                    <div className="flex items-center gap-2">
                                      <Input
                                        type="number"
                                        min="0"
                                        step="1"
                                        placeholder={`100 (tarif mensuel)`}
                                        defaultValue={partner.custom_subscription_price !== null && partner.custom_subscription_price !== undefined ? partner.custom_subscription_price : ''}
                                        className="w-40"
                                        data-testid={`pricing-input-${partner.id}`}
                                        id={`pricing-${partner.id}`}
                                      />
                                      <span className="text-sm text-gray-500">€/mois</span>
                                      <Button size="sm" className="bg-purple-600 hover:bg-purple-700 text-white" data-testid={`save-pricing-${partner.id}`}
                                        onClick={() => {
                                          const val = document.getElementById(`pricing-${partner.id}`).value;
                                          updatePartnerPricing(partner.id, val === '' ? null : parseFloat(val));
                                        }}
                                      >
                                        <Check className="w-3 h-3 mr-1" /> Enregistrer
                                      </Button>
                                    </div>
                                  </div>
                                  {/* Services config */}
                                  <div className="bg-white p-4 rounded-lg border">
                                    <h4 className="font-semibold text-sm mb-3 flex items-center gap-2"><Settings className="w-4 h-4 text-purple-600" /> Services activés</h4>
                                    {partner.country && partner.country !== 'france' ? (
                                      <div>
                                        <p className="text-xs text-amber-600 bg-amber-50 border border-amber-200 rounded px-3 py-2 mb-3">
                                          Partenaire étranger — Service disponible : <strong>Estimation uniquement</strong>
                                        </p>
                                        <span className="px-3 py-1.5 rounded-full text-xs font-medium border bg-purple-100 text-purple-700 border-purple-300">
                                          <Check className="w-3 h-3 inline mr-1" />Estimation Gratuite ✓
                                        </span>
                                      </div>
                                    ) : (
                                      <div>
                                        <p className="text-xs text-gray-500 mb-3">Choisissez les services que ce partenaire peut proposer.</p>
                                        <div className="flex flex-wrap gap-3">
                                          {[
                                            { key: 'estimation', label: 'Estimation Gratuite' },
                                            { key: 'radar', label: 'Radar Foncier (49€)' },
                                            { key: 'audit', label: 'Audit Complet' },
                                            { key: 'conseil', label: 'Conseil (99€)' }
                                          ].map(svc => {
                                            const currentServices = partner.enabled_services || ['estimation', 'radar', 'audit', 'conseil'];
                                            const isEnabled = currentServices.includes(svc.key);
                                            return (
                                              <button key={svc.key} data-testid={`toggle-service-${svc.key}-${partner.id}`}
                                                onClick={() => {
                                                  const updated = isEnabled 
                                                    ? currentServices.filter(s => s !== svc.key)
                                                    : [...currentServices, svc.key];
                                                  updatePartnerServices(partner.id, updated);
                                                }}
                                                className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                                                  isEnabled ? 'bg-purple-100 text-purple-700 border-purple-300' : 'bg-gray-100 text-gray-400 border-gray-200'
                                                }`}
                                              >
                                                {isEnabled ? <Check className="w-3 h-3 inline mr-1" /> : <X className="w-3 h-3 inline mr-1" />}
                                                {svc.label}
                                              </button>
                                            );
                                          })}
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </td>
                            </tr>
                          )}
                          </React.Fragment>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
                
                {/* Legend */}
                <div className="mt-6 p-4 bg-gray-50 rounded-lg">
                  <h4 className="font-medium mb-2">Code couleur :</h4>
                  <div className="flex flex-wrap gap-3 text-sm mb-4">
                    <span className="flex items-center gap-2"><span className="w-3 h-3 rounded-full bg-emerald-400 inline-block"></span> Actif</span>
                    <span className="flex items-center gap-2"><span className="w-3 h-3 rounded-full bg-yellow-400 inline-block"></span> Expire dans ≤ 7 jours</span>
                    <span className="flex items-center gap-2"><span className="w-3 h-3 rounded-full bg-orange-400 inline-block"></span> Expiré (en attente paiement)</span>
                    <span className="flex items-center gap-2"><span className="w-3 h-3 rounded-full bg-red-400 inline-block"></span> Bloqué</span>
                    <span className="text-gray-500 ml-2">— France: <strong>100€/mois</strong> | Algérie: <strong>15 000 DZD/mois</strong></span>
                  </div>
                  <h4 className="font-medium mb-2">Actions :</h4>
                  <div className="flex flex-wrap gap-4 text-sm">
                    <span className="flex items-center gap-1"><Mail className="w-4 h-4 text-purple-600" /> Envoyer lien Stripe</span>
                    <span className="flex items-center gap-1"><RotateCcw className="w-4 h-4 text-blue-600" /> Renouveler gratis</span>
                    <span className="flex items-center gap-1"><Bell className="w-4 h-4 text-amber-600" /> Envoyer un rappel</span>
                    <span className="flex items-center gap-1"><Ban className="w-4 h-4 text-red-600" /> Bloquer l'accès</span>
                    <span className="flex items-center gap-1"><CheckCircle className="w-4 h-4 text-emerald-600" /> Débloquer</span>
                    <span className="flex items-center gap-1"><XCircle className="w-4 h-4 text-red-600" /> Annuler l'abonnement</span>
                    <span className="flex items-center gap-1 text-base">🇫🇷/🇩🇿 Changer pays</span>
                  </div>
                </div>

                {/* === SECTION ALGÉRIE — Paiements manuels DZD === */}
                {algeriaPayments.length > 0 && (
                  <div className="mt-6">
                    <h3 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2">
                      🇩🇿 Paiements Algérie (Virement / CCP)
                      <Badge className="bg-amber-100 text-amber-700">{algeriaPayments.filter(p => p.status === 'pending').length} en attente</Badge>
                    </h3>
                    <div className="overflow-x-auto rounded-lg border">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="bg-amber-50 border-b">
                            <th className="text-left p-3 font-medium">Partenaire</th>
                            <th className="text-left p-3 font-medium">Type</th>
                            <th className="text-left p-3 font-medium">Montant</th>
                            <th className="text-left p-3 font-medium">Référence</th>
                            <th className="text-left p-3 font-medium">Date</th>
                            <th className="text-left p-3 font-medium">Statut</th>
                            <th className="text-right p-3 font-medium">Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {algeriaPayments.map(pay => (
                            <tr key={pay.id} className={`border-b ${pay.status === 'pending' ? 'bg-amber-50/40' : pay.status === 'approved' ? 'bg-emerald-50/40' : 'bg-red-50/40'}`}>
                              <td className="p-3">
                                <p className="font-medium">{pay.partner_name}</p>
                                <p className="text-xs text-gray-500">{pay.partner_email}</p>
                              </td>
                              <td className="p-3 uppercase font-mono text-xs">{pay.payment_type}</td>
                              <td className="p-3 font-bold text-emerald-700">{parseInt(pay.amount_dzd).toLocaleString('fr-FR')} DZD</td>
                              <td className="p-3 text-gray-600 font-mono text-xs">{pay.reference || '—'}</td>
                              <td className="p-3 text-gray-500 text-xs">{new Date(pay.created_at).toLocaleDateString('fr-FR')}</td>
                              <td className="p-3">
                                {pay.status === 'pending' ? (
                                  <Badge className="bg-amber-100 text-amber-700">En attente</Badge>
                                ) : pay.status === 'approved' ? (
                                  <Badge className="bg-emerald-100 text-emerald-700">Validé</Badge>
                                ) : (
                                  <Badge className="bg-red-100 text-red-700">Rejeté</Badge>
                                )}
                              </td>
                              <td className="p-3 text-right">
                                {pay.status === 'pending' && (
                                  <div className="flex justify-end gap-2">
                                    <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs" onClick={() => validateAlgeriaPayment(pay.id, 'approved')}>
                                      <CheckCircle className="w-3 h-3 mr-1" /> Valider
                                    </Button>
                                    <Button size="sm" variant="outline" className="text-red-600 border-red-200 text-xs" onClick={() => {
                                      const note = window.prompt('Motif du rejet (optionnel) :') || '';
                                      validateAlgeriaPayment(pay.id, 'rejected', note);
                                    }}>
                                      <X className="w-3 h-3 mr-1" /> Rejeter
                                    </Button>
                                  </div>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* PROSPECTION PROPRIO TAB */}
          <TabsContent value="prospection">
            <div className="space-y-6">
              {/* Stats Cards */}
              {prospectionStats && (
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <Card className="bg-gradient-to-br from-purple-500 to-purple-600 text-white">
                    <CardContent className="p-6">
                      <div className="flex items-center gap-4">
                        <MapPin className="w-8 h-8 opacity-80" />
                        <div>
                          <p className="text-2xl font-bold">{prospectionStats.total_demandes}</p>
                          <p className="text-sm opacity-80">Total demandes</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                  <Card className="bg-gradient-to-br from-yellow-500 to-orange-500 text-white">
                    <CardContent className="p-6">
                      <div className="flex items-center gap-4">
                        <Clock className="w-8 h-8 opacity-80" />
                        <div>
                          <p className="text-2xl font-bold">{prospectionStats.pending + prospectionStats.in_progress}</p>
                          <p className="text-sm opacity-80">En cours</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                  <Card className="bg-gradient-to-br from-green-500 to-emerald-600 text-white">
                    <CardContent className="p-6">
                      <div className="flex items-center gap-4">
                        <CheckCircle className="w-8 h-8 opacity-80" />
                        <div>
                          <p className="text-2xl font-bold">{prospectionStats.completed}</p>
                          <p className="text-sm opacity-80">Terminées</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                  <Card className="bg-gradient-to-br from-blue-500 to-indigo-600 text-white">
                    <CardContent className="p-6">
                      <div className="flex items-center gap-4">
                        <Euro className="w-8 h-8 opacity-80" />
                        <div>
                          <p className="text-2xl font-bold">{formatPrice(prospectionStats.revenue_ttc)}</p>
                          <p className="text-sm opacity-80">CA TTC</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              )}

              {/* Filter */}
              <div className="flex gap-2">
                {['all', 'pending', 'in_progress', 'completed'].map((filter) => (
                  <Button
                    key={filter}
                    variant={prospectionFilter === filter ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setProspectionFilter(filter)}
                    className={prospectionFilter === filter ? 'bg-purple-600' : ''}
                  >
                    {filter === 'all' && 'Toutes'}
                    {filter === 'pending' && 'En attente'}
                    {filter === 'in_progress' && 'En cours'}
                    {filter === 'completed' && 'Terminées'}
                  </Button>
                ))}
              </div>

              {/* Demandes List */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <MapPin className="w-5 h-5 text-purple-600" />
                    Demandes de Propriétaires
                    <InfoTooltip text="Demandes Radar Foncier payées (49€) avec code RAD-2026-XXXXX. Ton job : identifier le propriétaire. Statut orange 'Sans plan' = client en attente. Clique 'Envoyer plan' pour tout automatiser." />
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {prospectionDemandes.filter(d => prospectionFilter === 'all' || d.status === prospectionFilter).length === 0 ? (
                    <p className="text-center text-muted-foreground py-8">Aucune demande</p>
                  ) : (
                    <div className="space-y-3">
                      {prospectionDemandes
                        .filter(d => prospectionFilter === 'all' || d.status === prospectionFilter)
                        .map((demande) => (
                        <div
                          key={demande.id}
                          className="border rounded-lg p-4 hover:bg-gray-50 cursor-pointer transition-colors"
                          onClick={() => {
                            setSelectedProspectionDemande(demande);
                            setShowProspectionModal(true);
                            setProprietaireForm(demande.proprietaire || { nom: '', prenom: '', adresse: '', ville: '', code_postal: '' });
                            // Fetch mairie info
                            if (demande.parcelle?.code_insee) {
                              axios.get(`${API}/admin/prospection/mairie-email/${demande.parcelle.code_insee}`)
                                .then(res => setMairieInfo(res.data))
                                .catch(() => setMairieInfo(null));
                            }
                          }}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-4">
                              <div className={`w-3 h-3 rounded-full ${
                                demande.status === 'completed' ? 'bg-green-500' :
                                demande.status === 'in_progress' ? 'bg-blue-500' : 'bg-yellow-500'
                              }`} />
                              <div>
                                <p className="font-medium">
                                  {demande.parcelle?.section}-{demande.parcelle?.numero}, {demande.parcelle?.commune}
                                </p>
                                <p className="text-sm text-muted-foreground">
                                  {demande.client?.email} • {formatDate(demande.paid_at)}
                                </p>
                              </div>
                            </div>
                            <div className="flex items-center gap-4">
                              {demande.message_count > 0 && (
                                <Badge variant="outline" className="gap-1">
                                  <MessageCircle className="w-3 h-3" /> {demande.message_count}
                                </Badge>
                              )}
                              <Badge className={
                                demande.status === 'completed' ? 'bg-green-100 text-green-700' :
                                demande.status === 'in_progress' ? 'bg-blue-100 text-blue-700' : 'bg-yellow-100 text-yellow-700'
                              }>
                                {demande.status === 'completed' ? 'Terminé' :
                                 demande.status === 'in_progress' ? 'En cours' : 'En attente'}
                              </Badge>
                              <InfoTooltip text="En attente = envoyer le plan cadastral | En cours = plan envoyé, recherche propriétaire | Terminé = dossier clôturé" size={11} />
                              <span className="font-semibold text-purple-600">49 €</span>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* CONSEIL STRATEGIQUE TAB */}
          <TabsContent value="conseil">
            <div className="space-y-6">
              {/* Stats */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <Card>
                  <CardContent className="pt-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-muted-foreground">Total demandes</p>
                        <p className="text-2xl font-bold text-navy">{conseilRequests.length}</p>
                      </div>
                      <Award className="w-8 h-8 text-indigo-500" />
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-muted-foreground">Payées</p>
                        <p className="text-2xl font-bold text-emerald-600">{conseilRequests.filter(c => c.status === 'paid').length}</p>
                      </div>
                      <CheckCircle className="w-8 h-8 text-emerald-500" />
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-muted-foreground">CA Généré</p>
                        <p className="text-2xl font-bold text-gold">{formatPrice(conseilRequests.filter(c => c.status === 'paid').length * 99)}</p>
                      </div>
                      <Euro className="w-8 h-8 text-gold" />
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-muted-foreground">En attente</p>
                        <p className="text-2xl font-bold text-yellow-600">{conseilRequests.filter(c => c.status === 'pending_payment').length}</p>
                      </div>
                      <Clock className="w-8 h-8 text-yellow-500" />
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* List */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Award className="w-5 h-5 text-indigo-600" />
                    Demandes de Conseil Stratégique
                    <InfoTooltip text="Demandes payées (99€) avec code CON-2026-XXXXX. Le client peut uploader des documents. Tu réponds via le chat interne. Les RDV sont pris via le calendrier interne." />
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {conseilRequests.length === 0 ? (
                    <div className="text-center py-12 text-muted-foreground">
                      <Award className="w-12 h-12 mx-auto mb-4 opacity-30" />
                      <p>Aucune demande de conseil pour le moment</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {conseilRequests.map((conseil) => (
                        <div
                          key={conseil.id}
                          className="p-4 border rounded-xl hover:border-indigo-300 hover:bg-indigo-50/30 cursor-pointer transition-all"
                          onClick={() => {
                            setSelectedConseil(conseil);
                            setShowConseilModal(true);
                          }}
                        >
                          <div className="flex items-start justify-between gap-4">
                            <div className="flex-1">
                              <div className="flex items-center gap-3 mb-2">
                                <Badge className={
                                  conseil.status === 'paid' ? 'bg-emerald-100 text-emerald-700' :
                                  conseil.status === 'completed' ? 'bg-blue-100 text-blue-700' :
                                  'bg-yellow-100 text-yellow-700'
                                }>
                                  {conseil.status === 'paid' ? 'Payé' : conseil.status === 'completed' ? 'Terminé' : 'En attente'}
                                </Badge>
                                <InfoTooltip text="En attente = pas encore payé (abandon possible) | Payé = 99€ reçus, à traiter | Terminé = consultation effectuée" size={11} />
                                <Badge variant="outline">{conseil.conseil_type_label}</Badge>
                              </div>
                              <p className="font-medium text-navy">{conseil.email}</p>
                              <p className="text-sm text-muted-foreground">{conseil.phone}</p>
                              <p className="text-sm text-navy/70 mt-2 line-clamp-2">{conseil.description}</p>
                            </div>
                            <div className="text-right">
                              <p className="text-xs text-muted-foreground">
                                {new Date(conseil.created_at).toLocaleDateString('fr-FR')}
                              </p>
                              <p className="font-bold text-gold mt-1">99€</p>
                              {conseil.files && conseil.files.length > 0 && (
                                <Badge variant="outline" className="mt-2">
                                  <FileText className="w-3 h-3 mr-1" />
                                  {conseil.files.length} doc{conseil.files.length > 1 ? 's' : ''}
                                </Badge>
                              )}
                              {conseil.messages && conseil.messages.length > 0 && (
                                <Badge variant="outline" className="mt-2 ml-1">
                                  <MessageCircle className="w-3 h-3 mr-1" />
                                  {conseil.messages.length}
                                </Badge>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Modal détail conseil */}
            {showConseilModal && selectedConseil && (
              <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                <Card className="w-full max-w-3xl max-h-[90vh] overflow-y-auto">
                  <CardHeader className="flex flex-row items-center justify-between sticky top-0 bg-white z-10 border-b">
                    <div>
                      <CardTitle className="flex items-center gap-2">
                        <Award className="w-5 h-5 text-indigo-600" />
                        {selectedConseil.conseil_type_label}
                      </CardTitle>
                      <p className="text-sm text-muted-foreground mt-1">{selectedConseil.email} • {selectedConseil.phone}</p>
                    </div>
                    <Button variant="ghost" size="sm" onClick={() => setShowConseilModal(false)}>
                      <X className="w-5 h-5" />
                    </Button>
                  </CardHeader>
                  <CardContent className="space-y-6 pt-6">
                    {/* Description */}
                    <div>
                      <h4 className="font-semibold text-navy mb-2">Problématique du client</h4>
                      <div className="p-4 bg-gray-50 rounded-lg">
                        <p className="text-navy/80">{selectedConseil.description}</p>
                      </div>
                    </div>

                    {/* Documents */}
                    {selectedConseil.files && selectedConseil.files.length > 0 && (
                      <div>
                        <h4 className="font-semibold text-navy mb-2 flex items-center gap-2">
                          <FileText className="w-4 h-4" />
                          Documents ({selectedConseil.files.length})
                        </h4>
                        <div className="space-y-2">
                          {selectedConseil.files.map((file) => (
                            <div key={file.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                              <div className="flex items-center gap-3">
                                <FileText className="w-5 h-5 text-indigo-600" />
                                <div>
                                  <p className="font-medium text-sm">{file.original_name}</p>
                                  <p className="text-xs text-muted-foreground">{(file.size / 1024).toFixed(0)} Ko</p>
                                </div>
                              </div>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => window.open(`${API}/conseil/${selectedConseil.id}/files/${file.id}`, '_blank')}
                              >
                                <Download className="w-4 h-4 mr-1" />
                                Ouvrir
                              </Button>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Messages / Ticket */}
                    <div>
                      <h4 className="font-semibold text-navy mb-2 flex items-center gap-2">
                        <MessageCircle className="w-4 h-4" />
                        Échanges
                      </h4>
                      <div className="border rounded-lg p-4 max-h-[300px] overflow-y-auto space-y-3 bg-gray-50">
                        {selectedConseil.messages && selectedConseil.messages.length > 0 ? (
                          selectedConseil.messages.map((msg) => (
                            <div
                              key={msg.id}
                              className={`flex ${msg.sender === 'admin' ? 'justify-start' : 'justify-end'}`}
                            >
                              <div className={`max-w-[80%] p-3 rounded-xl ${
                                msg.sender === 'admin'
                                  ? 'bg-indigo-600 text-white rounded-tl-none'
                                  : 'bg-white border text-navy rounded-tr-none'
                              }`}>
                                <p className="text-sm">{msg.content}</p>
                                <p className={`text-xs mt-1 ${msg.sender === 'admin' ? 'text-indigo-200' : 'text-muted-foreground'}`}>
                                  {msg.sender_name} • {new Date(msg.created_at).toLocaleString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                                </p>
                              </div>
                            </div>
                          ))
                        ) : (
                          <p className="text-center text-muted-foreground py-8">Aucun message</p>
                        )}
                      </div>
                      
                      {/* Envoyer un message */}
                      <div className="flex gap-2 mt-3">
                        <Input
                          value={newConseilMessage}
                          onChange={(e) => setNewConseilMessage(e.target.value)}
                          placeholder="Écrire un message au client..."
                          onKeyPress={(e) => {
                            if (e.key === 'Enter' && newConseilMessage.trim()) {
                              // Send message
                              setSendingConseilMessage(true);
                              axios.post(`${API}/admin/conseil-requests/${selectedConseil.id}/message?content=${encodeURIComponent(newConseilMessage)}`)
                                .then(() => {
                                  setNewConseilMessage('');
                                  fetchData();
                                  toast.success('Message envoyé');
                                })
                                .catch(() => toast.error('Erreur'))
                                .finally(() => setSendingConseilMessage(false));
                            }
                          }}
                        />
                        <Button
                          onClick={() => {
                            if (!newConseilMessage.trim()) return;
                            setSendingConseilMessage(true);
                            axios.post(`${API}/admin/conseil-requests/${selectedConseil.id}/message?content=${encodeURIComponent(newConseilMessage)}`)
                              .then(() => {
                                setNewConseilMessage('');
                                fetchData();
                                toast.success('Message envoyé');
                              })
                              .catch(() => toast.error('Erreur'))
                              .finally(() => setSendingConseilMessage(false));
                          }}
                          disabled={sendingConseilMessage || !newConseilMessage.trim()}
                          className="bg-indigo-600 hover:bg-indigo-700"
                        >
                          {sendingConseilMessage ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}
          </TabsContent>

          {/* GRATUITÉS TAB */}
          <TabsContent value="gratuites">
            <div className="space-y-6">
              {/* Stats */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <Card>
                  <CardContent className="pt-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-muted-foreground">Démos Partenaires</p>
                        <p className="text-2xl font-bold text-navy">{gratuitesStats?.demo_partners_active || 0} <span className="text-sm font-normal text-muted-foreground">/ {gratuitesStats?.demo_partners_total || 0}</span></p>
                      </div>
                      <UserPlus className="w-8 h-8 text-pink-500" />
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-muted-foreground">Études Gratuites</p>
                        <p className="text-2xl font-bold text-emerald-600">{gratuitesStats?.free_studies_total || 0}</p>
                      </div>
                      <FileText className="w-8 h-8 text-emerald-500" />
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-muted-foreground">Valeur Offerte</p>
                        <p className="text-2xl font-bold text-gold">{formatPrice(gratuitesStats?.total_value_offered || 0)}</p>
                      </div>
                      <Gift className="w-8 h-8 text-gold" />
                    </div>
                  </CardContent>
                </Card>
                <Card className="bg-gradient-to-br from-pink-50 to-purple-50 border-pink-200">
                  <CardContent className="pt-6">
                    <div className="flex flex-col gap-2">
                      <Button
                        onClick={() => setShowCreateDemoModal(true)}
                        className="bg-pink-600 hover:bg-pink-700 text-white w-full"
                      >
                        <UserPlus className="w-4 h-4 mr-2" />
                        Créer Démo Partenaire
                      </Button>
                      <Button
                        onClick={() => setShowCreateStudyModal(true)}
                        variant="outline"
                        className="border-pink-300 text-pink-700 hover:bg-pink-50 w-full"
                      >
                        <Plus className="w-4 h-4 mr-2" />
                        Créer Étude Gratuite
                      </Button>
                      <Button
                        onClick={async () => {
                          setGeneratingFreeLink(true);
                          try {
                            const res = await axios.post(`${API}/admin/generate-free-audit-token`);
                            const url = `${window.location.origin}/?mode=audit&free_token=${res.data.token}`;
                            setGeneratedFreeLink(url);
                            await navigator.clipboard.writeText(url).catch(() => {});
                            toast.success('Lien copié dans le presse-papier !');
                          } catch (e) {
                            toast.error('Erreur lors de la génération du lien');
                          } finally {
                            setGeneratingFreeLink(false);
                          }
                        }}
                        disabled={generatingFreeLink}
                        variant="outline"
                        className="border-emerald-400 text-emerald-700 hover:bg-emerald-50 w-full"
                      >
                        <Link2 className="w-4 h-4 mr-2" />
                        {generatingFreeLink ? 'Génération...' : 'Générer lien audit gratuit'}
                      </Button>
                      {generatedFreeLink && (
                        <div className="mt-2 p-3 bg-emerald-50 border border-emerald-200 rounded-lg">
                          <p className="text-xs text-emerald-700 font-semibold mb-1">Lien valable 7 jours (usage unique) :</p>
                          <div className="flex gap-2 items-start">
                            <code className="text-xs text-emerald-800 break-all flex-1 bg-white p-2 rounded border border-emerald-200">{generatedFreeLink}</code>
                            <Button size="sm" variant="outline" className="shrink-0 border-emerald-400 text-emerald-700"
                              onClick={() => { navigator.clipboard.writeText(generatedFreeLink); toast.success('Copié !'); }}>
                              <Copy className="w-3 h-3" />
                            </Button>
                          </div>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Demo Partners List */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <UserPlus className="w-5 h-5 text-pink-600" />
                    Comptes Démo Partenaires ({demoPartners.length})
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {demoPartners.length === 0 ? (
                    <div className="text-center py-12 text-muted-foreground">
                      <UserPlus className="w-12 h-12 mx-auto mb-4 opacity-30" />
                      <p>Aucun compte démo</p>
                      <p className="text-sm mt-1">Créez un compte démo pour vos prospects partenaires</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {demoPartners.map((partner) => (
                        <div
                          key={partner.id}
                          className={`p-4 border rounded-xl transition-all ${partner.is_expired ? 'bg-red-50 border-red-200' : 'hover:border-pink-300 hover:bg-pink-50/30'}`}
                        >
                          <div className="flex items-start justify-between gap-4">
                            <div className="flex-1">
                              <div className="flex items-center gap-3 mb-2">
                                <Badge className={partner.is_expired ? 'bg-red-100 text-red-700' : 'bg-emerald-100 text-emerald-700'}>
                                  {partner.is_expired ? 'Expiré' : `${partner.days_remaining}j restants`}
                                </Badge>
                                <span className="font-semibold text-navy">{partner.company_name}</span>
                              </div>
                              <p className="text-sm text-muted-foreground">{partner.email} • {partner.phone}</p>
                              {partner.demo_note && <p className="text-xs text-navy/60 mt-1 italic">Note: {partner.demo_note}</p>}
                            </div>
                            <div className="flex items-center gap-2">
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={async () => {
                                  try {
                                    await axios.post(`${API}/admin/demo-partner/${partner.id}/extend?days=7`);
                                    toast.success('+7 jours ajoutés');
                                    fetchData();
                                  } catch (e) {
                                    toast.error('Erreur');
                                  }
                                }}
                                className="text-emerald-600 border-emerald-300"
                              >
                                <Plus className="w-3 h-3 mr-1" />7j
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={async () => {
                                  if (window.confirm('Convertir en compte payant (offert) ?')) {
                                    try {
                                      await axios.post(`${API}/admin/demo-partner/${partner.id}/convert`);
                                      toast.success('Converti en partenaire actif');
                                      fetchData();
                                    } catch (e) {
                                      toast.error('Erreur');
                                    }
                                  }
                                }}
                                className="text-blue-600 border-blue-300"
                              >
                                <Check className="w-3 h-3 mr-1" />Convertir
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={async () => {
                                  if (window.confirm('Supprimer ce compte démo ?')) {
                                    try {
                                      await axios.delete(`${API}/admin/demo-partner/${partner.id}`);
                                      toast.success('Compte supprimé');
                                      fetchData();
                                    } catch (e) {
                                      toast.error('Erreur');
                                    }
                                  }
                                }}
                                className="text-red-600 border-red-300"
                              >
                                <Trash2 className="w-3 h-3" />
                              </Button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Free Studies List */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <FileText className="w-5 h-5 text-emerald-600" />
                    Études Gratuites ({freeStudies.length})
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {freeStudies.length === 0 ? (
                    <div className="text-center py-12 text-muted-foreground">
                      <FileText className="w-12 h-12 mx-auto mb-4 opacity-30" />
                      <p>Aucune étude gratuite</p>
                      <p className="text-sm mt-1">Créez des études pour vos propres besoins</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {freeStudies.map((study) => (
                        <div
                          key={study.id}
                          className="p-4 border rounded-xl hover:border-emerald-300 hover:bg-emerald-50/30 transition-all"
                        >
                          <div className="flex items-start justify-between gap-4">
                            <div className="flex-1">
                              <div className="flex items-center gap-3 mb-2">
                                <Badge className="bg-emerald-100 text-emerald-700">{study.type_label}</Badge>
                                <Badge variant="outline" className="text-gold border-gold">{formatPrice(study.normal_price)} offert</Badge>
                              </div>
                              <p className="font-medium text-navy">{study.client_name || study.client_email}</p>
                              <p className="text-sm text-muted-foreground">{study.client_email} • {study.client_phone}</p>
                              {study.description && <p className="text-xs text-navy/60 mt-1 line-clamp-1">{study.description}</p>}
                            </div>
                            <div className="flex items-center gap-2">
                              <p className="text-xs text-muted-foreground">
                                {new Date(study.created_at).toLocaleDateString('fr-FR')}
                              </p>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={async () => {
                                  if (window.confirm('Supprimer cette étude gratuite ?')) {
                                    try {
                                      await axios.delete(`${API}/admin/free-study/${study.id}`);
                                      toast.success('Étude supprimée');
                                      fetchData();
                                    } catch (e) {
                                      toast.error('Erreur');
                                    }
                                  }
                                }}
                                className="text-red-600 border-red-300"
                              >
                                <Trash2 className="w-3 h-3" />
                              </Button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Modal Créer Démo Partenaire */}
            {showCreateDemoModal && (
              <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                <Card className="w-full max-w-lg">
                  <CardHeader className="flex flex-row items-center justify-between">
                    <CardTitle className="flex items-center gap-2">
                      <UserPlus className="w-5 h-5 text-pink-600" />
                      Créer Compte Démo Partenaire
                    </CardTitle>
                    <Button variant="ghost" size="sm" onClick={() => setShowCreateDemoModal(false)}>
                      <X className="w-5 h-5" />
                    </Button>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div>
                      <Label>Nom commercial *</Label>
                      <Input
                        value={demoForm.company_name}
                        onChange={(e) => setDemoForm({ ...demoForm, company_name: e.target.value })}
                        placeholder="Ex: Immobilier ABC"
                      />
                    </div>
                    <div>
                      <Label>Email *</Label>
                      <Input
                        type="email"
                        value={demoForm.email}
                        onChange={(e) => setDemoForm({ ...demoForm, email: e.target.value })}
                        placeholder="contact@entreprise.fr"
                      />
                    </div>
                    <div>
                      <Label>Téléphone</Label>
                      <Input
                        value={demoForm.phone}
                        onChange={(e) => setDemoForm({ ...demoForm, phone: e.target.value })}
                        placeholder="06 12 34 56 78"
                      />
                    </div>
                    <div>
                      <Label>Durée de l'essai</Label>
                      <div className="flex gap-2 mt-2">
                        {[1, 7, 14, 30].map((days) => (
                          <Button
                            key={days}
                            type="button"
                            variant={demoForm.duration_days === days ? 'default' : 'outline'}
                            onClick={() => setDemoForm({ ...demoForm, duration_days: days })}
                            className={demoForm.duration_days === days ? 'bg-pink-600' : ''}
                          >
                            {days} jours
                          </Button>
                        ))}
                      </div>
                    </div>
                    <div>
                      <Label>Note interne (optionnel)</Label>
                      <Input
                        value={demoForm.note}
                        onChange={(e) => setDemoForm({ ...demoForm, note: e.target.value })}
                        placeholder="Ex: Rencontré au salon immobilier"
                      />
                    </div>
                    <Button
                      onClick={async () => {
                        if (!demoForm.company_name || !demoForm.email) {
                          toast.error('Remplissez les champs obligatoires');
                          return;
                        }
                        setCreatingDemo(true);
                        try {
                          const res = await axios.post(`${API}/admin/demo-partner/create`, demoForm);
                          setShowCreateDemoModal(false);
                          setDemoForm({ company_name: '', email: '', phone: '', duration_days: 14, note: '' });
                          setDemoCreatedResult(res.data);
                          fetchData();
                        } catch (e) {
                          toast.error(e.response?.data?.detail || 'Erreur lors de la création');
                        } finally {
                          setCreatingDemo(false);
                        }
                      }}
                      disabled={creatingDemo}
                      className="w-full bg-pink-600 hover:bg-pink-700"
                    >
                      {creatingDemo ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <UserPlus className="w-4 h-4 mr-2" />}
                      Créer le compte démo
                    </Button>
                  </CardContent>
                </Card>
              </div>
            )}

            {/* Modal Résultat Demo Partenaire créé */}
            {demoCreatedResult && (
              <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
                <Card className="w-full max-w-md border-2 border-emerald-400">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-emerald-700">
                      <CheckCircle2 className="w-5 h-5" />
                      Compte partenaire démo créé !
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <p className="text-sm text-gray-600">Conservez ces identifiants — ils ne seront plus affichés.</p>
                    <div className="space-y-3 bg-gray-50 rounded-lg p-4 border">
                      <div className="flex items-center justify-between">
                        <span className="text-xs text-gray-500 font-medium uppercase">Email</span>
                        <div className="flex items-center gap-2">
                          <code className="text-sm font-mono bg-white px-2 py-0.5 rounded border">{demoCreatedResult.email}</code>
                          <button onClick={() => { navigator.clipboard.writeText(demoCreatedResult.email); toast.success('Copié !'); }} className="text-gray-400 hover:text-gray-700"><Copy className="w-4 h-4" /></button>
                        </div>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-xs text-gray-500 font-medium uppercase">Mot de passe</span>
                        <div className="flex items-center gap-2">
                          <code className="text-sm font-mono bg-white px-2 py-0.5 rounded border">{demoCreatedResult.password}</code>
                          <button onClick={() => { navigator.clipboard.writeText(demoCreatedResult.password); toast.success('Copié !'); }} className="text-gray-400 hover:text-gray-700"><Copy className="w-4 h-4" /></button>
                        </div>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-xs text-gray-500 font-medium uppercase">URL partenaire</span>
                        <div className="flex items-center gap-2">
                          <code className="text-xs font-mono bg-white px-2 py-0.5 rounded border text-blue-600">/p/{demoCreatedResult.subdomain}</code>
                          <button onClick={() => { navigator.clipboard.writeText(`${window.location.origin}/p/${demoCreatedResult.subdomain}`); toast.success('Copié !'); }} className="text-gray-400 hover:text-gray-700"><Copy className="w-4 h-4" /></button>
                        </div>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-xs text-gray-500 font-medium uppercase">Expire le</span>
                        <span className="text-sm text-orange-600 font-medium">{new Date(demoCreatedResult.expires_at).toLocaleDateString('fr-FR')}</span>
                      </div>
                    </div>
                    <Button className="w-full bg-emerald-600 hover:bg-emerald-700" onClick={() => setDemoCreatedResult(null)}>
                      Compris, fermer
                    </Button>
                  </CardContent>
                </Card>
              </div>
            )}

            {/* Modal Créer Étude Gratuite */}
            {showCreateStudyModal && (
              <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                <Card className="w-full max-w-lg">
                  <CardHeader className="flex flex-row items-center justify-between">
                    <CardTitle className="flex items-center gap-2">
                      <Gift className="w-5 h-5 text-emerald-600" />
                      Créer Étude Gratuite
                    </CardTitle>
                    <Button variant="ghost" size="sm" onClick={() => setShowCreateStudyModal(false)}>
                      <X className="w-5 h-5" />
                    </Button>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div>
                      <Label>Type d'étude *</Label>
                      <div className="grid grid-cols-2 gap-2 mt-2">
                        {[
                          { value: 'audit_flash', label: 'Audit Flash', price: 97 },
                          { value: 'audit_pro', label: 'Audit Pro', price: 247 },
                          { value: 'audit_master', label: 'Audit Master', price: 497 },
                          { value: 'radar', label: 'Radar Foncier', price: 49 },
                          { value: 'conseil', label: 'Conseil Stratégique', price: 99 }
                        ].map((type) => (
                          <Button
                            key={type.value}
                            type="button"
                            variant={studyForm.study_type === type.value ? 'default' : 'outline'}
                            onClick={() => setStudyForm({ ...studyForm, study_type: type.value })}
                            className={`justify-between ${studyForm.study_type === type.value ? 'bg-emerald-600' : ''}`}
                          >
                            <span>{type.label}</span>
                            <Badge variant="secondary" className="ml-2">{type.price}€</Badge>
                          </Button>
                        ))}
                      </div>
                    </div>
                    <div>
                      <Label>Email client *</Label>
                      <Input
                        type="email"
                        value={studyForm.client_email}
                        onChange={(e) => setStudyForm({ ...studyForm, client_email: e.target.value })}
                        placeholder="client@email.com"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>Nom client</Label>
                        <Input
                          value={studyForm.client_name}
                          onChange={(e) => setStudyForm({ ...studyForm, client_name: e.target.value })}
                          placeholder="Jean Dupont"
                        />
                      </div>
                      <div>
                        <Label>Téléphone</Label>
                        <Input
                          value={studyForm.client_phone}
                          onChange={(e) => setStudyForm({ ...studyForm, client_phone: e.target.value })}
                          placeholder="06 12 34 56 78"
                        />
                      </div>
                    </div>
                    <div>
                      <Label>Description / Notes</Label>
                      <textarea
                        value={studyForm.description}
                        onChange={(e) => setStudyForm({ ...studyForm, description: e.target.value })}
                        placeholder="Détails sur l'étude..."
                        className="w-full p-3 border rounded-lg min-h-[80px] resize-none"
                      />
                    </div>
                    <Button
                      onClick={async () => {
                        if (!studyForm.client_email) {
                          toast.error('Email client requis');
                          return;
                        }
                        setCreatingStudy(true);
                        try {
                          await axios.post(`${API}/admin/free-study/create`, studyForm);
                          toast.success('Étude créée !');
                          setShowCreateStudyModal(false);
                          setStudyForm({ study_type: 'audit_flash', client_email: '', client_phone: '', client_name: '', description: '' });
                          fetchData();
                        } catch (e) {
                          toast.error(e.response?.data?.detail || 'Erreur');
                        } finally {
                          setCreatingStudy(false);
                        }
                      }}
                      disabled={creatingStudy}
                      className="w-full bg-emerald-600 hover:bg-emerald-700"
                    >
                      {creatingStudy ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Gift className="w-4 h-4 mr-2" />}
                      Créer l'étude gratuite
                    </Button>
                  </CardContent>
                </Card>
              </div>
            )}
          </TabsContent>

          {/* ESTIMATION COEFS TAB */}
          <TabsContent value="estimation-coefs">
            <EstimationCoefsTab />
          </TabsContent>

          {/* PRODUCT CATALOG TAB */}
          <TabsContent value="product-catalog">
            <ProductCatalogTab />
          </TabsContent>

          {/* GUIDE COMPLET TAB */}
          <TabsContent value="guide-complet">
            <GuideCompletTab />
          </TabsContent>

          {/* PAYMENT ERRORS TAB */}
          <TabsContent value="payment-errors">
            <PaymentErrorsTab />
          </TabsContent>

          {/* LOAN RATES TAB */}
          <TabsContent value="loan-rates">
            <LoanRatesTab />
          </TabsContent>

          {/* PURGE TAB */}
          <TabsContent value="purge">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="flex items-center gap-2 text-red-700"><Trash2 className="w-5 h-5" /> Purge des Données</CardTitle>
                    <p className="text-sm text-gray-500 mt-1">Supprimez les données de test avant la mise en production. Irréversible.</p>
                  </div>
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={fetchPurgeCollections} data-testid="refresh-purge">
                      <RefreshCw className="w-4 h-4 mr-1" /> Rafraîchir
                    </Button>
                    <Button variant="destructive" size="sm" onClick={purgeAll} disabled={purgeLoading} data-testid="purge-all-btn">
                      <Trash2 className="w-4 h-4 mr-1" /> TOUT SUPPRIMER
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {purgeCollections.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    <p>Chargement des collections...</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <div className="grid grid-cols-12 gap-2 px-3 py-2 bg-gray-50 rounded font-medium text-sm">
                      <div className="col-span-5">Collection</div>
                      <div className="col-span-3 text-right">Documents</div>
                      <div className="col-span-4 text-right">Action</div>
                    </div>
                    {purgeCollections.map((coll) => (
                      <div key={coll.key} className={`grid grid-cols-12 gap-2 px-3 py-2 rounded items-center hover:bg-gray-50 ${coll.count > 0 ? 'border border-gray-200' : 'opacity-50'}`}>
                        <div className="col-span-5">
                          <p className="font-medium text-sm">{coll.label}</p>
                          <p className="text-xs text-gray-400">{coll.collection}</p>
                        </div>
                        <div className="col-span-3 text-right">
                          <Badge className={coll.count > 0 ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-400'}>
                            {coll.count} docs
                          </Badge>
                        </div>
                        <div className="col-span-4 text-right">
                          {coll.count > 0 ? (
                            <Button 
                              variant="outline" 
                              size="sm" 
                              className="text-red-600 border-red-200 hover:bg-red-50"
                              onClick={() => purgeSingleCollection(coll.key, coll.label)}
                              disabled={purgeLoading}
                              data-testid={`purge-${coll.key}`}
                            >
                              <Trash2 className="w-3 h-3 mr-1" /> Supprimer
                            </Button>
                          ) : (
                            <span className="text-xs text-gray-400">Vide</span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                <div className="mt-6 p-4 bg-amber-50 border border-amber-200 rounded-lg">
                  <h4 className="font-medium text-amber-800 mb-2 flex items-center gap-1"><AlertTriangle className="w-4 h-4" /> Attention</h4>
                  <ul className="text-sm text-amber-700 space-y-1">
                    <li>La suppression est <strong>définitive et irréversible</strong>.</li>
                    <li>Pour "TOUT SUPPRIMER" vous devrez taper "SUPPRIMER TOUT" pour confirmer.</li>
                    <li>Les comptes admin ne sont pas affectés par la purge.</li>
                    <li>Pensez à exporter vos données CSV avant de purger si besoin.</li>
                  </ul>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="domaines">
            <DomainRequestsTab token={localStorage.getItem('token')} />
          </TabsContent>

          <TabsContent value="settings">
            <Card>
              <CardHeader>
                <CardTitle>Paramètres Entreprise (Facturation)</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl">
                  <div className="space-y-4">
                    <div>
                      <Label>Nom commercial</Label>
                      <Input value={settings.company_name || ''} onChange={(e) => setSettings({...settings, company_name: e.target.value})} />
                    </div>
                    <div>
                      <Label>Nom de l'entreprise (Raison sociale)</Label>
                      <Input value={settings.company_legal_name || ''} onChange={(e) => setSettings({...settings, company_legal_name: e.target.value})} placeholder="Ex : Votre Monde SARL" />
                    </div>
                    <div>
                      <Label>Forme juridique</Label>
                      <select
                        value={settings.legal_form || ''}
                        onChange={(e) => setSettings({...settings, legal_form: e.target.value})}
                        className="w-full h-9 rounded-md border border-input bg-white px-3 text-sm"
                      >
                        <option value="">-- Sélectionner --</option>
                        <option value="SAS">SAS (Société par Actions Simplifiée)</option>
                        <option value="SASU">SASU (SAS Unipersonnelle)</option>
                        <option value="SARL">SARL (Société à Responsabilité Limitée)</option>
                        <option value="EURL">EURL (SARL Unipersonnelle)</option>
                        <option value="SA">SA (Société Anonyme)</option>
                        <option value="SNC">SNC (Société en Nom Collectif)</option>
                        <option value="SCI">SCI (Société Civile Immobilière)</option>
                        <option value="Micro-entreprise">Micro-entreprise</option>
                        <option value="EI">EI (Entreprise Individuelle)</option>
                      </select>
                    </div>
                    <div>
                      <Label>Capital social (€)</Label>
                      <Input value={settings.capital_social || ''} onChange={(e) => setSettings({...settings, capital_social: e.target.value})} placeholder="Ex : 10 000" />
                    </div>
                    <div>
                      <Label>RCS (Ville + Numéro)</Label>
                      <Input value={settings.rcs_info || ''} onChange={(e) => setSettings({...settings, rcs_info: e.target.value})} placeholder="Ex : Marseille B 123 456 789" />
                    </div>
                    <div>
                      <Label>SIRET</Label>
                      <Input value={settings.siret || ''} onChange={(e) => setSettings({...settings, siret: e.target.value})} placeholder="123 456 789 00012" />
                    </div>
                    <div>
                      <Label>N° TVA Intracommunautaire</Label>
                      <Input value={settings.tva_number || ''} onChange={(e) => setSettings({...settings, tva_number: e.target.value})} placeholder="FR12345678901" />
                    </div>
                    <div>
                      <Label>Adresse (numéro et rue)</Label>
                      <Input value={settings.address || ''} onChange={(e) => setSettings({...settings, address: e.target.value})} placeholder="Ex : 60 Rue François 1er" />
                    </div>
                    <div>
                      <Label>Code Postal / Ville</Label>
                      <Input value={settings.city_postal || ''} onChange={(e) => setSettings({...settings, city_postal: e.target.value})} placeholder="Ex : 75008 Paris" />
                    </div>
                  </div>
                  <div className="space-y-4">
                    <div>
                      <Label>Téléphone</Label>
                      <Input value={settings.phone || ''} onChange={(e) => setSettings({...settings, phone: e.target.value})} />
                    </div>
                    <div>
                      <Label>Email</Label>
                      <Input value={settings.email || ''} onChange={(e) => setSettings({...settings, email: e.target.value})} />
                    </div>
                    <div>
                      <Label>IBAN</Label>
                      <Input value={settings.bank_iban || ''} onChange={(e) => setSettings({...settings, bank_iban: e.target.value})} />
                    </div>
                    <div>
                      <Label>BIC</Label>
                      <Input value={settings.bank_bic || ''} onChange={(e) => setSettings({...settings, bank_bic: e.target.value})} />
                    </div>
                    <div className="p-3 rounded-lg bg-blue-50 border border-blue-200 text-xs text-blue-700 space-y-1">
                      <p className="font-semibold">Mentions légales générées automatiquement</p>
                      <p>Le pied de page de chaque facture PDF contiendra :</p>
                      <p className="italic">{settings.company_name || 'Nom commercial'}{settings.company_legal_name ? ` — ${settings.company_legal_name}` : ''}{settings.legal_form ? ` — ${settings.legal_form}` : ''}{settings.capital_social ? ` au capital de ${settings.capital_social} €` : ''}{settings.siret ? ` — SIRET : ${settings.siret}` : ''}{settings.rcs_info ? ` — RCS ${settings.rcs_info}` : ''}</p>
                      <p className="italic">+ Pénalités de retard (art. L441-10 C. com.) + Indemnité forfaitaire 40 €</p>
                    </div>
                  </div>
                </div>

                {/* Section SPF Emails */}
                <div className="mt-6 p-4 rounded-xl border border-purple-200 bg-purple-50/50">
                  <h3 className="font-semibold text-navy mb-1 flex items-center gap-2">
                    <MapPin className="w-4 h-4 text-purple-600" /> Emails SPF par département
                  </h3>
                  <p className="text-xs text-muted-foreground mb-4">
                    Configurez l'email du bureau SPF pour chaque département. Il apparaîtra automatiquement dans les demandes Radar Proprio.
                  </p>
                  <div className="space-y-2 mb-3">
                    {Object.entries(settings.spf_emails || {}).sort(([a],[b]) => a.localeCompare(b)).map(([dept, email]) => (
                      <div key={dept} className="flex items-center gap-2 bg-white border rounded-lg px-3 py-2">
                        <span className="text-xs font-bold text-purple-700 w-14">Dept. {dept}</span>
                        <span className="text-xs text-gray-600 flex-1 font-mono">{email}</span>
                        <button
                          onClick={() => {
                            const updated = {...(settings.spf_emails || {})};
                            delete updated[dept];
                            setSettings({...settings, spf_emails: updated});
                          }}
                          className="text-red-400 hover:text-red-600 text-xs px-1"
                        >✕</button>
                      </div>
                    ))}
                    {Object.keys(settings.spf_emails || {}).length === 0 && (
                      <p className="text-xs text-muted-foreground italic">Aucun bureau configuré. Ajoutez-en ci-dessous.</p>
                    )}
                  </div>
                  <div className="flex gap-2 items-end">
                    <div className="w-24">
                      <Label className="text-xs">Département</Label>
                      <Input
                        placeholder="Ex: 13"
                        maxLength={3}
                        value={newSpfDept || ''}
                        onChange={(e) => setNewSpfDept(e.target.value.replace(/\D/g, ''))}
                        className="text-sm"
                      />
                    </div>
                    <div className="flex-1">
                      <Label className="text-xs">Email SPF</Label>
                      <Input
                        placeholder="spf.aix-en-provence1@dgfip.finances.gouv.fr"
                        value={newSpfEmail || ''}
                        onChange={(e) => setNewSpfEmail(e.target.value)}
                        className="text-sm font-mono"
                      />
                    </div>
                    <Button
                      size="sm"
                      onClick={() => {
                        if (newSpfDept && newSpfEmail) {
                          setSettings({...settings, spf_emails: {...(settings.spf_emails || {}), [newSpfDept]: newSpfEmail}});
                          setNewSpfDept('');
                          setNewSpfEmail('');
                        }
                      }}
                      className="bg-purple-600 hover:bg-purple-700 text-white"
                    >+ Ajouter</Button>
                  </div>
                  <p className="text-[10px] text-muted-foreground mt-2">
                    Pour trouver l'email d'un nouveau département → cliquez "Trouver le bureau SPF" dans la demande Radar.
                  </p>
                </div>

                <div className="mt-8 p-4 rounded-xl border border-gold/30 bg-gold/5" data-testid="accountant-settings">
                  <h3 className="font-semibold text-navy mb-1 flex items-center gap-2"><FileText className="w-4 h-4 text-gold" /> Envoi automatique au comptable</h3>
                  <p className="text-xs text-muted-foreground mb-4">Chaque 1er du mois, l'export du mois écoulé (CSV + ZIP des factures PDF) est envoyé par email à votre comptable. Vous êtes en copie si l'email société est renseigné.</p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-end">
                    <div>
                      <Label>Email du comptable</Label>
                      <Input type="email" data-testid="accountant-email-input" value={settings.accountant_email || ''} onChange={(e) => setSettings({...settings, accountant_email: e.target.value})} placeholder="comptable@cabinet.fr" />
                    </div>
                    <label className="flex items-center gap-3 cursor-pointer select-none pb-2">
                      <input type="checkbox" data-testid="accountant-auto-send-toggle" className="w-4 h-4 accent-[#F59E0B]" checked={!!settings.accountant_auto_send} onChange={(e) => setSettings({...settings, accountant_auto_send: e.target.checked})} />
                      <span className="text-sm">Activer l'envoi automatique mensuel</span>
                    </label>
                  </div>
                  <div className="flex flex-wrap items-end gap-3 mt-4">
                    <div>
                      <Label className="text-xs">Période à envoyer</Label>
                      <div className="flex items-center gap-2 mt-1">
                        <select data-testid="accountant-year-select" value={accountantPeriod.year} onChange={(e) => setAccountantPeriod({ ...accountantPeriod, year: e.target.value })} className="h-9 rounded-md border border-input bg-white px-2 text-sm">
                          {[0, 1, 2].map(i => { const y = (new Date().getFullYear() - i).toString(); return <option key={y} value={y}>{y}</option>; })}
                        </select>
                        <select data-testid="accountant-month-select" value={accountantPeriod.month} onChange={(e) => setAccountantPeriod({ ...accountantPeriod, month: e.target.value })} className="h-9 rounded-md border border-input bg-white px-2 text-sm">
                          <option value="0">Année entière</option>
                          {['Janvier','Février','Mars','Avril','Mai','Juin','Juillet','Août','Septembre','Octobre','Novembre','Décembre'].map((m, i) => <option key={m} value={String(i + 1)}>{m}</option>)}
                        </select>
                      </div>
                    </div>
                    <Button variant="outline" size="sm" data-testid="send-to-accountant-now-btn" disabled={sendingToAccountant || !settings.accountant_email} onClick={sendToAccountantNow} className="border-navy text-navy h-9">
                      <Send className="w-4 h-4 mr-2" />{sendingToAccountant ? 'Envoi…' : 'Envoyer maintenant'}
                    </Button>
                    {lastAccountantExport && (
                      <span className="text-xs text-muted-foreground pb-2" data-testid="last-accountant-export">
                        Dernier envoi : {lastAccountantExport.period} → {lastAccountantExport.to_email} ({lastAccountantExport.invoice_count} facture{lastAccountantExport.invoice_count > 1 ? 's' : ''}, {new Date(lastAccountantExport.sent_at).toLocaleDateString('fr-FR')})
                      </span>
                    )}
                  </div>
                  <p className="text-[11px] text-muted-foreground mt-2">Plusieurs mois de retard ? Choisissez « Année entière » pour tout envoyer en un seul email, ou envoyez mois par mois.</p>
                </div>
                <div className="mt-6">
                  <Button onClick={updateSettings} className="bg-gold hover:bg-gold/90 text-black">Enregistrer</Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
          </div>
        </div>
      </main>

      {/* Modals */}

      {/* Analyst Modal */}
      <Dialog open={showAnalystModal} onOpenChange={setShowAnalystModal}>
        <DialogContent>
          <DialogHeader><DialogTitle>Nouvel Analyste</DialogTitle></DialogHeader>
          <div className="space-y-4 pt-4">
            <div><Label>Nom *</Label><Input value={newAnalyst.name} onChange={(e) => setNewAnalyst({...newAnalyst, name: e.target.value})} /></div>
            <div><Label>Email *</Label><Input type="email" value={newAnalyst.email} onChange={(e) => setNewAnalyst({...newAnalyst, email: e.target.value})} /></div>
            <div><Label>Téléphone</Label><Input type="tel" value={newAnalyst.phone} onChange={(e) => setNewAnalyst({...newAnalyst, phone: e.target.value})} placeholder="+33 6 12 34 56 78" /></div>
            <div><Label>Mot de passe *</Label><Input type="password" value={newAnalyst.password} onChange={(e) => setNewAnalyst({...newAnalyst, password: e.target.value})} /></div>
            <p className="text-xs text-muted-foreground">L'analyste pourra se connecter sur /analyst avec son email et mot de passe</p>
            <Button onClick={createAnalyst} className="w-full bg-gold hover:bg-gold/90 text-black">Créer l'analyste</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Chiffreur Modal */}
      <Dialog open={showChiffreurModal} onOpenChange={setShowChiffreurModal}>
        <DialogContent>
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Calculator className="w-5 h-5 text-emerald-600" />Nouveau Chiffreur</DialogTitle></DialogHeader>
          <div className="space-y-4 pt-4">
            <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-lg">
              <p className="text-sm text-emerald-800">
                <strong>🔒</strong> Le chiffreur aura accès aux vidéos et photos pour estimer les travaux, 
                mais pas aux données personnelles du client (nom, email, adresse exacte).
              </p>
            </div>
            <div><Label>Nom *</Label><Input value={newChiffreur.name} onChange={(e) => setNewChiffreur({...newChiffreur, name: e.target.value})} placeholder="Jean Dupont" /></div>
            <div><Label>Email *</Label><Input type="email" value={newChiffreur.email} onChange={(e) => setNewChiffreur({...newChiffreur, email: e.target.value})} placeholder="jean.dupont@email.com" /></div>
            <div><Label>Téléphone</Label><Input type="tel" value={newChiffreur.phone} onChange={(e) => setNewChiffreur({...newChiffreur, phone: e.target.value})} placeholder="+33 6 12 34 56 78" /></div>
            <div><Label>Mot de passe *</Label><Input type="password" value={newChiffreur.password} onChange={(e) => setNewChiffreur({...newChiffreur, password: e.target.value})} /></div>
            <p className="text-xs text-muted-foreground">Le chiffreur pourra se connecter sur /chiffreur avec son email et mot de passe</p>
            <Button onClick={createChiffreur} className="w-full bg-emerald-600 hover:bg-emerald-700 text-white">Créer le chiffreur</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Chiffreur Password Modal */}
      <Dialog open={chiffreurPasswordModal.show} onOpenChange={(open) => !open && setChiffreurPasswordModal({ show: false, chiffreur: null, newPassword: '' })}>
        <DialogContent>
          <DialogHeader><DialogTitle>Modifier le mot de passe</DialogTitle></DialogHeader>
          <div className="space-y-4 pt-4">
            <p className="text-sm text-muted-foreground">Chiffreur: {chiffreurPasswordModal.chiffreur?.name}</p>
            <div><Label>Nouveau mot de passe</Label><Input type="password" value={chiffreurPasswordModal.newPassword} onChange={(e) => setChiffreurPasswordModal({...chiffreurPasswordModal, newPassword: e.target.value})} /></div>
            <Button onClick={updateChiffreurPassword} className="w-full">Mettre à jour</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Created Chiffreur Password Modal */}
      {createdChiffreurPassword && (
        <Dialog open={true} onOpenChange={() => setCreatedChiffreurPassword(null)}>
          <DialogContent>
            <DialogHeader><DialogTitle className="text-emerald-600">✓ Chiffreur créé avec succès !</DialogTitle></DialogHeader>
            <div className="space-y-4 pt-4">
              <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-lg">
                <p className="text-sm mb-3">Voici les identifiants de connexion pour <strong>{createdChiffreurPassword.name}</strong> :</p>
                <div className="space-y-2">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-muted-foreground">Email :</span>
                    <code className="bg-white px-2 py-1 rounded text-sm">{createdChiffreurPassword.email}</code>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-muted-foreground">Mot de passe :</span>
                    <code className="bg-emerald-100 text-emerald-800 px-2 py-1 rounded text-sm font-mono">{createdChiffreurPassword.password}</code>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-muted-foreground">URL de connexion :</span>
                    <code className="bg-white px-2 py-1 rounded text-sm">/chiffreur</code>
                  </div>
                </div>
              </div>
              <p className="text-xs text-muted-foreground">Un email avec ces informations a été envoyé au chiffreur.</p>
              <Button onClick={() => setCreatedChiffreurPassword(null)} className="w-full">Fermer</Button>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Viewing Chiffreur Details Modal */}
      <Dialog open={!!viewingChiffreur} onOpenChange={() => setViewingChiffreur(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>Détails du chiffreur</DialogTitle></DialogHeader>
          {viewingChiffreur && (
            <div className="space-y-4 pt-4">
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 rounded-full bg-emerald-500 flex items-center justify-center text-white text-2xl font-bold">
                  {viewingChiffreur.name?.charAt(0)?.toUpperCase()}
                </div>
                <div>
                  <p className="text-xl font-bold">{viewingChiffreur.name}</p>
                  <p className="text-muted-foreground">{viewingChiffreur.email}</p>
                  {viewingChiffreur.phone && <p className="text-sm">{viewingChiffreur.phone}</p>}
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="p-3 bg-emerald-50 rounded-lg text-center">
                  <p className="text-2xl font-bold text-emerald-600">{viewingChiffreur.assigned_projects?.length || 0}</p>
                  <p className="text-xs text-muted-foreground">Dossiers assignés</p>
                </div>
                <div className="p-3 bg-blue-50 rounded-lg text-center">
                  <p className="text-2xl font-bold text-blue-600">{viewingChiffreur.completed_projects || 0}</p>
                  <p className="text-xs text-muted-foreground">Chiffrages terminés</p>
                </div>
              </div>
              {viewingChiffreur.projects?.length > 0 && (
                <div>
                  <p className="font-semibold mb-2">Dossiers en cours :</p>
                  <div className="space-y-2 max-h-40 overflow-y-auto">
                    {viewingChiffreur.projects.map(p => (
                      <div key={p.id} className="p-2 bg-gray-50 rounded text-sm flex justify-between">
                        <span>#{p.id?.slice(-6).toUpperCase()}</span>
                        <Badge variant="outline">{p.chiffrage_status || 'en_cours'}</Badge>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Expense Modal */}
      <Dialog open={showExpenseModal} onOpenChange={setShowExpenseModal}>
        <DialogContent>
          <DialogHeader><DialogTitle>Ajouter une dépense</DialogTitle></DialogHeader>
          <div className="space-y-4 pt-4">
            <div><Label>Description *</Label><Input value={newExpense.description} onChange={(e) => setNewExpense({...newExpense, description: e.target.value})} /></div>
            <div><Label>Catégorie</Label><select className="w-full p-2 border rounded" value={newExpense.category} onChange={(e) => setNewExpense({...newExpense, category: e.target.value})}>{EXPENSE_CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}</select></div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Montant HT *</Label><Input type="number" value={newExpense.amount_ht} onChange={(e) => setNewExpense({...newExpense, amount_ht: e.target.value})} /></div>
              <div><Label>Taux TVA</Label><select className="w-full p-2 border rounded" value={newExpense.tva_rate} onChange={(e) => setNewExpense({...newExpense, tva_rate: parseFloat(e.target.value)})}><option value="0.20">20%</option><option value="0.10">10%</option><option value="0.055">5.5%</option><option value="0">0%</option></select></div>
            </div>
            <div><Label>Fournisseur</Label><Input value={newExpense.supplier_name} onChange={(e) => setNewExpense({...newExpense, supplier_name: e.target.value})} /></div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label>N° Facture</Label><Input value={newExpense.invoice_number} onChange={(e) => setNewExpense({...newExpense, invoice_number: e.target.value})} /></div>
              <div><Label>Date *</Label><Input type="date" value={newExpense.invoice_date} onChange={(e) => setNewExpense({...newExpense, invoice_date: e.target.value})} /></div>
            </div>
            <div>
              <Label>Photo ou PDF de la facture</Label>
              <div className="mt-2">
                {newExpense.file_url ? (
                  <div className="flex items-center gap-3 p-3 bg-emerald-50 rounded-lg border border-emerald-200">
                    {newExpense.file_name?.toLowerCase().endsWith('.pdf') ? (
                      <File className="w-8 h-8 text-red-500" />
                    ) : (
                      <Image className="w-8 h-8 text-blue-500" />
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{newExpense.file_name}</p>
                      <a href={newExpense.file_url} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-600 hover:underline">Voir le fichier</a>
                    </div>
                    <Button variant="ghost" size="sm" onClick={() => setNewExpense({...newExpense, file_url: '', file_name: ''})} className="text-red-500">
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                ) : (
                  <label className="flex items-center justify-center gap-2 p-4 border-2 border-dashed rounded-lg cursor-pointer hover:border-gold hover:bg-gold/5 transition-colors">
                    {expenseFileUploading ? (
                      <RefreshCw className="w-5 h-5 animate-spin text-gold" />
                    ) : (
                      <Upload className="w-5 h-5 text-muted-foreground" />
                    )}
                    <span className="text-sm text-muted-foreground">
                      {expenseFileUploading ? 'Upload en cours...' : 'Cliquez pour uploader une photo ou PDF'}
                    </span>
                    <input 
                      type="file" 
                      className="hidden" 
                      accept="image/*,.pdf"
                      onChange={(e) => e.target.files[0] && uploadExpenseFile(e.target.files[0])}
                      disabled={expenseFileUploading}
                    />
                  </label>
                )}
              </div>
            </div>
            <Button onClick={createExpense} disabled={expenseFileUploading} className="w-full bg-gold hover:bg-gold/90 text-black">Ajouter</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Assign Project Modal */}
      <Dialog open={assignModal.show} onOpenChange={(open) => setAssignModal({ show: open, projectId: null })}>
        <DialogContent>
          <DialogHeader><DialogTitle>Assigner à un analyste</DialogTitle></DialogHeader>
          <div className="space-y-4 pt-4">
            {analysts.length === 0 ? (
              <p className="text-center text-muted-foreground py-4">Créez d'abord un analyste</p>
            ) : (
              <div className="space-y-2">
                {analysts.map(analyst => (
                  <Button key={analyst.id} variant="outline" className="w-full justify-start" onClick={() => assignProjectToAnalyst(assignModal.projectId, analyst.id)}>
                    <UserCheck className="w-4 h-4 mr-2" />{analyst.name} ({analyst.assigned_count || 0} dossiers)
                  </Button>
                ))}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Assign Chiffreur Modal */}
      <Dialog open={assignChiffreurModal.show} onOpenChange={(open) => setAssignChiffreurModal({ show: open, projectId: null })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Calculator className="w-5 h-5 text-emerald-600" />
              Assigner à un chiffreur
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-lg">
              <p className="text-sm text-emerald-800">
                <strong>🔒</strong> Le chiffreur n'aura pas accès aux données personnelles du client (nom, email, adresse exacte).
              </p>
            </div>
            {chiffreurs.length === 0 ? (
              <p className="text-center text-muted-foreground py-4">
                Créez d'abord un chiffreur dans l'onglet "Chiffreurs"
              </p>
            ) : (
              <div className="space-y-2">
                {chiffreurs.map(chiffreur => (
                  <Button 
                    key={chiffreur.id} 
                    variant="outline" 
                    className="w-full justify-start border-emerald-200 hover:bg-emerald-50" 
                    onClick={() => assignProjectToChiffreur(assignChiffreurModal.projectId, chiffreur.id)}
                  >
                    <Calculator className="w-4 h-4 mr-2 text-emerald-600" />
                    {chiffreur.name} 
                    <span className="ml-auto text-xs text-muted-foreground">
                      {chiffreur.assigned_count || 0} dossiers • {chiffreur.completed_projects || 0} terminés
                    </span>
                  </Button>
                ))}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit User Modal */}
      <Dialog open={!!editingUser} onOpenChange={() => setEditingUser(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Modifier l'utilisateur</DialogTitle></DialogHeader>
          {editingUser && (
            <div className="space-y-4 pt-4">
              <div><Label>Nom</Label><Input value={editingUser.name || ''} onChange={(e) => setEditingUser({...editingUser, name: e.target.value})} /></div>
              <div><Label>Téléphone</Label><Input value={editingUser.phone || ''} onChange={(e) => setEditingUser({...editingUser, phone: e.target.value})} /></div>
              <div><Label>Abonnement</Label><select className="w-full p-2 border rounded" value={editingUser.subscription_type || 'free'} onChange={(e) => setEditingUser({...editingUser, subscription_type: e.target.value})}><option value="free">Gratuit</option><option value="flash">Flash</option><option value="pro">Pro</option><option value="master">Master</option></select></div>
              <div><Label>Crédits</Label><Input type="number" value={editingUser.credits || 0} onChange={(e) => setEditingUser({...editingUser, credits: parseInt(e.target.value)})} /></div>
              <Button onClick={() => updateUser(editingUser.id, { name: editingUser.name, phone: editingUser.phone, subscription_type: editingUser.subscription_type, credits: editingUser.credits })} className="w-full bg-gold hover:bg-gold/90 text-black">Enregistrer</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit Project Modal */}
      <Dialog open={!!editingProject} onOpenChange={() => setEditingProject(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>Modifier le dossier</DialogTitle></DialogHeader>
          {editingProject && (
            <div className="space-y-4 pt-4 max-h-[70vh] overflow-y-auto">
              <div><Label>Adresse</Label><Input value={editingProject.address || ''} onChange={(e) => setEditingProject({...editingProject, address: e.target.value})} /></div>
              <div className="grid grid-cols-2 gap-4">
                <div><Label>Surface (m²)</Label><Input type="number" value={editingProject.surface || ''} onChange={(e) => setEditingProject({...editingProject, surface: parseFloat(e.target.value)})} /></div>
                <div><Label>Prix d'achat (€)</Label><Input type="number" value={editingProject.purchase_price || ''} onChange={(e) => setEditingProject({...editingProject, purchase_price: parseFloat(e.target.value)})} /></div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div><Label>Prix revente (€)</Label><Input type="number" value={editingProject.estimated_resale_price || ''} onChange={(e) => setEditingProject({...editingProject, estimated_resale_price: parseFloat(e.target.value)})} /></div>
                <div><Label>Statut</Label><select className="w-full p-2 border rounded" value={editingProject.status || 'pending'} onChange={(e) => setEditingProject({...editingProject, status: e.target.value})}><option value="pending">En attente</option><option value="in_review">En cours</option><option value="analyst_review">Chez analyste</option><option value="pending_validation">À valider</option><option value="completed">Terminé</option><option value="rejected">Refusé</option></select></div>
              </div>
              <div><Label>Score Expert</Label><select className="w-full p-2 border rounded" value={editingProject.expert_score || ''} onChange={(e) => setEditingProject({...editingProject, expert_score: e.target.value})}><option value="">Non évalué</option><option value="green">Vert</option><option value="orange">Orange</option><option value="red">Rouge</option></select></div>
              <div><Label>Notes Expert</Label><textarea className="w-full p-2 border rounded h-24" value={editingProject.expert_notes || ''} onChange={(e) => setEditingProject({...editingProject, expert_notes: e.target.value})} /></div>
              <Button onClick={() => updateProject(editingProject.id, editingProject)} className="w-full bg-gold hover:bg-gold/90 text-black">Enregistrer</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* View User Projects Modal */}
      <Dialog open={!!viewingUser} onOpenChange={() => setViewingUser(null)}>
        <DialogContent className="max-w-4xl">
          <DialogHeader><DialogTitle>Dossiers de {viewingUser?.name || viewingUser?.email}</DialogTitle></DialogHeader>
          {viewingUser && (
            <div className="space-y-4 pt-4 max-h-[70vh] overflow-y-auto">
              <div className="flex gap-4 mb-4 p-4 bg-gray-50 rounded-lg">
                {viewingUser.phone && (
                  <a href={`tel:${viewingUser.phone}`} className="flex items-center gap-2 text-blue-600 hover:underline">
                    <Phone className="w-4 h-4" />{viewingUser.phone}
                  </a>
                )}
                <a href={`mailto:${viewingUser.email}`} className="flex items-center gap-2 text-blue-600 hover:underline">
                  <Mail className="w-4 h-4" />{viewingUser.email}
                </a>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left p-2">Adresse</th>
                      <th className="text-left p-2">Prix</th>
                      <th className="text-left p-2">Statut</th>
                      <th className="text-left p-2">Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {projects.filter(p => p.user_id === viewingUser.id).map(project => {
                      const status = STATUS_CONFIG[project.status] || STATUS_CONFIG.pending;
                      return (
                        <tr key={project.id} className="border-b">
                          <td className="p-2">{project.address}</td>
                          <td className="p-2">{formatPrice(project.purchase_price)}</td>
                          <td className="p-2"><Badge className={status.color}>{status.label}</Badge></td>
                          <td className="p-2 text-sm">{formatDate(project.created_at)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* View Affiliate Modal */}
      <Dialog open={!!viewingAffiliate} onOpenChange={() => setViewingAffiliate(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>Détails Affilié - {viewingAffiliate?.user?.name || viewingAffiliate?.user?.email}</DialogTitle></DialogHeader>
          {viewingAffiliate && (
            <div className="space-y-4 pt-4">
              <div className="grid grid-cols-2 gap-4 p-4 bg-gray-50 rounded-lg">
                <div>
                  <p className="text-sm text-muted-foreground">Code de parrainage</p>
                  <p className="font-mono font-bold text-lg">{viewingAffiliate.code}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Commission</p>
                  <div className="flex items-center gap-2 mt-1">
                    <input
                      type="number" min="1" max="50" step="1"
                      defaultValue={(viewingAffiliate.commission_rate * 100).toFixed(0)}
                      className="w-20 h-8 border rounded px-2 text-sm font-bold"
                      id="affiliate-commission-input"
                    />
                    <span className="text-sm font-medium">%</span>
                    <button
                      className="px-3 py-1 bg-blue-600 text-white text-xs rounded hover:bg-blue-700"
                      onClick={async () => {
                        const val = document.getElementById('affiliate-commission-input').value;
                        const rate = parseFloat(val) / 100;
                        try {
                          await axios.put(`${API}/admin/affiliates/${viewingAffiliate.id}?commission_rate=${rate}`);
                          toast.success(`Commission mise à jour : ${val}%`);
                          fetchData();
                          setViewingAffiliate({...viewingAffiliate, commission_rate: rate});
                        } catch { toast.error('Erreur'); }
                      }}
                    >Sauvegarder</button>
                  </div>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Parrainages</p>
                  <p className="font-bold text-lg">{viewingAffiliate.total_referrals || 0}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Gains totaux</p>
                  <p className="font-bold text-lg text-emerald-600">{formatPrice(viewingAffiliate.total_earnings)}</p>
                </div>
              </div>
              
              <div>
                <h4 className="font-semibold mb-2">Contact</h4>
                <div className="flex gap-4">
                  {viewingAffiliate.user?.email && (
                    <a href={`mailto:${viewingAffiliate.user.email}`} className="flex items-center gap-2 text-blue-600 hover:underline">
                      <Mail className="w-4 h-4" />{viewingAffiliate.user.email}
                    </a>
                  )}
                </div>
              </div>

              <div>
                <h4 className="font-semibold mb-2">Utilisateurs parrainés</h4>
                {viewingAffiliate.referred_users?.length > 0 ? (
                  <div className="space-y-2">
                    {viewingAffiliate.referred_users.map(user => (
                      <div key={user.id} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                        <div>
                          <p className="font-medium">{user.name || user.email}</p>
                          <p className="text-xs text-muted-foreground">{formatDate(user.created_at)}</p>
                        </div>
                        <Badge>{user.subscription_type || 'free'}</Badge>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-muted-foreground text-sm">Aucun utilisateur parrainé pour le moment</p>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* View Analyst Modal */}
      <Dialog open={!!viewingAnalyst} onOpenChange={() => setViewingAnalyst(null)}>
        <DialogContent className="max-w-3xl">
          <DialogHeader><DialogTitle>Analyste - {viewingAnalyst?.name}</DialogTitle></DialogHeader>
          {viewingAnalyst && (
            <div className="space-y-4 pt-4">
              {/* Contact Info */}
              <div className="flex gap-4 p-4 bg-gray-50 rounded-lg">
                <div className="flex-1">
                  <p className="text-sm text-muted-foreground mb-1">Contact</p>
                  <div className="space-y-1">
                    <a href={`mailto:${viewingAnalyst.email}`} className="flex items-center gap-2 text-blue-600 hover:underline">
                      <Mail className="w-4 h-4" />{viewingAnalyst.email}
                    </a>
                    {viewingAnalyst.phone && (
                      <a href={`tel:${viewingAnalyst.phone}`} className="flex items-center gap-2 text-blue-600 hover:underline">
                        <Phone className="w-4 h-4" />{viewingAnalyst.phone}
                      </a>
                    )}
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-sm text-muted-foreground">Performance</p>
                  <p className="text-2xl font-bold">{viewingAnalyst.completed_projects || 0}</p>
                  <p className="text-xs">dossiers terminés</p>
                </div>
              </div>

              {/* Assigned Projects */}
              <div>
                <h4 className="font-semibold mb-3">Dossiers assignés ({viewingAnalyst.projects?.length || 0})</h4>
                {viewingAnalyst.projects?.length > 0 ? (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b text-sm">
                          <th className="text-left p-2">Adresse</th>
                          <th className="text-left p-2">Prix</th>
                          <th className="text-left p-2">Statut</th>
                          <th className="text-left p-2">Score Analyste</th>
                        </tr>
                      </thead>
                      <tbody>
                        {viewingAnalyst.projects.map(project => {
                          const status = STATUS_CONFIG[project.status] || STATUS_CONFIG.pending;
                          return (
                            <tr key={project.id} className="border-b hover:bg-gray-50">
                              <td className="p-2">
                                <p className="font-medium">{project.address}</p>
                                <p className="text-xs text-muted-foreground">{project.surface} m²</p>
                              </td>
                              <td className="p-2">{formatPrice(project.purchase_price)}</td>
                              <td className="p-2"><Badge className={status.color}>{status.label}</Badge></td>
                              <td className="p-2">
                                {project.analyst_score ? (
                                  <div className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium ${
                                    project.analyst_score === 'green' ? 'bg-emerald-100 text-emerald-700' :
                                    project.analyst_score === 'orange' ? 'bg-amber-100 text-amber-700' :
                                    'bg-red-100 text-red-700'
                                  }`}>
                                    {project.analyst_score === 'green' ? 'Validé' : project.analyst_score === 'orange' ? 'Risqué' : 'Refusé'}
                                  </div>
                                ) : (
                                  <span className="text-muted-foreground text-xs">En cours</span>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <p className="text-muted-foreground text-sm text-center py-4">Aucun dossier assigné</p>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* PASSWORD UPDATE MODAL */}
      <Dialog open={passwordModal.show} onOpenChange={(open) => !open && setPasswordModal({ show: false, analyst: null, newPassword: '' })}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Modifier le mot de passe</DialogTitle>
          </DialogHeader>
          {passwordModal.analyst && (
            <div className="space-y-4">
              <div className="p-3 bg-muted rounded-lg">
                <p className="font-medium">{passwordModal.analyst.name}</p>
                <p className="text-sm text-muted-foreground">{passwordModal.analyst.email}</p>
              </div>
              <div>
                <Label>Nouveau mot de passe *</Label>
                <Input 
                  type="text" 
                  value={passwordModal.newPassword} 
                  onChange={(e) => setPasswordModal({...passwordModal, newPassword: e.target.value})}
                  placeholder="Entrez le nouveau mot de passe"
                />
                <p className="text-xs text-muted-foreground mt-1">Minimum 4 caractères</p>
              </div>
              <Button onClick={updateAnalystPassword} className="w-full bg-gold hover:bg-gold/90 text-black">
                Mettre à jour le mot de passe
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* CREATED ANALYST PASSWORD MODAL */}
      <Dialog open={!!createdAnalystPassword} onOpenChange={(open) => !open && setCreatedAnalystPassword(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-emerald-600 flex items-center gap-2">
              <CheckCircle className="w-5 h-5" /> Analyste créé avec succès !
            </DialogTitle>
          </DialogHeader>
          {createdAnalystPassword && (
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">
                Communiquez ces identifiants à votre analyste pour qu'il puisse se connecter :
              </p>
              <div className="p-4 bg-navy/5 border-2 border-navy/20 rounded-lg space-y-3">
                <div>
                  <p className="text-xs text-muted-foreground">Nom</p>
                  <p className="font-medium">{createdAnalystPassword.name}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Email</p>
                  <p className="font-medium font-mono bg-white px-2 py-1 rounded">{createdAnalystPassword.email}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Mot de passe</p>
                  <p className="font-medium font-mono bg-gold/20 px-2 py-1 rounded text-lg">{createdAnalystPassword.password}</p>
                </div>
              </div>
              <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                <p className="text-sm text-amber-800">
                  <strong>URL de connexion :</strong> <span className="font-mono">/analyst</span>
                </p>
              </div>
              <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-lg">
                <p className="text-sm text-emerald-800">
                  ✉️ <strong>Un email a été envoyé</strong> à l'analyste avec ses identifiants.
                </p>
                <p className="text-xs text-emerald-600 mt-1">
                  S'il ne le reçoit pas, dites-lui de vérifier son dossier <strong>SPAM</strong>.
                </p>
              </div>
              <Button onClick={() => setCreatedAnalystPassword(null)} className="w-full">
                J'ai noté les identifiants
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Transfer Modal */}
      <Dialog open={transferModal.show} onOpenChange={(open) => !open && setTransferModal({ show: false, project: null })}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Send className="w-5 h-5 text-emerald-500" />
              Transférer le dossier à un artisan
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            {/* Project Info */}
            {transferModal.project && (
              <div className="p-4 bg-gray-50 rounded-lg">
                <h4 className="font-medium mb-2">Dossier à transférer</h4>
                <p className="text-sm">{transferModal.project.address}</p>
                <p className="text-sm text-gray-500">
                  {transferModal.project.city} {transferModal.project.postal_code} • {transferModal.project.surface} m²
                </p>
              </div>
            )}

            {/* Notes */}
            <div>
              <Label>Notes pour l'artisan (optionnel)</Label>
              <Input 
                value={transferNotes}
                onChange={(e) => setTransferNotes(e.target.value)}
                placeholder="Instructions particulières, délais, etc."
                className="mt-1"
              />
            </div>

            {/* Artisan Selection */}
            <div>
              <Label className="mb-2 block">Sélectionnez un artisan</Label>
              {nearbyArtisans.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <Wrench className="w-12 h-12 mx-auto mb-4 opacity-30" />
                  <p>Aucun artisan validé disponible</p>
                  <p className="text-sm">Validez d'abord des artisans dans l'onglet "Mon Réseau"</p>
                </div>
              ) : (
                <div className="space-y-2 max-h-[300px] overflow-y-auto">
                  {nearbyArtisans.map((artisan) => (
                    <div 
                      key={artisan.id}
                      className="flex items-center justify-between p-3 border rounded-lg hover:bg-gray-50 transition-colors"
                    >
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <p className="font-medium">{artisan.full_name}</p>
                          {artisan.distance_km && (
                            <Badge variant="outline" className="text-xs">
                              {artisan.distance_km} km
                            </Badge>
                          )}
                        </div>
                        <p className="text-sm text-gray-500">{artisan.company_info?.name || artisan.email}</p>
                        {artisan.artisan_info?.trades && (
                          <div className="flex flex-wrap gap-1 mt-1">
                            {artisan.artisan_info.trades.slice(0, 3).map((trade, idx) => (
                              <Badge key={idx} variant="outline" className="text-xs">
                                {trade}
                              </Badge>
                            ))}
                            {artisan.artisan_info.trades.length > 3 && (
                              <Badge variant="outline" className="text-xs">
                                +{artisan.artisan_info.trades.length - 3}
                              </Badge>
                            )}
                          </div>
                        )}
                      </div>
                      <Button
                        size="sm"
                        onClick={() => executeTransfer(artisan.id)}
                        disabled={transferLoading}
                        className="bg-emerald-500 hover:bg-emerald-600"
                      >
                        {transferLoading ? 'Transfert...' : 'Transférer'}
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Prospection Demande Detail Modal */}
      <Dialog open={showProspectionModal} onOpenChange={setShowProspectionModal}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MapPin className="w-5 h-5 text-purple-600" />
              Dossier Prospection #{selectedProspectionDemande?.id?.slice(0, 8)}
            </DialogTitle>
          </DialogHeader>
          
          {selectedProspectionDemande && (
            <div className="space-y-6">
              {/* Bilan financier immédiat */}
              {(() => {
                const ttc = selectedProspectionDemande.amount_ttc || 49;
                const stripe = Math.round((ttc * 0.029 + 0.30) * 100) / 100;
                const spf = selectedProspectionDemande.spf_cost_ht || 15;
                const net = Math.round((ttc - stripe - spf) * 100) / 100;
                return (
                  <div className="grid grid-cols-4 gap-2 p-3 bg-gray-50 rounded-lg border text-center text-sm">
                    <div>
                      <p className="text-xs text-muted-foreground">Encaissé</p>
                      <p className="font-bold text-green-600">+{ttc.toFixed(2)} €</p>
                      <p className="text-xs text-gray-400">TTC client</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">Frais Stripe</p>
                      <p className="font-bold text-red-500">-{stripe.toFixed(2)} €</p>
                      <p className="text-xs text-gray-400">~2,9% + 0,30€</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">Frais SPF</p>
                      <p className="font-bold text-red-500">-{spf.toFixed(2)} €</p>
                      <p className="text-xs text-gray-400">HT=TTC, 0% TVA</p>
                    </div>
                    <div className="bg-white rounded border">
                      <p className="text-xs text-muted-foreground">Gain net</p>
                      <p className="font-bold text-blue-700 text-base">{net.toFixed(2)} €</p>
                      <p className="text-xs text-gray-400">après tout</p>
                    </div>
                  </div>
                );
              })()}
              {/* Status & Client Info */}
              <div className="grid md:grid-cols-2 gap-4">
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm">Client</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="font-medium">{selectedProspectionDemande.client?.email}</p>
                    {selectedProspectionDemande.client?.telephone && (
                      <p className="text-sm text-muted-foreground">{selectedProspectionDemande.client.telephone}</p>
                    )}
                    <p className="text-sm text-green-600 mt-2">
                      Payé le {formatDate(selectedProspectionDemande.paid_at)} - 49€
                    </p>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm">Parcelle recherchée</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="font-medium">
                      {selectedProspectionDemande.parcelle?.commune} ({selectedProspectionDemande.parcelle?.code_postal})
                    </p>
                    <p className="text-sm">
                      Section {selectedProspectionDemande.parcelle?.section} • N° {selectedProspectionDemande.parcelle?.numero}
                    </p>
                    {selectedProspectionDemande.parcelle?.surface && (
                      <p className="text-sm text-muted-foreground">{selectedProspectionDemande.parcelle.surface} m²</p>
                    )}
                  </CardContent>
                </Card>
              </div>

              {/* Workflow SPF */}
              {(() => {
                const d = selectedProspectionDemande;
                const parcelle = d.parcelle || {};
                const dept = (parcelle.code_postal || '').slice(0, 2);
                const spfStatus = d.spf_status || 'pending';
                const steps = [
                  { key: 'pending',   label: 'Payé' },
                  { key: 'requested', label: 'SPF Demandé' },
                  { key: 'received',  label: 'SPF Reçu' },
                  { key: 'completed', label: 'Livré Client' },
                ];
                const stepIndex = steps.findIndex(s => s.key === (spfStatus === 'in_progress' ? 'requested' : spfStatus === 'completed' ? 'completed' : spfStatus));
                const settings_company = settings?.company_name || "L'Œil du Marchand";
                const settings_address = settings?.address || '';
                const settings_city_postal = settings?.city_postal || '';
                const settings_email = settings?.email || '';

                const spfEmailText = `Objet : Demande de relevé de propriété — Parcelle ${parcelle.section || ''}-${parcelle.numero || ''}, ${parcelle.commune || ''} (${dept})

Madame, Monsieur,

Je souhaite obtenir un relevé de propriété concernant la parcelle suivante :

Commune : ${parcelle.commune || ''} (${parcelle.code_postal || ''})
Section cadastrale : ${parcelle.section || ''}
Numéro de parcelle : ${parcelle.numero || ''}${parcelle.surface ? `\nSurface : ${parcelle.surface} m²` : ''}

Je suis disposé(e) à m'acquitter des frais correspondants.

Mes coordonnées :
${settings_company}
${settings_address}${settings_city_postal ? '\n' + settings_city_postal : ''}
${settings_email}

Cordialement,
${settings_company}`;

                // Mapping département → email SPF DGFiP (fallback statique)
                const SPF_EMAILS_DEFAULT = {
                  '01': 'spf.bourg-en-bresse@dgfip.finances.gouv.fr',
                  '06': 'spf.nice@dgfip.finances.gouv.fr',
                  '13': 'spf.aix-en-provence1@dgfip.finances.gouv.fr',
                  '17': 'spf.la-rochelle@dgfip.finances.gouv.fr',
                  '31': 'spf.toulouse@dgfip.finances.gouv.fr',
                  '33': 'spf.bordeaux@dgfip.finances.gouv.fr',
                  '34': 'spf.montpellier@dgfip.finances.gouv.fr',
                  '38': 'spf.grenoble@dgfip.finances.gouv.fr',
                  '44': 'spf.nantes@dgfip.finances.gouv.fr',
                  '59': 'spf.lille@dgfip.finances.gouv.fr',
                  '67': 'spf.strasbourg@dgfip.finances.gouv.fr',
                  '69': 'spf.lyon@dgfip.finances.gouv.fr',
                  '75': 'spf.paris1@dgfip.finances.gouv.fr',
                  '83': 'spf.toulon@dgfip.finances.gouv.fr',
                  '84': 'spf.avignon@dgfip.finances.gouv.fr',
                };
                // Settings DB emails take priority over static fallback
                const SPF_EMAILS = {...SPF_EMAILS_DEFAULT, ...(settings?.spf_emails || {})};
                const spfEmail = SPF_EMAILS[dept] || null;

                return (
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm flex items-center justify-between">
                        <span className="flex items-center gap-2">
                          <FileText className="w-4 h-4 text-blue-600" /> Workflow SPF
                        </span>
                        <a
                          href={`https://www.google.com/search?q=Service+Publicit%C3%A9+Fonci%C3%A8re+SPF+d%C3%A9partement+${dept}+email+contact+dgfip`}
                          target="_blank" rel="noreferrer"
                          className="text-xs text-blue-600 underline"
                        >
                          Trouver le bureau SPF (dept. {dept}) →
                        </a>
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      {/* Stepper */}
                      <div className="flex items-center gap-1">
                        {steps.map((step, i) => (
                          <React.Fragment key={step.key}>
                            <div className={`flex-1 text-center text-xs py-1 px-2 rounded font-medium ${
                              i <= stepIndex ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-400'
                            }`}>
                              {step.label}
                            </div>
                            {i < steps.length - 1 && <div className={`w-3 h-0.5 ${i < stepIndex ? 'bg-blue-600' : 'bg-gray-200'}`} />}
                          </React.Fragment>
                        ))}
                      </div>

                      {/* Email SPF pré-rédigé */}
                      {spfStatus !== 'completed' && (
                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 space-y-2">
                          {spfEmail && (
                            <div className="flex items-center justify-between bg-white border border-blue-300 rounded px-3 py-2 mb-1">
                              <div>
                                <p className="text-[10px] text-blue-500 font-medium uppercase tracking-wide">Destinataire SPF (dept. {dept})</p>
                                <p className="text-xs font-mono font-semibold text-blue-800">{spfEmail}</p>
                              </div>
                              <a
                                href={`mailto:${spfEmail}?subject=${encodeURIComponent(`Demande de relevé de propriété — Parcelle ${parcelle.section}-${parcelle.numero}, ${parcelle.commune}`)}&body=${encodeURIComponent(spfEmailText)}`}
                                className="ml-3 text-xs bg-blue-600 text-white px-3 py-1.5 rounded hover:bg-blue-700 whitespace-nowrap flex items-center gap-1"
                              >
                                <Mail className="w-3 h-3" /> Ouvrir dans ma messagerie
                              </a>
                            </div>
                          )}
                          <p className="text-xs font-semibold text-blue-800">Email pré-rédigé à envoyer au SPF :</p>
                          <pre className="text-xs text-blue-700 whitespace-pre-wrap font-mono bg-white p-2 rounded border border-blue-100 max-h-36 overflow-y-auto">{spfEmailText}</pre>
                          <Button
                            size="sm"
                            variant="outline"
                            className="border-blue-400 text-blue-700 hover:bg-blue-100"
                            onClick={() => {
                              navigator.clipboard.writeText(spfEmailText);
                              setCopiedSpf(true);
                              setTimeout(() => setCopiedSpf(false), 2000);
                            }}
                          >
                            {copiedSpf ? <><CheckCircle className="w-3 h-3 mr-1 text-green-600" /> Copié !</> : <><Copy className="w-3 h-3 mr-1" /> Copier l'email</>}
                          </Button>
                        </div>
                      )}

                      {/* Coût SPF + boutons d'action */}
                      {spfStatus !== 'completed' && (
                        <div className="flex items-end gap-3">
                          <div className="flex-1">
                            <Label className="text-xs">Coût SPF payé (€ TTC = HT — pas de TVA)</Label>
                            <Input
                              type="number"
                              step="0.01"
                              value={spfCostInput}
                              onChange={e => setSpfCostInput(e.target.value)}
                              className="h-8 text-sm"
                            />
                            <p className="text-xs text-muted-foreground mt-1">Service d'État exonéré de TVA (art. 256 B CGI)</p>
                          </div>
                          {spfStatus === 'pending' && (
                            <Button
                              size="sm"
                              className="bg-blue-600 hover:bg-blue-700"
                              disabled={savingSpf}
                              onClick={async () => {
                                setSavingSpf(true);
                                try {
                                  await axios.post(`${API}/admin/prospection/demandes/${d.id}/spf`, {
                                    spf_status: 'requested',
                                    spf_cost_ht: parseFloat(spfCostInput) || 15
                                  });
                                  toast.success('SPF marqué comme demandé');
                                  fetchData();
                                  setSelectedProspectionDemande(prev => ({...prev, spf_status: 'requested', status: 'in_progress'}));
                                } catch { toast.error('Erreur'); } finally { setSavingSpf(false); }
                              }}
                            >
                              SPF Demandé
                            </Button>
                          )}
                          {(spfStatus === 'requested' || spfStatus === 'in_progress') && (
                            <Button
                              size="sm"
                              className="bg-green-600 hover:bg-green-700"
                              disabled={savingSpf}
                              onClick={async () => {
                                setSavingSpf(true);
                                try {
                                  await axios.post(`${API}/admin/prospection/demandes/${d.id}/spf`, {
                                    spf_status: 'received',
                                    spf_cost_ht: parseFloat(spfCostInput) || 15
                                  });
                                  toast.success('SPF reçu — saisir le propriétaire ci-dessous');
                                  fetchData();
                                  setSelectedProspectionDemande(prev => ({...prev, spf_status: 'received'}));
                                } catch { toast.error('Erreur'); } finally { setSavingSpf(false); }
                              }}
                            >
                              SPF Reçu
                            </Button>
                          )}
                        </div>
                      )}
                      {spfStatus === 'completed' && (
                        <p className="text-sm text-green-700 font-medium flex items-center gap-2">
                          <CheckCircle className="w-4 h-4" /> Dossier livré au client — Coût SPF : {d.spf_cost_ht || 15}€ (comptabilisé)
                        </p>
                      )}
                    </CardContent>
                  </Card>
                );
              })()}

              {/* Messages / Ticket */}
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm flex items-center gap-2">
                    <MessageCircle className="w-4 h-4" /> Ticket de discussion
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3 max-h-48 overflow-y-auto mb-4">
                    {selectedProspectionDemande.messages?.length > 0 ? (
                      selectedProspectionDemande.messages.map((msg) => (
                        <div
                          key={msg.id}
                          className={`p-3 rounded-lg ${
                            msg.sender === 'admin'
                              ? 'bg-purple-50 border border-purple-200'
                              : 'bg-gray-50 border border-gray-200'
                          }`}
                        >
                          <div className="flex items-center gap-2 mb-1">
                            <span className="text-sm font-medium">
                              {msg.sender === 'admin' ? '👑 Vous' : '👤 Client'}
                            </span>
                            <span className="text-xs text-muted-foreground">
                              {formatDate(msg.timestamp)}
                            </span>
                          </div>
                          <p className="text-sm">{msg.content}</p>
                        </div>
                      ))
                    ) : (
                      <p className="text-center text-muted-foreground py-4">Aucun message</p>
                    )}
                  </div>
                  <div className="flex gap-2">
                    <Input
                      value={newProspectionMessage}
                      onChange={(e) => setNewProspectionMessage(e.target.value)}
                      placeholder="Écrire un message au client..."
                      onKeyPress={(e) => {
                        if (e.key === 'Enter' && newProspectionMessage.trim()) {
                          setSendingProspectionMessage(true);
                          axios.post(`${API}/admin/prospection/demandes/${selectedProspectionDemande.id}/message`, {
                            content: newProspectionMessage
                          }).then(() => {
                            toast.success('Message envoyé');
                            setNewProspectionMessage('');
                            fetchData();
                          }).catch(() => {
                            toast.error('Erreur');
                          }).finally(() => {
                            setSendingProspectionMessage(false);
                          });
                        }
                      }}
                    />
                    <Button
                      onClick={() => {
                        if (!newProspectionMessage.trim()) return;
                        setSendingProspectionMessage(true);
                        axios.post(`${API}/admin/prospection/demandes/${selectedProspectionDemande.id}/message`, {
                          content: newProspectionMessage
                        }).then(() => {
                          toast.success('Message envoyé');
                          setNewProspectionMessage('');
                          fetchData();
                        }).catch(() => {
                          toast.error('Erreur');
                        }).finally(() => {
                          setSendingProspectionMessage(false);
                        });
                      }}
                      disabled={sendingProspectionMessage || !newProspectionMessage.trim()}
                    >
                      <Send className="w-4 h-4" />
                    </Button>
                  </div>
                </CardContent>
              </Card>

              {/* Proprietaire Form */}
              <Card className={selectedProspectionDemande.status === 'completed' ? 'bg-green-50 border-green-200' : ''}>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm flex items-center gap-2">
                    <User className="w-4 h-4" /> 
                    {selectedProspectionDemande.status === 'completed' ? 'Propriétaire identifié' : 'Saisir le propriétaire'}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {selectedProspectionDemande.status === 'completed' && selectedProspectionDemande.proprietaire ? (
                    <div className="bg-green-100 p-4 rounded-lg">
                      <p className="font-semibold text-lg">
                        {selectedProspectionDemande.proprietaire.nom} {selectedProspectionDemande.proprietaire.prenom}
                      </p>
                      {selectedProspectionDemande.proprietaire.adresse && (
                        <p>{selectedProspectionDemande.proprietaire.adresse}</p>
                      )}
                      {(selectedProspectionDemande.proprietaire.code_postal || selectedProspectionDemande.proprietaire.ville) && (
                        <p>{selectedProspectionDemande.proprietaire.code_postal} {selectedProspectionDemande.proprietaire.ville}</p>
                      )}
                      <p className="text-sm text-green-700 mt-2">
                        ✅ Envoyé au client le {formatDate(selectedProspectionDemande.completed_at)}
                      </p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      <div className="grid md:grid-cols-2 gap-4">
                        <div>
                          <Label>Nom *</Label>
                          <Input
                            value={proprietaireForm.nom}
                            onChange={(e) => setProprietaireForm({...proprietaireForm, nom: e.target.value})}
                            placeholder="DUPONT"
                          />
                        </div>
                        <div>
                          <Label>Prénom</Label>
                          <Input
                            value={proprietaireForm.prenom}
                            onChange={(e) => setProprietaireForm({...proprietaireForm, prenom: e.target.value})}
                            placeholder="Marcel"
                          />
                        </div>
                      </div>
                      <div>
                        <Label>Adresse</Label>
                        <Input
                          value={proprietaireForm.adresse}
                          onChange={(e) => setProprietaireForm({...proprietaireForm, adresse: e.target.value})}
                          placeholder="15 rue des Oliviers"
                        />
                      </div>
                      <div className="grid md:grid-cols-2 gap-4">
                        <div>
                          <Label>Code postal</Label>
                          <Input
                            value={proprietaireForm.code_postal}
                            onChange={(e) => setProprietaireForm({...proprietaireForm, code_postal: e.target.value})}
                            placeholder="13100"
                          />
                        </div>
                        <div>
                          <Label>Ville</Label>
                          <Input
                            value={proprietaireForm.ville}
                            onChange={(e) => setProprietaireForm({...proprietaireForm, ville: e.target.value})}
                            placeholder="AIX-EN-PROVENCE"
                          />
                        </div>
                      </div>
                      <Button
                        onClick={async () => {
                          if (!proprietaireForm.nom.trim()) {
                            toast.error('Le nom est obligatoire');
                            return;
                          }
                          setSavingProprietaire(true);
                          try {
                            await axios.post(`${API}/admin/prospection/demandes/${selectedProspectionDemande.id}/proprietaire`, proprietaireForm);
                            toast.success('Propriétaire enregistré et envoyé au client !');
                            setShowProspectionModal(false);
                            fetchData();
                          } catch (err) {
                            toast.error(err.response?.data?.detail || 'Erreur');
                          } finally {
                            setSavingProprietaire(false);
                          }
                        }}
                        disabled={savingProprietaire || !proprietaireForm.nom.trim()}
                        className="w-full bg-green-600 hover:bg-green-700"
                      >
                        {savingProprietaire ? (
                          <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Envoi en cours...</>
                        ) : (
                          <><CheckCircle className="w-4 h-4 mr-2" /> Valider et envoyer au client</>
                        )}
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* MANUAL PAYMENT MODAL */}
      <Dialog open={!!manualPaymentPartner} onOpenChange={(open) => { if (!open) setManualPaymentPartner(null); }}>
        <DialogContent className="max-w-md" data-testid="manual-payment-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Euro className="w-5 h-5 text-emerald-600" />
              Enregistrer un paiement reçu
            </DialogTitle>
            <DialogDescription>
              Pour <strong>{manualPaymentPartner?.full_name || manualPaymentPartner?.company_name}</strong> ({manualPaymentPartner?.email})
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label>Montant TTC reçu (€) <span className="text-red-500">*</span></Label>
              <Input
                type="number"
                step="0.01"
                min="0"
                placeholder="2000"
                value={manualPaymentForm.amount}
                onChange={(e) => setManualPaymentForm({ ...manualPaymentForm, amount: e.target.value })}
                data-testid="manual-payment-amount"
              />
              <p className="text-xs text-muted-foreground mt-1">
                {manualPaymentForm.amount && parseFloat(manualPaymentForm.amount) > 0 && (
                  <>
                    HT : {(parseFloat(manualPaymentForm.amount) / 1.20).toFixed(2)}€ · TVA 20% : {(parseFloat(manualPaymentForm.amount) - parseFloat(manualPaymentForm.amount) / 1.20).toFixed(2)}€
                  </>
                )}
              </p>
            </div>
            <div>
              <Label>Moyen de paiement</Label>
              <select
                className="w-full border rounded-md px-3 py-2 mt-1"
                value={manualPaymentForm.payment_method}
                onChange={(e) => setManualPaymentForm({ ...manualPaymentForm, payment_method: e.target.value })}
                data-testid="manual-payment-method"
              >
                <option value="virement">Virement bancaire</option>
                <option value="cheque">Chèque</option>
                <option value="cash">Espèces</option>
                <option value="stripe_recovered">Stripe (paiement réussi non synchronisé)</option>
              </select>
            </div>
            <div>
              <Label>Date du paiement</Label>
              <Input
                type="date"
                value={manualPaymentForm.payment_date}
                onChange={(e) => setManualPaymentForm({ ...manualPaymentForm, payment_date: e.target.value })}
                data-testid="manual-payment-date"
              />
            </div>
            <div>
              <Label>Notes internes (facultatif)</Label>
              <Textarea
                placeholder="N° chèque, référence virement, contexte..."
                rows={2}
                value={manualPaymentForm.notes}
                onChange={(e) => setManualPaymentForm({ ...manualPaymentForm, notes: e.target.value })}
                data-testid="manual-payment-notes"
              />
            </div>
            <div className="bg-emerald-50 border border-emerald-200 rounded-md p-3 text-xs text-emerald-700">
              <p className="font-medium mb-1">Cela va automatiquement :</p>
              <ul className="list-disc ml-4 space-y-0.5">
                <li>Créer une facture (TVA 20% inclus) dans la Comptabilité</li>
                <li>Créer un paiement dans les transactions</li>
                <li>Activer / prolonger l'abonnement pour 1 an</li>
              </ul>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setManualPaymentPartner(null)} data-testid="manual-payment-cancel">
              Annuler
            </Button>
            <Button onClick={submitManualPayment} className="bg-emerald-600 hover:bg-emerald-700" data-testid="manual-payment-submit">
              Enregistrer le paiement
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Mobile sidebar overlay - rendered at root level for proper z-index */}
      {mobileSidebarOpen && (
        <div style={{position:'fixed',inset:0,zIndex:9999}} className="lg:hidden">
          <div style={{position:'absolute',inset:0,backgroundColor:'rgba(0,0,0,0.5)'}} onClick={() => setMobileSidebarOpen(false)} />
          <div style={{position:'absolute',top:0,left:0,bottom:0,width:'280px',zIndex:10000,backgroundColor:'white',boxShadow:'0 25px 50px -12px rgba(0,0,0,0.25)',overflowY:'auto'}} onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-4 py-3 bg-navy text-white">
              <span className="text-sm font-bold">Navigation</span>
              <button onClick={() => setMobileSidebarOpen(false)} className="p-1.5 rounded-lg hover:bg-white/20"><X className="w-5 h-5" /></button>
            </div>
            <AdminSidebar
              activeTab={activeTab}
              onTabChange={handleTabChange}
              mobileOpen={true}
              onMobileClose={() => setMobileSidebarOpen(false)}
              badges={{
                network: pendingNetworkCount,
                hotline: totalUnreadHotline,
                conseil: conseilRequests.filter(c => c.status === 'paid' || c.status === 'pending').length,
              }}
            />
          </div>
        </div>
      )}

    </div>
  );
}
